// Extracted from CaseBuddy.vue in refactor pass 2.
// Template/table/report deliverable methods, What Went Where, conclusion, report body, and one-click package preview.
// These are Options API method definitions and intentionally use `this`.

import {
  estimateCaseBuddyHistoricalUsdValue
} from '../CaseBuddyHistoricalUsdReference.js';
import {
  buildCaseAddressLegendAnalysis,
  buildCaseAddressLegendDeliverableRows,
  buildCaseAddressLegendQuickAccessRows,
  buildCaseAddendumAuditRows,
  buildCaseAddendumPreviewRows,
  buildCaseCompletionSummaryText,
  buildCaseConclusionPreviewRows,
  buildCaseConclusionState,
  buildCaseDormantAddressAuditRows,
  buildCaseDormantAddressDeliverablesState,
  buildCaseDormantAddressMonitoringRows,
  buildCaseDormantAddressTableDeliverableRows,
  buildCaseDuplicateHashLabelerAnalysis,
  buildCaseExchangeAddressTableAuditRows,
  buildCaseExchangeAddressTableDeliverableRows,
  buildCaseExchangeAddressTableState,
  buildCaseCombinedFlowVisual,
  buildCaseFlowAggregateVisual,
  buildCaseFlowVisual,
  buildCaseFlowVisualBundleFiles,
  buildCaseFlowVisualsPackageFilename,
  buildCaseFlowVisualsPackageRows,
  buildCaseFlowVisualsState,
  buildCaseFullReportPackageAuditRows,
  buildCaseFullReportPackageClipboardHtml,
  buildCaseFullReportPackagePayload,
  buildCaseFullReportPackagePreviewRows,
  buildCaseFullReportPackageState,
  buildCaseNoteHashRewriteBackupOutputRows,
  buildCaseNoteHashRewriteBackupRecord,
  buildCaseReportBodyAuditRows,
  buildCaseReportBodyPreviewRows,
  buildCaseReportBodyState,
  buildCaseSuspiciousAddressTableAuditRows,
  buildCaseSuspiciousAddressTableDeliverableRows,
  buildCaseSuspiciousAddressTableState,
  buildCaseTemplateDeliverableRows,
  buildCaseTemplateState,
  buildCaseWhatWentWhereAuditRows,
  buildCaseWhatWentWhereDeliverableRows,
  buildCaseWhatWentWhereState,
  buildCaseWhatWentWhereValuationAuditRows,
  buildCaseWwwUsdReferenceAuditRows,
  buildCaseWwwUsdReferenceReviewState,
  buildDuplicateHashTableRows,
  buildPathOperationAnalysis,
  buildSaplingPreparationAnalysis,
  buildTsv,
  caseBuddyHashString,
  caseFlowVisualEdgeClass,
  caseFlowVisualEdgePath,
  caseFlowVisualNodeClass,
  createCaseFlowVisualZipBlob,
  downloadCaseFlowVisualBlob,
  serializeCaseFlowVisualSvg,
  CASE_ADDENDUM_VARIANT_NARRATIVE,
  CASE_ADDENDUM_VARIANT_STRUCTURED,
  CASE_DELIVERABLE_GROUPS,
  CASE_FLOW_VISUALS_DEFAULT_OPTIONS,
  CASE_INVESTIGATION_VISUALS_DEFAULT_OPTIONS,
  CASE_FLOW_VISUALS_RENDERER_VERSION,
  CASE_FULL_REPORT_PACKAGE_RENDERER_VERSION,
  CASE_REPORT_BODY_RENDERER_VERSION,
  CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING,
  CASE_REPORT_BODY_VARIANT_STANDARD,
  CASE_SUSPICIOUS_ADDRESS_TABLE_HEADERS,
  downloadTextFile,
  findExactPathOperationCandidateForRow,
  findPathOperationCandidateForRow,
  formatCaseWwwUsdCell,
  loadEntityCuratorOverlayFromStorage,
  normalizeCaseMatrix,
  normalizeCell,
  normalizeComparisonKey,
  parseCaseWwwUsdReference,
  rowHasMeaningfulData,
  safeDateLabel,
  sleep,
  WORKFLOW_SIGNAL_CASE_CONCLUSION_REVIEWED,
  WORKFLOW_SIGNAL_CASE_FLOW_VISUALS_CREATED,
  WORKFLOW_SIGNAL_DORMANT_ADDRESS_DELIVERABLES_REVIEWED,
  WORKFLOW_SIGNAL_EXCHANGE_ADDRESS_TABLE_REVIEWED,
  WORKFLOW_SIGNAL_FULL_REPORT_PACKAGE_REVIEWED,
  WORKFLOW_SIGNAL_REPORT_BODY_REVIEWED,
  WORKFLOW_SIGNAL_SUSPICIOUS_ADDRESS_TABLE_REVIEWED,
  WORKFLOW_SIGNAL_TEMPLATE_DELIVERABLE_REVIEWED,
  WORKFLOW_SIGNAL_WHAT_WENT_WHERE_REVIEWED,
  WORKFLOW_SIGNAL_WWW_USD_REFERENCES_REVIEWED
} from '../CaseBuddyCore.js';


export const caseBuddyDeliverableMethods = {
    reviewRecordIsCurrentForCaseRevision(review = {}, utcFields = ['reviewedAtUtc', 'confirmedAtUtc']) {
      if (!review || typeof review !== 'object') return false;
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      const reviewRevision = Number.isFinite(Number(review.revision))
        ? Number(review.revision)
        : Number.isFinite(Number(review.reviewedRevision))
          ? Number(review.reviewedRevision)
          : Number.isFinite(Number(review.builtRevision))
            ? Number(review.builtRevision)
            : null;
      return reviewRevision === currentRevision && utcFields.some(field => !!review[field]);
    },

    caseCheckResultIsCurrentPass(id) {
      const result = this.caseSupportState.checkResultsById?.[id];
      if (!result || result.status !== 'pass') return false;
      return Number(result.revision) === (Number(this.caseSupportState.caseDataRevision) || 0);
    },

    getAddressLegendRequirementAnalysis() {
      const review = this.caseSupportState.workflowState?.addressLegendReview || {};
      const entries = Array.isArray(review.entries) ? review.entries : [];
      if (entries.length === 0 || !review.reviewedAtUtc) {
        return {
          buildIsCurrent: false,
          reviewIsCurrent: false,
          summary: 'Address legend has not been reviewed yet.'
        };
      }

      const analysisOptions = {
        labelDormantAddressesSeparately: !!review.labelDormantAddressesSeparately,
        useDepositAddressLabels: !!review.useDepositAddressLabels
      };
      const cacheKey = this.getCaseComputationCacheKey?.('address-legend-deliverable-requirement', {
        builtBasisHash: review.builtBasisHash || '',
        reviewedBasisHash: review.reviewedBasisHash || review.confirmedReviewBasisHash || '',
        reviewedAtUtc: review.reviewedAtUtc || '',
        entryCount: entries.length,
        staleLegendEntryCount: review.staleLegendEntryCount || 0,
        ...analysisOptions
      }) || '';

      return this.getCachedCaseComputation?.('state', cacheKey, () => buildCaseAddressLegendAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        analysisOptions
      )) || buildCaseAddressLegendAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        analysisOptions
      );
    },

    getAddressLegendRequirementStatus() {
      const review = this.caseSupportState.workflowState?.addressLegendReview || {};
      const revisionCurrent = this.reviewRecordIsCurrentForCaseRevision(review, ['reviewedAtUtc']);
      if (revisionCurrent) {
        return { current: true, source: 'review-record' };
      }

      try {
        const analysis = this.getAddressLegendRequirementAnalysis();
        if (analysis?.reviewIsCurrent) {
          return { current: true, source: 'legend-analysis', basisHash: analysis.currentBasisHash || '' };
        }
      } catch {
        // Fall through to the stored check snapshot below.
      }

      if (this.caseCheckResultIsCurrentPass('address_legend')) {
        return { current: true, source: 'check-result' };
      }
      return { current: false, source: 'missing' };
    },

    getDeliverableRequirementSnapshot(requirements = []) {
      return (Array.isArray(requirements) ? requirements : []).map(requirement => ({
        requirement,
        met: this.isDeliverableRequirementMet(requirement)
      }));
    },

    deliverableGeneratorCanDownload(generator = '') {
      return [
        'main_tsv',
        'raw_tsv',
        'duplicate_hash_table',
        'notes_backup',
        'address_legend_preview',
        'case_template',
        'suspicious_address_table',
        'exchange_address_table',
        'dormant_address_table',
        'dormant_address_monitoring_view',
        'what_went_where',
        'what_went_where_valuation_audit',
        'case_conclusion',
        'case_report_body',
        'case_report_body_spacing_tags',
        'case_addendum',
        'case_addendum_audit',
        'case_addendum_narrative',
        'case_flow_visuals',
        'case_flow_visuals_placeholder',
        'case_completion_summary',
        'full_report_package'
      ].includes(generator);
    },

    isDeliverableRequirementMet(requirement) {
      if (requirement === 'mainEntries') return this.caseSummary.mainEntries > 0;
      if (requirement === 'rawEntries') return this.caseSummary.rawEntries > 0;
      if (requirement === 'addresses') return (this.caseSummary.caseAddressCount || this.caseSummary.destinationAddressCount) > 0;
      if (requirement === 'pathLabels') return this.caseSummary.mainEntries > 0 && this.caseSummary.missingPathLabels === 0;
      if (requirement === 'attribution') return this.caseSummary.caseAddressCount > 0 && this.caseSummary.missingAttribution === 0;
      if (requirement === 'addressLegendReviewed') {
        return !!this.getAddressLegendRequirementStatus().current;
      }
      if (requirement === 'addendumCanBuild') {
        return this.reviewRecordIsCurrentForCaseRevision(this.caseSupportState.workflowState?.addendumPathEndingReview, ['reviewedAtUtc']) &&
          this.reviewRecordIsCurrentForCaseRevision(this.caseSupportState.workflowState?.addendumRecipientEntityReview, ['reviewedAtUtc']);
      }
      if (requirement === 'fullReportPackageReady') {
        return this.reviewRecordIsCurrentForCaseRevision(this.caseSupportState.workflowState?.reportBodyReview, ['reviewedAtUtc']) &&
          this.reviewRecordIsCurrentForCaseRevision(this.caseSupportState.workflowState?.addendumBuildReview, ['reviewedAtUtc', 'confirmedAtUtc']);
      }
      return false;
    },

    getCaseTemplateState(options = {}) {
      const buildOptions = {
        caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
        labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately,
        ...(options || {})
      };
      const cacheKey = this.getCaseComputationCacheKey?.('template-state', buildOptions) || '';
      return this.getCachedCaseComputation?.('state', cacheKey, () => buildCaseTemplateState(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        buildOptions
      )) || buildCaseTemplateState(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        buildOptions
      );
    },

    getTemplateDeliverableDefinition() {
      return CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'template') || null;
    },

    async openTemplateDeliverableModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening template deliverable…',
        message: 'Building the current template preview from Main Data and the reviewed legend.',
        target: 'template-deliverable-modal',
        finishMessage: 'Template preview ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.templateDeliverableSearch = '';
        this.templateDeliverableViewFilter = options.view || 'all';
        this.templateDeliverableStatus = 'Refreshing template deliverable preview.';
        this.entityCuratorLocalOverlay = loadEntityCuratorOverlayFromStorage();
        this.clearCasePerformanceCache?.();
        if (this.entityCuratorRemote.status === 'idle' || options.forceRemote) {
          await this.loadEntityCuratorRemoteKeywords({ force: !!options.forceRemote, silent: true });
        }
        this.showTemplateDeliverableModal = true;
        await this.yieldToCaseUi();
        this.refreshTemplateDeliverableReview({ silent: true });
        return true;
      });
    },

    cancelTemplateDeliverableModal() {
      this.showTemplateDeliverableModal = false;
    },

    refreshTemplateDeliverableReview(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      this.entityCuratorLocalOverlay = loadEntityCuratorOverlayFromStorage();
      this.clearCasePerformanceCache?.();
      const state = this.getCaseTemplateState();
      const check = this.buildTemplateDeliverableCheckResult();
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        template_deliverable: check
      };
      this.persistCaseSupportState();
      this.templateDeliverableStatus = state.summary || 'Template preview refreshed.';
      if (!options.silent) this.showCaseToast(this.templateDeliverableStatus);
      return state;
    },

    getTemplateDeliverableRows() {
      return buildCaseTemplateDeliverableRows(this.getCaseTemplateState());
    },

    updateTemplateDeliverableCheckResult() {
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        template_deliverable: this.buildTemplateDeliverableCheckResult()
      };
      this.persistCaseSupportState();
    },

    getTemplateDeliverableRecordStatus(state = null) {
      const templateState = state || this.getCaseTemplateState();
      const existing = this.caseSupportState.deliverableStateById?.template || {};
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      const builtRevision = Number.isFinite(Number(existing.builtRevision)) ? Number(existing.builtRevision) : null;
      const basisHash = normalizeCell(templateState.currentBasisHash);
      const recordBasisHash = normalizeCell(existing.basisHash);
      const builtAtUtc = existing.builtAtUtc || '';
      const basisMatches = basisHash ? recordBasisHash === basisHash : !recordBasisHash;
      const hasBuildRecord = builtRevision !== null || !!builtAtUtc;
      const storedBuiltIsCurrent = !!builtAtUtc && builtRevision === currentRevision && basisMatches;
      const session = this.templateDeliverableBuildSession || {};
      const sessionBasisHash = normalizeCell(session.basisHash);
      const sessionBuildIsCurrentRevision = !!session.builtAtUtc && Number(session.revision) === currentRevision;
      const sessionBasisMatches = !basisHash || !sessionBasisHash || sessionBasisHash === basisHash || session.trustedCurrentRevision === true;
      const sessionBuildIsCurrent = sessionBuildIsCurrentRevision && sessionBasisMatches;
      const builtIsCurrent = storedBuiltIsCurrent || sessionBuildIsCurrent;
      const reviewIsCurrent = storedBuiltIsCurrent && !!existing.reviewedAtUtc;
      return {
        state: templateState,
        existing,
        currentRevision,
        builtRevision,
        basisHash,
        recordBasisHash,
        builtAtUtc,
        hasBuildRecord,
        basisMatches,
        storedBuiltIsCurrent,
        sessionBuildIsCurrentRevision,
        sessionBuildIsCurrent,
        sessionBuiltAtUtc: session.builtAtUtc || '',
        builtIsCurrent,
        reviewIsCurrent,
        reviewedAtUtc: reviewIsCurrent ? existing.reviewedAtUtc || '' : ''
      };
    },

    updateTemplateDeliverableBuildSession(state = null, builtAtUtc = '') {
      const templateState = state || this.getCaseTemplateState();
      this.templateDeliverableBuildSession = {
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        basisHash: normalizeCell(templateState.currentBasisHash),
        builtAtUtc: builtAtUtc || new Date().toISOString(),
        trustedCurrentRevision: true
      };
      return this.templateDeliverableBuildSession;
    },

    templateDeliverableBuildSessionIsCurrent(state = null) {
      const session = this.templateDeliverableBuildSession || {};
      if (!session.builtAtUtc) return false;
      const templateState = state || this.getCaseTemplateState();
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      const basisHash = normalizeCell(templateState.currentBasisHash);
      const sessionBasisHash = normalizeCell(session.basisHash);
      return Number(session.revision) === currentRevision
        && (!basisHash || !sessionBasisHash || sessionBasisHash === basisHash || session.trustedCurrentRevision === true);
    },

    markTemplateDeliverableBuiltRecord(options = {}) {
      const state = options.state || this.getCaseTemplateState();
      const existing = this.caseSupportState.deliverableStateById?.template || {};
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      const basisHash = normalizeCell(state.currentBasisHash);
      const builtAtUtc = new Date().toISOString();
      const keepReview = !!options.keepReview &&
        Number(existing.builtRevision) === currentRevision &&
        normalizeCell(existing.basisHash) === basisHash;
      const nextRecord = {
        ...existing,
        builtRevision: currentRevision,
        builtAtUtc,
        basisHash,
        reviewedAtUtc: keepReview ? (existing.reviewedAtUtc || '') : ''
      };

      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        template: nextRecord
      };
      this.updateTemplateDeliverableBuildSession(state, builtAtUtc);
      this.persistCaseSupportState();
      this.updateTemplateDeliverableCheckResult();
      return nextRecord;
    },

    stageTemplateDeliverablePreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging template preview…',
          message: 'Building preview rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Template preview staged.'
        }, () => this.stageTemplateDeliverablePreview({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getCaseTemplateState();
      if (!state.buildIsCurrent) {
        this.templateDeliverableStatus = state.summary || 'Template prerequisites are not current.';
        if (!options.silent) this.showCaseToast(this.templateDeliverableStatus);
        return false;
      }
      const rows = buildCaseTemplateDeliverableRows(state);
      this.loadOutputRows(rows, {
        title: 'Template deliverable preview',
        detail: 'Tool 31B-style template staged in Output for review. Main Data is not modified.',
        sourceActionId: 'stage_template_deliverable',
        sourceDeliverableId: 'template',
        target: 'deliverable-preview'
      });
      this.markTemplateDeliverableBuiltRecord({ state });
      this.templateDeliverableStatus = `Template staged in Output with ${rows.length} row${rows.length === 1 ? '' : 's'}. Review it, then mark the template reviewed when it looks correct.`;
      if (!options.silent) {
        this.scrollToSection('case-output');
        this.showCaseToast(this.templateDeliverableStatus);
      }
      return true;
    },

    downloadTemplateDeliverableTsv(options = {}) {
      const state = this.getCaseTemplateState();
      if (!state.buildIsCurrent) {
        this.templateDeliverableStatus = state.summary || 'Template prerequisites are not current.';
        if (!options.silent) this.showCaseToast(this.templateDeliverableStatus);
        return false;
      }
      const rows = buildCaseTemplateDeliverableRows(state);
      downloadTextFile('template.tsv', buildTsv(rows), 'text/tab-separated-values;charset=utf-8;');
      this.markTemplateDeliverableBuiltRecord({ state });
      this.templateDeliverableStatus = `Template TSV downloaded with ${rows.length} row${rows.length === 1 ? '' : 's'}.`;
      if (!options.silent) this.showCaseToast(this.templateDeliverableStatus);
      return true;
    },

    markTemplateDeliverableReviewComplete(options = {}) {
      const state = this.getCaseTemplateState();
      let recordStatus = this.getTemplateDeliverableRecordStatus(state);
      if (!state.buildIsCurrent) {
        this.templateDeliverableStatus = state.summary || 'Template prerequisites are not current.';
        return false;
      }
      if (!recordStatus.storedBuiltIsCurrent && recordStatus.sessionBuildIsCurrent) {
        this.markTemplateDeliverableBuiltRecord({ state, keepReview: false });
        recordStatus = this.getTemplateDeliverableRecordStatus(state);
      }
      const { existing, currentRevision, basisHash, builtIsCurrent } = recordStatus;
      if (!builtIsCurrent) {
        this.templateDeliverableStatus = 'Stage or download the current template before marking it reviewed.';
        return false;
      }
      const reviewedAtUtc = new Date().toISOString();
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        template: {
          ...existing,
          builtRevision: currentRevision,
          builtAtUtc: existing.builtAtUtc || reviewedAtUtc,
          basisHash,
          reviewedAtUtc
        }
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_TEMPLATE_DELIVERABLE_REVIEWED, options.source || 'review_template_deliverable', { silent: true });
      this.updateTemplateDeliverableCheckResult();
      this.loadOutputRows([
        ['Case Buddy Template Review Audit', '', '', '', '', '', '', '', '', 'Audit only. Template review was confirmed; Main Data was not modified.'],
        ['Action', 'Template review confirmed', 'Rows', state.rowCount || 0, 'Legend Entries', state.legendEntryCount || 0, 'Exchange Keywords', state.exchangeKeywordCount || 0, 'Basis', basisHash],
        ['Status', state.summary || '', 'Built At', existing.builtAtUtc || '', 'Reviewed At', this.caseSupportState.deliverableStateById.template.reviewedAtUtc || '']
      ], {
        title: 'Template review audit',
        detail: 'Template deliverable review was confirmed for the current case data.',
        sourceActionId: 'review_template_deliverable',
        sourceDeliverableId: 'template',
        target: 'audit-only'
      });
      this.templateDeliverableStatus = 'Template deliverable marked reviewed for the current case data.';
      this.showCaseToast(this.templateDeliverableStatus);
      return true;
    },

    toggleTemplateDeliverableReviewed() {
      const state = this.getCaseTemplateState();
      const recordStatus = this.getTemplateDeliverableRecordStatus(state);
      const { existing, builtIsCurrent, reviewIsCurrent } = recordStatus;
      if (!builtIsCurrent) {
        this.showCaseToast('Stage or download the current template before marking it reviewed.');
        return false;
      }
      if (reviewIsCurrent) {
        this.caseSupportState.deliverableStateById = {
          ...(this.caseSupportState.deliverableStateById || {}),
          template: { ...existing, reviewedAtUtc: '' }
        };
        this.updateTemplateDeliverableCheckResult();
        this.showCaseToast('Template review mark cleared.');
        return true;
      }
      return this.markTemplateDeliverableReviewComplete({ source: 'deliverables-panel' });
    },

    getSuspiciousAddressTableState(options = {}) {
      const buildOptions = {
        caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
        labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately,
        ...(options || {})
      };
      const cacheKey = this.getCaseComputationCacheKey?.('suspicious-address-state', buildOptions) || '';
      return this.getCachedCaseComputation?.('state', cacheKey, () => buildCaseSuspiciousAddressTableState(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        buildOptions
      )) || buildCaseSuspiciousAddressTableState(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        buildOptions
      );
    },

    getSuspiciousAddressTableDefinition() {
      return CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'suspicious_address_table') || null;
    },

    async openSuspiciousAddressTableModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening suspicious address table…',
        message: 'Collecting suspicious-address rows from the current reviewed legend.',
        target: 'suspicious-address-table-modal',
        finishMessage: 'Suspicious address table preview ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.suspiciousAddressTableSearch = '';
        this.suspiciousAddressTableViewFilter = options.view || 'all';
        this.showSuspiciousAddressTableModal = true;
        await this.yieldToCaseUi();
        this.refreshSuspiciousAddressTableReview({ silent: true });
        return true;
      });
    },

    cancelSuspiciousAddressTableModal() {
      this.showSuspiciousAddressTableModal = false;
    },

    refreshSuspiciousAddressTableReview(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const state = this.getSuspiciousAddressTableState();
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        suspicious_address_table: this.buildSuspiciousAddressTableCheckResult(state)
      };
      this.persistCaseSupportState();
      this.suspiciousAddressTableStatus = state.summary || 'Suspicious address table preview refreshed.';
      if (!options.silent) this.showCaseToast(this.suspiciousAddressTableStatus);
      return state;
    },

    buildSuspiciousAddressTableCheckResult(state = null) {
      const reviewState = state || this.getSuspiciousAddressTableState();
      const definition = this.getSuspiciousAddressTableDefinition();
      const deliverable = definition ? this.buildDeliverableView(definition) : null;
      if (!reviewState.legendBuildIsCurrent) {
        return this.buildCheckResult('suspicious_address_table', 'fail', 'Suspicious address table needs the Address Legend to be built from current Main Data first.', reviewState);
      }
      if (!reviewState.legendReviewIsCurrent) {
        return this.buildCheckResult('suspicious_address_table', 'fail', 'Suspicious address table needs the Address Legend to be reviewed for the current case data first.', reviewState);
      }
      if (deliverable?.status === 'current') {
        if (!deliverable.reviewed) {
          return this.buildCheckResult('suspicious_address_table', 'warn', `Suspicious address table is built current (${reviewState.rowCount} row${reviewState.rowCount === 1 ? '' : 's'}), but it has not been marked reviewed yet.`, reviewState);
        }
        return this.buildCheckResult('suspicious_address_table', 'pass', `Suspicious address table is current and reviewed (${reviewState.rowCount} row${reviewState.rowCount === 1 ? '' : 's'}).`, reviewState);
      }
      if (deliverable?.status === 'stale') {
        return this.buildCheckResult('suspicious_address_table', 'fail', 'Suspicious address table is stale because Main Data or the reviewed Address Legend changed. Rebuild it.', reviewState);
      }
      return this.buildCheckResult('suspicious_address_table', 'warn', reviewState.summary || 'Suspicious address table is ready to build and review.', reviewState);
    },

    updateSuspiciousAddressTableCheckResult() {
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        suspicious_address_table: this.buildSuspiciousAddressTableCheckResult()
      };
      this.persistCaseSupportState();
    },

    markSuspiciousAddressTableBuiltRecord(options = {}) {
      const definition = this.getSuspiciousAddressTableDefinition();
      if (!definition) return;
      this.markDeliverableBuilt(definition);
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        suspicious_address_table: {
          ...(this.caseSupportState.deliverableStateById?.suspicious_address_table || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: new Date().toISOString(),
          basisHash: this.getSuspiciousAddressTableState().currentBasisHash || '',
          reviewedAtUtc: options.keepReview ? (this.caseSupportState.deliverableStateById?.suspicious_address_table?.reviewedAtUtc || '') : ''
        }
      };
      this.updateSuspiciousAddressTableCheckResult();
    },

    stageSuspiciousAddressTablePreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging suspicious address table…',
          message: 'Building preview rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Suspicious address table staged.'
        }, () => this.stageSuspiciousAddressTablePreview({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getSuspiciousAddressTableState();
      if (!state.canBuild) {
        this.suspiciousAddressTableStatus = state.summary || 'Suspicious address table prerequisites are not current.';
        if (!options.silent) this.showCaseToast(this.suspiciousAddressTableStatus);
        return false;
      }
      const rows = buildCaseSuspiciousAddressTableDeliverableRows(state);
      this.loadOutputRows(rows, {
        title: 'Suspicious Address Table preview',
        detail: 'Tool 8F-style suspicious address table staged in Output for review. Main Data is not modified.',
        sourceActionId: 'stage_suspicious_address_table',
        sourceDeliverableId: 'suspicious_address_table',
        target: 'deliverable-preview'
      });
      this.caseSupportState.workflowState.suspiciousAddressTableReview = {
        ...(this.caseSupportState.workflowState.suspiciousAddressTableReview || {}),
        builtBasisHash: state.currentBasisHash,
        builtAtUtc: new Date().toISOString(),
        builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        rowCount: state.rowCount || 0,
        contributionCount: state.contributionCount || 0,
        uniqueTransactionCount: state.uniqueTransactionCount || 0
      };
      this.markSuspiciousAddressTableBuiltRecord();
      this.suspiciousAddressTableStatus = `Suspicious address table staged in Output with ${state.rowCount} row${state.rowCount === 1 ? '' : 's'}. Review it, then mark the table reviewed when it looks correct.`;
      if (!options.silent) {
        this.scrollToSection('case-output');
        this.showCaseToast(this.suspiciousAddressTableStatus);
      }
      return true;
    },

    downloadSuspiciousAddressTableTsv(options = {}) {
      const state = this.getSuspiciousAddressTableState();
      if (!state.canBuild) {
        this.suspiciousAddressTableStatus = state.summary || 'Suspicious address table prerequisites are not current.';
        if (!options.silent) this.showCaseToast(this.suspiciousAddressTableStatus);
        return false;
      }
      const rows = [
        CASE_SUSPICIOUS_ADDRESS_TABLE_HEADERS.slice(),
        ...(Array.isArray(state.rows) ? state.rows : []).map(row => [
          normalizeCell(row.address),
          normalizeCell(row.label),
          normalizeCell(row.receivedSummaryDisplay || row.receivedSummaryCell)
        ])
      ];
      downloadTextFile('suspicious_address_table.tsv', buildTsv(rows), 'text/tab-separated-values;charset=utf-8;');
      this.caseSupportState.workflowState.suspiciousAddressTableReview = {
        ...(this.caseSupportState.workflowState.suspiciousAddressTableReview || {}),
        builtBasisHash: state.currentBasisHash,
        builtAtUtc: new Date().toISOString(),
        builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        rowCount: state.rowCount || 0,
        contributionCount: state.contributionCount || 0,
        uniqueTransactionCount: state.uniqueTransactionCount || 0
      };
      this.markSuspiciousAddressTableBuiltRecord();
      this.suspiciousAddressTableStatus = `Suspicious address table TSV downloaded with ${state.rowCount} row${state.rowCount === 1 ? '' : 's'}.`;
      if (!options.silent) this.showCaseToast(this.suspiciousAddressTableStatus);
      return true;
    },

    markSuspiciousAddressTableReviewComplete(options = {}) {
      const state = this.getSuspiciousAddressTableState();
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      const basisHash = state.currentBasisHash || '';
      if (!state.canBuild) {
        this.suspiciousAddressTableStatus = state.summary || 'Suspicious address table prerequisites are not current.';
        return false;
      }

      let existing = this.caseSupportState.deliverableStateById?.suspicious_address_table || {};
      const builtIsCurrent = !!existing.builtAtUtc && Number(existing.builtRevision) === currentRevision && existing.basisHash === basisHash;
      if (!builtIsCurrent) {
        this.markSuspiciousAddressTableBuiltRecord();
        existing = this.caseSupportState.deliverableStateById?.suspicious_address_table || {};
      }

      const reviewedAtUtc = new Date().toISOString();
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        suspicious_address_table: {
          ...existing,
          builtRevision: currentRevision,
          builtAtUtc: existing.builtAtUtc || reviewedAtUtc,
          basisHash,
          reviewedAtUtc
        }
      };
      this.caseSupportState.workflowState.suspiciousAddressTableReview = {
        ...(this.caseSupportState.workflowState.suspiciousAddressTableReview || {}),
        reviewedBasisHash: basisHash,
        reviewedAtUtc: new Date().toISOString(),
        reviewedRevision: currentRevision,
        rowCount: state.rowCount || 0,
        contributionCount: state.contributionCount || 0,
        uniqueTransactionCount: state.uniqueTransactionCount || 0
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_SUSPICIOUS_ADDRESS_TABLE_REVIEWED, options.source || 'review_suspicious_address_table', { silent: true });
      this.updateSuspiciousAddressTableCheckResult();
      this.loadOutputRows(buildCaseSuspiciousAddressTableAuditRows(state, 'Review confirmed'), {
        title: 'Suspicious Address Table review audit',
        detail: 'Suspicious address table review was confirmed for the current case data.',
        sourceActionId: 'review_suspicious_address_table',
        sourceDeliverableId: 'suspicious_address_table',
        target: 'audit-only'
      });
      this.suspiciousAddressTableStatus = 'Suspicious address table marked reviewed for the current case data.';
      this.showCaseToast(this.suspiciousAddressTableStatus);
      return true;
    },

    toggleSuspiciousAddressTableReviewed() {
      const state = this.getSuspiciousAddressTableState();
      const existing = this.caseSupportState.deliverableStateById?.suspicious_address_table || {};
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      const basisHash = state.currentBasisHash || '';
      const builtIsCurrent = !!existing.builtAtUtc && Number(existing.builtRevision) === currentRevision && existing.basisHash === basisHash;
      if (!builtIsCurrent) {
        this.showCaseToast('Stage or download the current suspicious address table before marking it reviewed.');
        return false;
      }
      if (existing.reviewedAtUtc) {
        this.caseSupportState.deliverableStateById = {
          ...(this.caseSupportState.deliverableStateById || {}),
          suspicious_address_table: { ...existing, reviewedAtUtc: '' }
        };
        this.updateSuspiciousAddressTableCheckResult();
        this.showCaseToast('Suspicious address table review mark cleared.');
        return true;
      }
      return this.markSuspiciousAddressTableReviewComplete({ source: 'deliverables-panel' });
    },

    getExchangeAddressTableState(options = {}) {
      const buildOptions = {
        caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
        labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately,
        ...(options || {})
      };
      const cacheKey = this.getCaseComputationCacheKey?.('exchange-address-state', buildOptions) || '';
      return this.getCachedCaseComputation?.('state', cacheKey, () => buildCaseExchangeAddressTableState(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        buildOptions
      )) || buildCaseExchangeAddressTableState(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        buildOptions
      );
    },

    getExchangeAddressTableDefinition() {
      return CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'exchange_address_table') || null;
    },

    async openExchangeAddressTableModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening exchange address table…',
        message: 'Preparing exchange/service rows and review exclusions.',
        target: 'exchange-address-table-modal',
        finishMessage: 'Exchange address table preview ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.exchangeAddressTableSearch = '';
        this.exchangeAddressTableReviewComment = '';
        this.exchangeAddressTableViewFilter = options.view || 'included';
        this.showExchangeAddressTableModal = true;
        await this.yieldToCaseUi();
        this.refreshExchangeAddressTableReview({ silent: true });
        return true;
      });
    },

    cancelExchangeAddressTableModal() {
      this.showExchangeAddressTableModal = false;
    },

    refreshExchangeAddressTableReview(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const state = this.getExchangeAddressTableState();
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        exchange_address_table: this.buildExchangeAddressTableCheckResult(state)
      };
      this.persistCaseSupportState();
      this.exchangeAddressTableStatus = state.summary || 'Exchange address table preview refreshed.';
      if (!options.silent) this.showCaseToast(this.exchangeAddressTableStatus);
      return state;
    },

    buildExchangeAddressTableCheckResult(state = null) {
      const reviewState = state || this.getExchangeAddressTableState();
      const definition = this.getExchangeAddressTableDefinition();
      const deliverable = definition ? this.buildDeliverableView(definition) : null;
      if (!reviewState.legendBuildIsCurrent) {
        return this.buildCheckResult('exchange_address_table', 'fail', 'Exchange address table needs the Address Legend to be built from current Main Data first.', reviewState);
      }
      if (!reviewState.legendReviewIsCurrent) {
        return this.buildCheckResult('exchange_address_table', 'fail', 'Exchange address table needs the Address Legend to be reviewed for the current case data first.', reviewState);
      }
      if (deliverable?.status === 'current') {
        if (!deliverable.reviewed) {
          return this.buildCheckResult('exchange_address_table', 'warn', `Exchange address table is built current (${reviewState.visibleRowCount} included row${reviewState.visibleRowCount === 1 ? '' : 's'}), but it has not been marked reviewed yet.`, reviewState);
        }
        return this.buildCheckResult('exchange_address_table', 'pass', `Exchange address table is current and reviewed (${reviewState.visibleRowCount} included row${reviewState.visibleRowCount === 1 ? '' : 's'}; ${reviewState.excludedCurrentCount || 0} excluded).`, reviewState);
      }
      if (deliverable?.status === 'stale') {
        return this.buildCheckResult('exchange_address_table', 'fail', 'Exchange address table is stale because Main Data, the reviewed Address Legend, or row exclusions changed. Rebuild it.', reviewState);
      }
      return this.buildCheckResult('exchange_address_table', 'warn', reviewState.summary || 'Exchange address table is ready to build and review.', reviewState);
    },

    updateExchangeAddressTableCheckResult() {
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        exchange_address_table: this.buildExchangeAddressTableCheckResult()
      };
      this.persistCaseSupportState();
    },

    invalidateExchangeAddressTableReview() {
      const existing = this.caseSupportState.deliverableStateById?.exchange_address_table || {};
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        exchange_address_table: { ...existing, reviewedAtUtc: '' }
      };
      this.updateExchangeAddressTableCheckResult();
    },

    ensureExchangeAddressTableReviewState() {
      const workflowState = this.caseSupportState.workflowState || {};
      const current = workflowState.exchangeAddressTableReview && typeof workflowState.exchangeAddressTableReview === 'object'
        ? workflowState.exchangeAddressTableReview
        : {};
      const normalized = {
        ...current,
        exclusionsByAddressKey: current.exclusionsByAddressKey && typeof current.exclusionsByAddressKey === 'object'
          ? { ...current.exclusionsByAddressKey }
          : {}
      };
      this.caseSupportState.workflowState = {
        ...workflowState,
        exchangeAddressTableReview: normalized
      };
      return normalized;
    },

    excludeExchangeAddressTableRow(row = {}) {
      const key = normalizeCell(row.normalizedAddress || row.reviewKey);
      if (!key) return false;
      const review = this.ensureExchangeAddressTableReviewState();
      review.exclusionsByAddressKey = {
        ...(review.exclusionsByAddressKey || {}),
        [key]: {
          excludedAtUtc: new Date().toISOString(),
          comment: normalizeCell(this.exchangeAddressTableReviewComment),
          exchangeName: normalizeCell(row.exchangeName),
          label: normalizeCell(row.label),
          address: normalizeCell(row.address)
        }
      };
      review.reviewedBasisHash = '';
      review.reviewedAtUtc = '';
      this.persistCaseSupportState();
      this.invalidateExchangeAddressTableReview();
      this.exchangeAddressTableStatus = `${row.exchangeName || row.label || row.address || 'Exchange table row'} excluded from this case's exchange address table.`;
      this.showCaseToast(this.exchangeAddressTableStatus);
      return true;
    },

    restoreExchangeAddressTableRow(row = {}) {
      const key = normalizeCell(row.normalizedAddress || row.reviewKey);
      if (!key) return false;
      const review = this.ensureExchangeAddressTableReviewState();
      const next = { ...(review.exclusionsByAddressKey || {}) };
      delete next[key];
      review.exclusionsByAddressKey = next;
      review.reviewedBasisHash = '';
      review.reviewedAtUtc = '';
      this.persistCaseSupportState();
      this.invalidateExchangeAddressTableReview();
      this.exchangeAddressTableStatus = `${row.exchangeName || row.label || row.address || 'Exchange table row'} restored to this case's exchange address table.`;
      this.showCaseToast(this.exchangeAddressTableStatus);
      return true;
    },

    selectExchangeAddressTableSourceRow(row = {}) {
      const source = Array.isArray(row.sourceRows) && row.sourceRows.length > 0 ? row.sourceRows[0] : null;
      if (!source || !Number.isInteger(source.rowIndex)) return false;
      this.activateLogicalSheet('main');
      this.$nextTick(() => {
        const sheet = this.getInputSheet?.();
        sheet?.selectCell?.(source.rowIndex, 3);
        sheet?.hotInstance?.scrollViewportTo?.(source.rowIndex, 3, true, true);
      });
      return true;
    },

    markExchangeAddressTableBuiltRecord(options = {}) {
      const definition = this.getExchangeAddressTableDefinition();
      if (!definition) return;
      this.markDeliverableBuilt(definition);
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        exchange_address_table: {
          ...(this.caseSupportState.deliverableStateById?.exchange_address_table || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: new Date().toISOString(),
          basisHash: this.getExchangeAddressTableState().currentBasisHash || '',
          reviewedAtUtc: options.keepReview ? (this.caseSupportState.deliverableStateById?.exchange_address_table?.reviewedAtUtc || '') : ''
        }
      };
      this.updateExchangeAddressTableCheckResult();
    },

    stageExchangeAddressTablePreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging exchange address table…',
          message: 'Building preview rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Exchange address table staged.'
        }, () => this.stageExchangeAddressTablePreview({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getExchangeAddressTableState();
      if (!state.canBuild) {
        this.exchangeAddressTableStatus = state.summary || 'Exchange address table prerequisites are not current.';
        if (!options.silent) this.showCaseToast(this.exchangeAddressTableStatus);
        return false;
      }
      const rows = buildCaseExchangeAddressTableDeliverableRows(state);
      this.loadOutputRows(rows, {
        title: 'Exchange Address Table preview',
        detail: 'Tool 9I-style exchange address table staged in Output for review. Main Data is not modified.',
        sourceActionId: 'stage_exchange_address_table',
        sourceDeliverableId: 'exchange_address_table',
        target: 'deliverable-preview'
      });
      this.caseSupportState.workflowState.exchangeAddressTableReview = {
        ...(this.caseSupportState.workflowState.exchangeAddressTableReview || {}),
        builtBasisHash: state.currentBasisHash,
        builtAtUtc: new Date().toISOString(),
        builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        rowCount: state.visibleRowCount || 0,
        excludedCount: state.excludedCurrentCount || 0,
        contributionCount: state.contributionCount || 0,
        uniqueTransactionCount: state.uniqueTransactionCount || 0
      };
      this.markExchangeAddressTableBuiltRecord();
      this.exchangeAddressTableStatus = `Exchange address table staged in Output with ${state.visibleRowCount} included row${state.visibleRowCount === 1 ? '' : 's'}${state.excludedCurrentCount ? ` (${state.excludedCurrentCount} excluded)` : ''}. Review it, then mark the table reviewed when it looks correct.`;
      if (!options.silent) {
        this.scrollToSection('case-output');
        this.showCaseToast(this.exchangeAddressTableStatus);
      }
      return true;
    },

    downloadExchangeAddressTableTsv(options = {}) {
      const state = this.getExchangeAddressTableState();
      if (!state.canBuild) {
        this.exchangeAddressTableStatus = state.summary || 'Exchange address table prerequisites are not current.';
        if (!options.silent) this.showCaseToast(this.exchangeAddressTableStatus);
        return false;
      }
      downloadTextFile('exchange_address_table.tsv', buildTsv(buildCaseExchangeAddressTableDeliverableRows(state)), 'text/tab-separated-values;charset=utf-8;');
      this.caseSupportState.workflowState.exchangeAddressTableReview = {
        ...(this.caseSupportState.workflowState.exchangeAddressTableReview || {}),
        builtBasisHash: state.currentBasisHash,
        builtAtUtc: new Date().toISOString(),
        builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        rowCount: state.visibleRowCount || 0,
        excludedCount: state.excludedCurrentCount || 0,
        contributionCount: state.contributionCount || 0,
        uniqueTransactionCount: state.uniqueTransactionCount || 0
      };
      this.markExchangeAddressTableBuiltRecord();
      this.exchangeAddressTableStatus = `Exchange address table TSV downloaded with ${state.visibleRowCount} included row${state.visibleRowCount === 1 ? '' : 's'}.`;
      if (!options.silent) this.showCaseToast(this.exchangeAddressTableStatus);
      return true;
    },

    markExchangeAddressTableReviewComplete(options = {}) {
      const state = this.getExchangeAddressTableState();
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      const basisHash = state.currentBasisHash || '';
      if (!state.canBuild) {
        this.exchangeAddressTableStatus = state.summary || 'Exchange address table prerequisites are not current.';
        return false;
      }

      let existing = this.caseSupportState.deliverableStateById?.exchange_address_table || {};
      const builtIsCurrent = !!existing.builtAtUtc && Number(existing.builtRevision) === currentRevision && existing.basisHash === basisHash;
      if (!builtIsCurrent) {
        this.markExchangeAddressTableBuiltRecord();
        existing = this.caseSupportState.deliverableStateById?.exchange_address_table || {};
      }

      const reviewedAtUtc = new Date().toISOString();
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        exchange_address_table: {
          ...existing,
          builtRevision: currentRevision,
          builtAtUtc: existing.builtAtUtc || reviewedAtUtc,
          basisHash,
          reviewedAtUtc
        }
      };
      this.caseSupportState.workflowState.exchangeAddressTableReview = {
        ...(this.caseSupportState.workflowState.exchangeAddressTableReview || {}),
        reviewedBasisHash: basisHash,
        reviewedAtUtc: new Date().toISOString(),
        reviewedRevision: currentRevision,
        rowCount: state.visibleRowCount || 0,
        excludedCount: state.excludedCurrentCount || 0,
        contributionCount: state.contributionCount || 0,
        uniqueTransactionCount: state.uniqueTransactionCount || 0
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_EXCHANGE_ADDRESS_TABLE_REVIEWED, options.source || 'review_exchange_address_table', { silent: true });
      this.updateExchangeAddressTableCheckResult();
      this.loadOutputRows(buildCaseExchangeAddressTableAuditRows(state, 'Review confirmed'), {
        title: 'Exchange Address Table review audit',
        detail: 'Exchange address table review was confirmed for the current case data.',
        sourceActionId: 'review_exchange_address_table',
        sourceDeliverableId: 'exchange_address_table',
        target: 'audit-only'
      });
      this.exchangeAddressTableStatus = 'Exchange address table marked reviewed for the current case data.';
      this.showCaseToast(this.exchangeAddressTableStatus);
      return true;
    },

    toggleExchangeAddressTableReviewed() {
      const state = this.getExchangeAddressTableState();
      const existing = this.caseSupportState.deliverableStateById?.exchange_address_table || {};
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      const basisHash = state.currentBasisHash || '';
      const builtIsCurrent = !!existing.builtAtUtc && Number(existing.builtRevision) === currentRevision && existing.basisHash === basisHash;
      if (!builtIsCurrent) {
        this.showCaseToast('Stage or download the current exchange address table before marking it reviewed.');
        return false;
      }
      if (existing.reviewedAtUtc) {
        this.caseSupportState.deliverableStateById = {
          ...(this.caseSupportState.deliverableStateById || {}),
          exchange_address_table: { ...existing, reviewedAtUtc: '' }
        };
        this.updateExchangeAddressTableCheckResult();
        this.showCaseToast('Exchange address table review mark cleared.');
        return true;
      }
      return this.markExchangeAddressTableReviewComplete({ source: 'deliverables-panel' });
    },

    getDormantAddressDeliverablesState(options = {}) {
      const buildOptions = {
        caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
        labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately,
        ...(options || {})
      };
      const cacheKey = this.getCaseComputationCacheKey?.('dormant-address-state', buildOptions) || '';
      return this.getCachedCaseComputation?.('state', cacheKey, () => buildCaseDormantAddressDeliverablesState(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        buildOptions
      )) || buildCaseDormantAddressDeliverablesState(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        buildOptions
      );
    },

    getDormantAddressTableDefinition() {
      return CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'dormant_address_table') || null;
    },

    getDormantAddressMonitoringDefinition() {
      return CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'dormant_address_monitoring_view') || null;
    },

    async openDormantAddressDeliverablesModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening dormant address deliverables…',
        message: 'Building dormant table and monitoring previews.',
        target: 'dormant-address-deliverables-modal',
        finishMessage: 'Dormant deliverable previews ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.dormantAddressDeliverableSearch = '';
        this.dormantAddressDeliverableActiveView = options.view || 'official';
        this.showDormantAddressDeliverablesModal = true;
        await this.yieldToCaseUi();
        this.refreshDormantAddressDeliverablesReview({ silent: true });
        return true;
      });
    },

    cancelDormantAddressDeliverablesModal() {
      this.showDormantAddressDeliverablesModal = false;
    },

    refreshDormantAddressDeliverablesReview(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const state = this.getDormantAddressDeliverablesState();
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        dormant_address_deliverables: this.buildDormantAddressDeliverablesCheckResult(state)
      };
      this.persistCaseSupportState();
      this.dormantAddressDeliverableStatus = state.summary || 'Dormant address deliverables preview refreshed.';
      if (!options.silent) this.showCaseToast(this.dormantAddressDeliverableStatus);
      return state;
    },

    buildDormantAddressDeliverablesCheckResult(state = null) {
      const reviewState = state || this.getDormantAddressDeliverablesState();
      const tableDefinition = this.getDormantAddressTableDefinition();
      const monitoringDefinition = this.getDormantAddressMonitoringDefinition();
      const tableView = tableDefinition ? this.buildDeliverableView(tableDefinition) : null;
      const monitoringView = monitoringDefinition ? this.buildDeliverableView(monitoringDefinition) : null;
      if (!reviewState.legendBuildIsCurrent) {
        return this.buildCheckResult('dormant_address_deliverables', 'fail', 'Dormant address deliverables need the Address Legend to be built from current Main Data first.', reviewState);
      }
      if (!reviewState.legendReviewIsCurrent) {
        return this.buildCheckResult('dormant_address_deliverables', 'fail', 'Dormant address deliverables need the Address Legend to be reviewed for the current case data first.', reviewState);
      }
      const tableCurrent = tableView?.status === 'current';
      const monitoringCurrent = monitoringView?.status === 'current';
      if (tableCurrent && monitoringCurrent) {
        if (!tableView?.reviewed || !monitoringView?.reviewed) {
          return this.buildCheckResult('dormant_address_deliverables', 'warn', `Dormant address deliverables are built current (${reviewState.officialRowCount} clean row${reviewState.officialRowCount === 1 ? '' : 's'}; ${reviewState.monitoringRowCount} monitoring row${reviewState.monitoringRowCount === 1 ? '' : 's'}), but they have not both been marked reviewed.`, reviewState);
        }
        return this.buildCheckResult('dormant_address_deliverables', 'pass', `Dormant address deliverables are current and reviewed (${reviewState.officialRowCount} clean row${reviewState.officialRowCount === 1 ? '' : 's'}; ${reviewState.monitoringRowCount} monitoring row${reviewState.monitoringRowCount === 1 ? '' : 's'}).`, reviewState);
      }
      if (tableView?.status === 'stale' || monitoringView?.status === 'stale') {
        return this.buildCheckResult('dormant_address_deliverables', 'fail', 'Dormant address deliverables are stale because Main Data or the reviewed Address Legend changed. Rebuild them.', reviewState);
      }
      return this.buildCheckResult('dormant_address_deliverables', 'warn', reviewState.summary || 'Dormant address deliverables are ready to build and review.', reviewState);
    },

    updateDormantAddressDeliverablesCheckResult() {
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        dormant_address_deliverables: this.buildDormantAddressDeliverablesCheckResult()
      };
      this.persistCaseSupportState();
    },

    markDormantAddressDeliverableBuiltRecord(deliverableId, options = {}) {
      const definition = deliverableId === 'dormant_address_monitoring_view'
        ? this.getDormantAddressMonitoringDefinition()
        : this.getDormantAddressTableDefinition();
      if (!definition) return;
      this.markDeliverableBuilt(definition);
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        [definition.id]: {
          ...(this.caseSupportState.deliverableStateById?.[definition.id] || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: new Date().toISOString(),
          basisHash: this.getDormantAddressDeliverablesState().currentBasisHash || '',
          reviewedAtUtc: options.keepReview ? (this.caseSupportState.deliverableStateById?.[definition.id]?.reviewedAtUtc || '') : ''
        }
      };
      this.updateDormantAddressDeliverablesCheckResult();
    },

    markDormantAddressBothDeliverablesBuiltRecord(options = {}) {
      this.markDormantAddressDeliverableBuiltRecord('dormant_address_table', options);
      this.markDormantAddressDeliverableBuiltRecord('dormant_address_monitoring_view', options);
    },

    stageDormantAddressTablePreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging dormant address table…',
          message: 'Building preview rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Dormant address table staged.'
        }, () => this.stageDormantAddressTablePreview({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getDormantAddressDeliverablesState();
      if (!state.canBuild) {
        this.dormantAddressDeliverableStatus = state.summary || 'Dormant address table prerequisites are not current.';
        if (!options.silent) this.showCaseToast(this.dormantAddressDeliverableStatus);
        return false;
      }
      const rows = buildCaseDormantAddressTableDeliverableRows(state);
      this.loadOutputRows(rows, {
        title: 'Dormant Address Table preview',
        detail: 'Clean Tool 52C-style dormant address table staged in Output for review. Main Data is not modified.',
        sourceActionId: 'stage_dormant_address_table',
        sourceDeliverableId: 'dormant_address_table',
        target: 'deliverable-preview'
      });
      this.caseSupportState.workflowState.dormantAddressDeliverablesReview = {
        ...(this.caseSupportState.workflowState.dormantAddressDeliverablesReview || {}),
        builtBasisHash: state.currentBasisHash,
        builtAtUtc: new Date().toISOString(),
        builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        officialRowCount: state.officialRowCount || 0,
        monitoringRowCount: state.monitoringRowCount || 0,
        uniqueTransactionCount: state.uniqueTransactionCount || 0
      };
      this.markDormantAddressDeliverableBuiltRecord('dormant_address_table');
      this.dormantAddressDeliverableStatus = `Dormant address clean table staged in Output with ${state.officialRowCount || 0} row${(state.officialRowCount || 0) === 1 ? '' : 's'}.`;
      if (!options.silent) this.showCaseToast(this.dormantAddressDeliverableStatus);
      this.scrollToSection('case-output');
      return true;
    },

    stageDormantAddressMonitoringPreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging dormant monitoring view…',
          message: 'Building preview rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Dormant monitoring view staged.'
        }, () => this.stageDormantAddressMonitoringPreview({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getDormantAddressDeliverablesState();
      if (!state.canBuild) {
        this.dormantAddressDeliverableStatus = state.summary || 'Dormant address monitoring prerequisites are not current.';
        if (!options.silent) this.showCaseToast(this.dormantAddressDeliverableStatus);
        return false;
      }
      const rows = buildCaseDormantAddressMonitoringRows(state);
      this.loadOutputRows(rows, {
        title: 'Dormant Address Monitoring View preview',
        detail: 'Monitoring-oriented dormant address view staged in Output for review. Main Data is not modified.',
        sourceActionId: 'stage_dormant_monitoring_view',
        sourceDeliverableId: 'dormant_address_monitoring_view',
        target: 'deliverable-preview'
      });
      this.caseSupportState.workflowState.dormantAddressDeliverablesReview = {
        ...(this.caseSupportState.workflowState.dormantAddressDeliverablesReview || {}),
        builtBasisHash: state.currentBasisHash,
        builtAtUtc: new Date().toISOString(),
        builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        officialRowCount: state.officialRowCount || 0,
        monitoringRowCount: state.monitoringRowCount || 0,
        uniqueTransactionCount: state.uniqueTransactionCount || 0
      };
      this.markDormantAddressDeliverableBuiltRecord('dormant_address_monitoring_view');
      this.dormantAddressDeliverableStatus = `Dormant address monitoring view staged in Output with ${state.monitoringRowCount || 0} row${(state.monitoringRowCount || 0) === 1 ? '' : 's'}.`;
      if (!options.silent) this.showCaseToast(this.dormantAddressDeliverableStatus);
      this.scrollToSection('case-output');
      return true;
    },

    stageDormantAddressAuditPreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging dormant audit…',
          message: 'Building preview rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Dormant audit staged.'
        }, () => this.stageDormantAddressAuditPreview({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getDormantAddressDeliverablesState();
      if (!state.canBuild) {
        this.dormantAddressDeliverableStatus = state.summary || 'Dormant address deliverables prerequisites are not current.';
        if (!options.silent) this.showCaseToast(this.dormantAddressDeliverableStatus);
        return false;
      }
      this.loadOutputRows(buildCaseDormantAddressAuditRows(state), {
        title: 'Dormant Address Deliverables audit',
        detail: 'Audit-only dormant address deliverables preview.',
        sourceActionId: 'review_dormant_address_deliverables',
        sourceDeliverableId: 'dormant_address_table',
        target: 'audit-only'
      });
      if (!options.silent) this.showCaseToast('Dormant address deliverables audit staged in Output.');
      return true;
    },

    downloadDormantAddressTableTsv(options = {}) {
      const state = this.getDormantAddressDeliverablesState();
      if (!state.canBuild) {
        this.dormantAddressDeliverableStatus = state.summary || 'Dormant address table prerequisites are not current.';
        if (!options.silent) this.showCaseToast(this.dormantAddressDeliverableStatus);
        return false;
      }
      const rows = buildCaseDormantAddressTableDeliverableRows(state);
      downloadTextFile('dormant_address_table.tsv', buildTsv(rows), 'text/tab-separated-values;charset=utf-8;');
      this.markDormantAddressDeliverableBuiltRecord('dormant_address_table');
      this.dormantAddressDeliverableStatus = 'Dormant address clean table downloaded.';
      if (!options.silent) this.showCaseToast(this.dormantAddressDeliverableStatus);
      return true;
    },

    downloadDormantAddressMonitoringTsv(options = {}) {
      const state = this.getDormantAddressDeliverablesState();
      if (!state.canBuild) {
        this.dormantAddressDeliverableStatus = state.summary || 'Dormant address monitoring prerequisites are not current.';
        if (!options.silent) this.showCaseToast(this.dormantAddressDeliverableStatus);
        return false;
      }
      const rows = buildCaseDormantAddressMonitoringRows(state);
      downloadTextFile('dormant_address_monitoring_view.tsv', buildTsv(rows), 'text/tab-separated-values;charset=utf-8;');
      this.markDormantAddressDeliverableBuiltRecord('dormant_address_monitoring_view');
      this.dormantAddressDeliverableStatus = 'Dormant address monitoring view downloaded.';
      if (!options.silent) this.showCaseToast(this.dormantAddressDeliverableStatus);
      return true;
    },

    markDormantAddressDeliverablesReviewComplete(options = {}) {
      const state = this.getDormantAddressDeliverablesState();
      if (!state.canBuild) {
        this.dormantAddressDeliverableStatus = state.summary || 'Dormant address deliverables prerequisites are not current.';
        this.showCaseToast(this.dormantAddressDeliverableStatus);
        return false;
      }
      const reviewedAtUtc = new Date().toISOString();
      this.caseSupportState.workflowState.dormantAddressDeliverablesReview = {
        ...(this.caseSupportState.workflowState.dormantAddressDeliverablesReview || {}),
        builtBasisHash: state.currentBasisHash,
        reviewedBasisHash: state.currentBasisHash,
        reviewedAtUtc,
        builtAtUtc: this.caseSupportState.workflowState.dormantAddressDeliverablesReview?.builtAtUtc || reviewedAtUtc,
        builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        officialRowCount: state.officialRowCount || 0,
        monitoringRowCount: state.monitoringRowCount || 0,
        uniqueTransactionCount: state.uniqueTransactionCount || 0
      };
      this.markDormantAddressBothDeliverablesBuiltRecord({ keepReview: true });
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        dormant_address_table: {
          ...(this.caseSupportState.deliverableStateById?.dormant_address_table || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: this.caseSupportState.deliverableStateById?.dormant_address_table?.builtAtUtc || reviewedAtUtc,
          basisHash: state.currentBasisHash || '',
          reviewedAtUtc
        },
        dormant_address_monitoring_view: {
          ...(this.caseSupportState.deliverableStateById?.dormant_address_monitoring_view || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: this.caseSupportState.deliverableStateById?.dormant_address_monitoring_view?.builtAtUtc || reviewedAtUtc,
          basisHash: state.currentBasisHash || '',
          reviewedAtUtc
        }
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_DORMANT_ADDRESS_DELIVERABLES_REVIEWED, options.source || 'review_dormant_address_deliverables', { silent: true });
      this.updateDormantAddressDeliverablesCheckResult();
      this.persistCaseSupportState();
      this.loadOutputRows(buildCaseDormantAddressAuditRows(state, 'Dormant address deliverables reviewed'), {
        title: 'Dormant Address Deliverables reviewed',
        detail: 'Audit-only confirmation that the dormant address table and monitoring view were reviewed.',
        sourceActionId: 'review_dormant_address_deliverables',
        sourceDeliverableId: 'dormant_address_table',
        target: 'audit-only'
      });
      this.dormantAddressDeliverableStatus = `Dormant address deliverables marked reviewed (${state.officialRowCount || 0} clean row${(state.officialRowCount || 0) === 1 ? '' : 's'}; ${state.monitoringRowCount || 0} monitoring row${(state.monitoringRowCount || 0) === 1 ? '' : 's'}).`;
      this.showCaseToast(this.dormantAddressDeliverableStatus);
      return true;
    },

    toggleDormantAddressDeliverableReviewed(item = {}) {
      const id = item?.id || 'dormant_address_table';
      const state = this.getDormantAddressDeliverablesState();
      const existing = this.caseSupportState.deliverableStateById?.[id] || {};
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      const basisHash = state.currentBasisHash || '';
      const builtIsCurrent = !!existing.builtAtUtc && Number(existing.builtRevision) === currentRevision && existing.basisHash === basisHash;
      if (!builtIsCurrent) {
        this.showCaseToast('Stage, download, or review the current dormant deliverable before marking it reviewed.');
        return false;
      }
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        [id]: {
          ...existing,
          reviewedAtUtc: existing.reviewedAtUtc ? '' : new Date().toISOString()
        }
      };
      this.updateDormantAddressDeliverablesCheckResult();
      this.showCaseToast(existing.reviewedAtUtc ? 'Dormant deliverable review mark cleared.' : 'Dormant deliverable marked reviewed.');
      return true;
    },

    getDeliverableBasisHash(item = {}) {
      if (item.generator === 'case_template' || item.id === 'template') {
        return this.getCaseTemplateState().currentBasisHash || '';
      }
      if (item.generator === 'suspicious_address_table' || item.id === 'suspicious_address_table') {
        return this.getSuspiciousAddressTableState().currentBasisHash || '';
      }
      if (item.generator === 'exchange_address_table' || item.id === 'exchange_address_table') {
        return this.getExchangeAddressTableState().currentBasisHash || '';
      }
      if (item.generator === 'dormant_address_table' || item.generator === 'dormant_address_monitoring_view' || item.id === 'dormant_address_table' || item.id === 'dormant_address_monitoring_view') {
        return this.getDormantAddressDeliverablesState().currentBasisHash || '';
      }
      if (item.generator === 'what_went_where' || item.generator === 'what_went_where_valuation_audit' || item.id === 'what_went_where' || item.id === 'what_went_where_valuation_audit') {
        return this.getWhatWentWhereState().currentBasisHash || '';
      }
      if (item.generator === 'case_conclusion' || item.id === 'conclusion') {
        return this.getCaseConclusionState().currentBasisHash || '';
      }
      if (item.generator === 'case_report_body' || item.generator === 'case_report_body_spacing_tags' || item.id === 'report_body' || item.id === 'report_body_spacing_tags') {
        return this.getReportBodyState().currentBasisHash || '';
      }
      if (item.generator === 'case_addendum' || item.generator === 'case_addendum_audit' || item.generator === 'case_addendum_narrative' || item.id === 'addendum' || item.id === 'addendum_audit' || item.id === 'addendum_narrative') {
        return this.getCaseAddendumState().currentReviewBasisHash || '';
      }
      if (item.generator === 'case_flow_visuals' || item.generator === 'case_flow_visuals_placeholder' || item.id === 'case_flow_visuals') {
        return this.getCaseFlowVisualsState().currentBasisHash || '';
      }
      if (item.generator === 'case_completion_summary' || item.generator === 'full_report_package' || item.id === 'case_completion_summary' || item.id === 'one_click_report_package') {
        return this.getCaseFullReportPackageState({ includeDeliverableViews: false }).currentBasisHash || '';
      }
      return '';
    },

    buildDeliverableView(item) {
      const itemId = item?.id || item?.generator || 'deliverable';
      const state = this.caseSupportState.deliverableStateById?.[itemId] || {};
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      const requirementSnapshot = this.getDeliverableRequirementSnapshot(item?.requires || []);
      const cacheKey = this.getCaseComputationCacheKey?.(`deliverable-view-${itemId}`, {
        generator: item?.generator || '',
        requires: requirementSnapshot.map(({ requirement, met }) => `${requirement}:${met ? '1' : '0'}`),
        builtRevision: state.builtRevision || '',
        builtAtUtc: state.builtAtUtc || '',
        reviewedAtUtc: state.reviewedAtUtc || '',
        basisHash: state.basisHash || '',
        currentRevision,
        mainEntries: this.caseSummary?.mainEntries || 0,
        rawEntries: this.caseSummary?.rawEntries || 0,
        addresses: this.caseSummary?.caseAddressCount || this.caseSummary?.destinationAddressCount || 0,
        missingPathLabels: this.caseSummary?.missingPathLabels || 0,
        missingAttribution: this.caseSummary?.missingAttribution || 0
      }) || '';

      const buildView = () => {
        let available = requirementSnapshot.every(({ met }) => met);
        const builtRevision = Number.isFinite(Number(state.builtRevision)) ? Number(state.builtRevision) : null;
        const hasBuildRecord = builtRevision !== null || !!state.builtAtUtc;
        const revisionIsCurrent = builtRevision !== null && builtRevision === currentRevision;
        const shouldCheckBasisHash = available && hasBuildRecord;
        const currentBasisHash = shouldCheckBasisHash ? this.getDeliverableBasisHash(item) : (state.basisHash || '');
        const storedBasisHash = normalizeCell(state.basisHash);
        const normalizedCurrentBasisHash = normalizeCell(currentBasisHash);
        const basisIsCurrent = !!storedBasisHash && !!normalizedCurrentBasisHash && storedBasisHash === normalizedCurrentBasisHash;
        let current = available && hasBuildRecord && (basisIsCurrent || (!normalizedCurrentBasisHash && revisionIsCurrent));
        let stale = available && hasBuildRecord && !current;

        if ((item.id === 'template' || item.generator === 'case_template') && typeof this.getTemplateDeliverableRecordStatus === 'function') {
          const templateStatus = this.getTemplateDeliverableRecordStatus();
          const templateSessionCurrent = typeof this.templateDeliverableBuildSessionIsCurrent === 'function'
            ? this.templateDeliverableBuildSessionIsCurrent(templateStatus.state)
            : false;
          if (templateStatus.builtIsCurrent || templateSessionCurrent) {
            current = true;
            stale = false;
          } else if (available && templateStatus.hasBuildRecord) {
            stale = true;
          }
        }

        if (current || stale) available = true;

        const generatorAvailable = item.generator !== 'placeholder';
        const canAccessGenerator = generatorAvailable && (available || current || stale);
        const canDownloadGenerator = canAccessGenerator && this.deliverableGeneratorCanDownload(item.generator);
        let status = 'unavailable';
        let statusLabel = 'Unavailable';

        if (current) {
          status = 'current';
          statusLabel = state.reviewedAtUtc ? 'Current · reviewed' : 'Current';
        } else if (stale) {
          status = 'stale';
          statusLabel = 'Stale';
        } else if (available) {
          status = generatorAvailable ? 'ready' : 'tool-pending';
          statusLabel = generatorAvailable ? 'Ready' : 'Tool pending';
        }

        return {
          ...item,
          available,
          status,
          statusLabel,
          statusClass: `deliverable-card--${status}`,
          canBuild: canAccessGenerator,
          canDownload: canDownloadGenerator,
          primaryActionLabel: this.getDeliverablePrimaryActionLabel(item),
          builtAtUtc: state.builtAtUtc || '',
          builtAtLabel: safeDateLabel(state.builtAtUtc),
          builtRevision,
          basisHash: currentBasisHash,
          reviewed: current && !!state.reviewedAtUtc,
          requirementLabel: available ? (generatorAvailable ? 'Ready to preview.' : 'Requires migrated deliverable tool.') : this.describeDeliverableRequirements(item)
        };
      };

      return this.getCachedCaseComputation?.('view', cacheKey, buildView) || buildView();
    },

    getDeliverablePrimaryActionLabel(item = {}) {
      if (item.id === 'case_flow_visuals' || item.generator === 'case_flow_visuals' || item.generator === 'case_flow_visuals_placeholder') {
        return 'Preview / download visuals';
      }
      if (item.id === 'one_click_report_package' || item.generator === 'full_report_package') {
        return 'Open package preview';
      }
      if (item.id === 'case_completion_summary' || item.generator === 'case_completion_summary') {
        return 'Build summary';
      }
      return 'Preview / build';
    },

    describeDeliverableRequirements(item) {
      const unmet = (item.requires || []).filter(requirement => !this.isDeliverableRequirementMet(requirement));
      if (unmet.length === 0) return 'Ready.';
      const labels = {
        mainEntries: 'main entries',
        rawEntries: 'raw entries',
        addresses: 'case addresses',
        pathLabels: 'path labels',
        attribution: 'attribution',
        addressLegendReviewed: 'current reviewed address legend',
        addendumCanBuild: 'current reviewed Tool55/Tool48 addendum inputs',
        fullReportPackageReady: 'current reviewed report body and addendum'
      };
      return `Needs ${unmet.map(requirement => labels[requirement] || requirement).join(', ')}.`;
    },

    markDeliverableBuilt(item) {
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        [item.id]: {
          ...(this.caseSupportState.deliverableStateById?.[item.id] || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: new Date().toISOString(),
          basisHash: this.getDeliverableBasisHash(item),
          reviewedAtUtc: ''
        }
      };
      this.persistCaseSupportState();
    },

    async buildDeliverable(item, options = {}) {
      if (item?.id === 'case_flow_visuals' || item?.generator === 'case_flow_visuals' || item?.generator === 'case_flow_visuals_placeholder') {
        if (item?.canBuild === false) {
          this.showCaseToast(item?.requirementLabel || 'Case flow visuals are not ready yet.');
          return false;
        }
        return this.openCaseFlowVisualsModal({
          ...options,
          source: options.source || 'deliverables-panel'
        });
      }

      if (item?.id === 'one_click_report_package' || item?.generator === 'full_report_package') {
        if (item?.canBuild === false) {
          this.showCaseToast(item?.requirementLabel || 'The one-click report package is not ready yet.');
          return false;
        }
        return this.openCaseFullReportPackageModal({
          ...options,
          source: options.source || 'deliverables-panel'
        });
      }

      if (this.shouldRunCaseOperationWrapper?.(options) && item?.canBuild !== false) {
        return this.runCaseModalOperation({
          title: 'Preparing deliverable preview…',
          message: `Building ${item?.title || 'deliverable'} output and updating Output/Review.`,
          stepMessage: 'Reading current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Deliverable preview ready.'
        }, () => this.buildDeliverable(item, { ...options, __caseOperationWrapped: true }));
      }

      const wrappedOptions = { ...options, __caseOperationWrapped: true };
      if (item?.id === 'template' || item?.generator === 'case_template') {
        return this.stageTemplateDeliverablePreview(wrappedOptions);
      }
      if (item?.id === 'suspicious_address_table' || item?.generator === 'suspicious_address_table') {
        return this.stageSuspiciousAddressTablePreview(wrappedOptions);
      }
      if (item?.id === 'exchange_address_table' || item?.generator === 'exchange_address_table') {
        return this.stageExchangeAddressTablePreview(wrappedOptions);
      }
      if (item?.id === 'dormant_address_table' || item?.generator === 'dormant_address_table') {
        return this.stageDormantAddressTablePreview(wrappedOptions);
      }
      if (item?.id === 'dormant_address_monitoring_view' || item?.generator === 'dormant_address_monitoring_view') {
        return this.stageDormantAddressMonitoringPreview(wrappedOptions);
      }
      if (item?.id === 'what_went_where' || item?.generator === 'what_went_where') {
        return this.stageWhatWentWherePreview(wrappedOptions);
      }
      if (item?.id === 'what_went_where_valuation_audit' || item?.generator === 'what_went_where_valuation_audit') {
        return this.stageWhatWentWhereValuationAudit(wrappedOptions);
      }
      if (item?.id === 'conclusion' || item?.generator === 'case_conclusion') {
        return this.stageCaseConclusionPreview(wrappedOptions);
      }
      if (item?.id === 'report_body' || item?.generator === 'case_report_body') {
        return this.stageReportBodyPreview({ ...wrappedOptions, variantId: CASE_REPORT_BODY_VARIANT_STANDARD });
      }
      if (item?.id === 'report_body_spacing_tags' || item?.generator === 'case_report_body_spacing_tags') {
        return this.stageReportBodyPreview({ ...wrappedOptions, variantId: CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING });
      }
      if (item?.id === 'addendum' || item?.generator === 'case_addendum') {
        return this.stageCaseAddendumPreview({ ...wrappedOptions, variantId: CASE_ADDENDUM_VARIANT_STRUCTURED });
      }
      if (item?.id === 'addendum_narrative' || item?.generator === 'case_addendum_narrative') {
        return this.stageCaseAddendumPreview({ ...wrappedOptions, variantId: CASE_ADDENDUM_VARIANT_NARRATIVE });
      }
      if (item?.id === 'addendum_audit' || item?.generator === 'case_addendum_audit') {
        return this.stageCaseAddendumAudit(wrappedOptions);
      }
      if (item?.id === 'case_completion_summary' || item?.generator === 'case_completion_summary') {
        return this.stageCaseCompletionSummaryPreview(wrappedOptions);
      }
      if (!item?.canBuild) {
        this.showCaseToast(item?.requirementLabel || 'This deliverable is not ready yet.');
        return false;
      }

      const rows = this.buildDeliverablePreviewRows(item);
      this.loadOutputRows(rows, {
        title: `${item.title} preview`,
        detail: 'Deliverable preview staged in the output buffer for review.',
        sourceDeliverableId: item.id,
        target: 'deliverable-preview'
      });
      this.markDeliverableBuilt(item);
      if (item.id === 'template' || item.generator === 'case_template') {
        this.caseSupportState.checkResultsById = {
          ...(this.caseSupportState.checkResultsById || {}),
          template_deliverable: this.buildTemplateDeliverableCheckResult()
        };
        this.persistCaseSupportState();
      }
      this.scrollToSection('case-output');
      this.showCaseToast(`${item.title} preview built in the output buffer.`);
      return true;
    },

    buildDeliverablePreviewRows(item) {
      if (item.generator === 'main_tsv') {
        return this.getMatrixForLogicalSheet('main').filter(rowHasMeaningfulData);
      }
      if (item.generator === 'raw_tsv') {
        return this.getMatrixForLogicalSheet('raw').filter(rowHasMeaningfulData);
      }
      if (item.generator === 'duplicate_hash_table') {
        return buildDuplicateHashTableRows(buildCaseDuplicateHashLabelerAnalysis(
          this.getMatrixForLogicalSheet('main'),
          this.caseSupportState.workflowState || {},
          { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
        ));
      }
      if (item.generator === 'notes_backup') {
        const backup = this.caseSupportState.workflowState.noteHashPathLabelRewriteReview?.backup || buildCaseNoteHashRewriteBackupRecord(
          this.getMatrixForLogicalSheet('main'),
          { caseId: this.currentProjectFileId || this.caseDisplayId || 'case', revision: Number(this.caseSupportState.caseDataRevision) || 0 }
        );
        return buildCaseNoteHashRewriteBackupOutputRows(backup);
      }
      if (item.generator === 'address_legend_preview') {
        const analysis = buildCaseAddressLegendAnalysis(
          this.getMatrixForLogicalSheet('main'),
          this.depositHotWalletEffectiveCurator,
          this.caseSupportState.workflowState || {},
          { labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately }
        );
        const storedEntries = Array.isArray(this.caseSupportState.workflowState?.addressLegendReview?.entries)
          ? this.caseSupportState.workflowState.addressLegendReview.entries
          : [];
        if (analysis.buildIsCurrent && storedEntries.length > 0) {
          return buildCaseAddressLegendDeliverableRows(analysis, { entries: storedEntries });
        }
        return buildCaseAddressLegendDeliverableRows(analysis);
      }
      if (item.generator === 'case_template') {
        return buildCaseTemplateDeliverableRows(this.getCaseTemplateState());
      }
      if (item.generator === 'suspicious_address_table') {
        return buildCaseSuspiciousAddressTableDeliverableRows(this.getSuspiciousAddressTableState());
      }
      if (item.generator === 'exchange_address_table') {
        return buildCaseExchangeAddressTableDeliverableRows(this.getExchangeAddressTableState());
      }
      if (item.generator === 'dormant_address_table') {
        return buildCaseDormantAddressTableDeliverableRows(this.getDormantAddressDeliverablesState());
      }
      if (item.generator === 'dormant_address_monitoring_view') {
        return buildCaseDormantAddressMonitoringRows(this.getDormantAddressDeliverablesState());
      }
      if (item.generator === 'what_went_where') {
        return buildCaseWhatWentWhereDeliverableRows(this.getWhatWentWhereState());
      }
      if (item.generator === 'what_went_where_valuation_audit') {
        return buildCaseWhatWentWhereValuationAuditRows(this.getWhatWentWhereState());
      }
      if (item.generator === 'case_conclusion') {
        const state = this.getCaseConclusionState();
        return buildCaseConclusionPreviewRows(state, this.caseConclusionDraft || state.selectedText || state.generatedText);
      }
      if (item.generator === 'case_report_body') {
        return buildCaseReportBodyPreviewRows(this.getReportBodyState(), CASE_REPORT_BODY_VARIANT_STANDARD);
      }
      if (item.generator === 'case_report_body_spacing_tags') {
        return buildCaseReportBodyPreviewRows(this.getReportBodyState(), CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING);
      }
      if (item.generator === 'case_addendum') {
        return buildCaseAddendumPreviewRows(this.getCaseAddendumState(), CASE_ADDENDUM_VARIANT_STRUCTURED);
      }
      if (item.generator === 'case_addendum_narrative') {
        return buildCaseAddendumPreviewRows(this.getCaseAddendumState(), CASE_ADDENDUM_VARIANT_NARRATIVE);
      }
      if (item.generator === 'case_addendum_audit') {
        return buildCaseAddendumAuditRows(this.getCaseAddendumState(), 'Deliverable preview');
      }
      if (item.generator === 'case_flow_visuals' || item.generator === 'case_flow_visuals_placeholder') {
        const state = this.getCaseFlowVisualsState();
        return buildCaseFlowVisualsPackageRows(state, {
          caseDisplayName: this.caseDisplayName,
          caseDisplayId: this.caseDisplayId,
          caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          reviewIsCurrent: state.reviewIsCurrent,
          completedAtUtc: state.completedAtUtc,
          source: state.source,
          currentBasisHash: state.currentBasisHash
        });
      }
      if (item.generator === 'case_completion_summary') {
        return this.buildCaseCompletionSummaryRows();
      }
      if (item.generator === 'full_report_package') {
        const payload = this.getCaseFullReportPackagePayload({ includeDeliverableViews: true });
        return buildCaseFullReportPackagePreviewRows(payload, payload.state);
      }
      return [[item.title, 'Preview generator pending']];
    },

    downloadDeliverable(item) {
      if (item?.id === 'template' || item?.generator === 'case_template') {
        this.downloadTemplateDeliverableTsv();
        return;
      }
      if (item?.id === 'suspicious_address_table' || item?.generator === 'suspicious_address_table') {
        this.downloadSuspiciousAddressTableTsv();
        return;
      }
      if (item?.id === 'exchange_address_table' || item?.generator === 'exchange_address_table') {
        this.downloadExchangeAddressTableTsv();
        return;
      }
      if (item?.id === 'dormant_address_table' || item?.generator === 'dormant_address_table') {
        this.downloadDormantAddressTableTsv();
        return;
      }
      if (item?.id === 'dormant_address_monitoring_view' || item?.generator === 'dormant_address_monitoring_view') {
        this.downloadDormantAddressMonitoringTsv();
        return;
      }
      if (item?.id === 'what_went_where' || item?.generator === 'what_went_where') {
        this.downloadWhatWentWhereTsv();
        return;
      }
      if (item?.id === 'what_went_where_valuation_audit' || item?.generator === 'what_went_where_valuation_audit') {
        this.downloadWhatWentWhereValuationAuditTsv();
        return;
      }
      if (item?.id === 'conclusion' || item?.generator === 'case_conclusion') {
        this.downloadCaseConclusionText();
        return;
      }
      if (item?.id === 'report_body' || item?.generator === 'case_report_body') {
        this.downloadReportBodyText({ variantId: CASE_REPORT_BODY_VARIANT_STANDARD });
        return;
      }
      if (item?.id === 'report_body_spacing_tags' || item?.generator === 'case_report_body_spacing_tags') {
        this.downloadReportBodyText({ variantId: CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING });
        return;
      }
      if (item?.id === 'addendum' || item?.generator === 'case_addendum') {
        this.downloadCaseAddendumText({ variantId: CASE_ADDENDUM_VARIANT_STRUCTURED });
        return;
      }
      if (item?.id === 'addendum_narrative' || item?.generator === 'case_addendum_narrative') {
        this.downloadCaseAddendumText({ variantId: CASE_ADDENDUM_VARIANT_NARRATIVE });
        return;
      }
      if (item?.id === 'addendum_audit' || item?.generator === 'case_addendum_audit') {
        this.downloadCaseAddendumAuditTsv();
        return;
      }
      if (item?.id === 'case_flow_visuals' || item?.generator === 'case_flow_visuals' || item?.generator === 'case_flow_visuals_placeholder') {
        this.downloadCaseFlowVisualsBundle('svg', { markReviewed: true });
        return;
      }
      if (item?.id === 'case_completion_summary' || item?.generator === 'case_completion_summary') {
        this.downloadCaseCompletionSummaryText();
        return;
      }
      if (item?.id === 'one_click_report_package' || item?.generator === 'full_report_package') {
        this.downloadCaseFullReportPackageHtml();
        return;
      }
      if (!item?.canDownload) {
        this.showCaseToast('This deliverable does not have a download generator yet.');
        return;
      }

      const rows = this.buildDeliverablePreviewRows(item);
      const filename = `${String(item.id || 'deliverable').replace(/[^a-z0-9_-]+/gi, '_')}.tsv`;
      downloadTextFile(filename, buildTsv(rows), 'text/tab-separated-values;charset=utf-8;');
      this.markDeliverableBuilt(item);
      if (item.id === 'template' || item.generator === 'case_template') {
        this.caseSupportState.checkResultsById = {
          ...(this.caseSupportState.checkResultsById || {}),
          template_deliverable: this.buildTemplateDeliverableCheckResult()
        };
        this.persistCaseSupportState();
      }
      this.showCaseToast(`${item.title} downloaded.`);
    },

    toggleDeliverableReviewed(item) {
      if (item?.id === 'template' || item?.generator === 'case_template') {
        this.toggleTemplateDeliverableReviewed();
        return;
      }
      if (item?.id === 'suspicious_address_table' || item?.generator === 'suspicious_address_table') {
        this.toggleSuspiciousAddressTableReviewed();
        return;
      }
      if (item?.id === 'exchange_address_table' || item?.generator === 'exchange_address_table') {
        this.toggleExchangeAddressTableReviewed();
        return;
      }
      if (item?.id === 'dormant_address_table' || item?.id === 'dormant_address_monitoring_view' || item?.generator === 'dormant_address_table' || item?.generator === 'dormant_address_monitoring_view') {
        this.toggleDormantAddressDeliverableReviewed(item);
        return;
      }
      if (item?.id === 'what_went_where' || item?.id === 'what_went_where_valuation_audit' || item?.generator === 'what_went_where' || item?.generator === 'what_went_where_valuation_audit') {
        this.toggleWhatWentWhereReviewed(item);
        return;
      }
      if (item?.id === 'conclusion' || item?.generator === 'case_conclusion') {
        this.toggleCaseConclusionReviewed(item);
        return;
      }
      if (item?.id === 'report_body' || item?.id === 'report_body_spacing_tags' || item?.generator === 'case_report_body' || item?.generator === 'case_report_body_spacing_tags') {
        this.toggleReportBodyReviewed(item);
        return;
      }
      if (item?.id === 'addendum' || item?.id === 'addendum_audit' || item?.id === 'addendum_narrative' || item?.generator === 'case_addendum' || item?.generator === 'case_addendum_audit' || item?.generator === 'case_addendum_narrative') {
        this.toggleCaseAddendumReviewed(item);
        return;
      }
      if (item?.id === 'case_flow_visuals' || item?.generator === 'case_flow_visuals' || item?.generator === 'case_flow_visuals_placeholder') {
        this.toggleCaseFlowVisualsReviewed();
        return;
      }
      if (item?.id === 'one_click_report_package' || item?.generator === 'full_report_package') {
        this.toggleCaseFullReportPackageReviewed();
        return;
      }
      if (!item?.id) return;
      const existing = this.caseSupportState.deliverableStateById?.[item.id];
      if (!existing?.builtAtUtc) return;

      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        [item.id]: {
          ...existing,
          reviewedAtUtc: existing.reviewedAtUtc ? '' : new Date().toISOString()
        }
      };
      this.persistCaseSupportState();
    },

    getWwwUsdReferenceReviewState(options = {}) {
      return buildCaseWwwUsdReferenceReviewState(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        {
          caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
          caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          ...(options || {})
        }
      );
    },

    async openWwwUsdReferenceReviewModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening USD reference review…',
        message: 'Preparing top-level Column H reference rows before deliverable buildout.',
        target: 'www-usd-reference-modal',
        finishMessage: 'USD reference review ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.wwwUsdReferenceSearch = '';
        this.wwwUsdReferenceViewFilter = options.view || 'missing';
        this.showWwwUsdReferenceModal = true;
        await this.yieldToCaseUi();
        this.refreshWwwUsdReferenceReview({ silent: true });
        this.initializeWwwUsdReferenceDrafts();
        return true;
      });
    },

    cancelWwwUsdReferenceReviewModal() {
      if (this.whatWentWhereEstimateRunning) return;
      this.showWwwUsdReferenceModal = false;
    },

    initializeWwwUsdReferenceDrafts() {
      const state = this.getWwwUsdReferenceReviewState();
      const drafts = { ...(this.whatWentWhereDraftByKey || {}) };
      const selected = { ...(this.whatWentWhereSelectedReferenceKeys || {}) };
      (state.referenceRows || []).forEach((row) => {
        if (drafts[row.key] == null) drafts[row.key] = row.currentColumnH || '';
        if (!row.isUsable && selected[row.key] == null) selected[row.key] = true;
      });
      this.whatWentWhereDraftByKey = drafts;
      this.whatWentWhereSelectedReferenceKeys = selected;
    },

    refreshWwwUsdReferenceReview(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const state = this.getWwwUsdReferenceReviewState();
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        www_usd_references: this.buildWwwUsdReferenceReviewCheckResult(state)
      };
      this.persistCaseSupportState();
      this.wwwUsdReferenceStatus = state.summary || 'Column H USD reference review refreshed.';
      if (!options.silent) this.showCaseToast(this.wwwUsdReferenceStatus);
      return state;
    },

    buildWwwUsdReferenceReviewCheckResult(state = null) {
      const reviewState = state || this.getWwwUsdReferenceReviewState();
      if ((reviewState.referenceRowCount || 0) === 0) {
        return this.buildCheckResult('www_usd_references', 'fail', 'No top-level main transactions were found for Column H USD reference review.', reviewState);
      }
      if ((reviewState.missingUsdReferenceCount || 0) > 0) {
        return this.buildCheckResult('www_usd_references', 'fail', `${reviewState.missingUsdReferenceCount} top-level main transaction${reviewState.missingUsdReferenceCount === 1 ? '' : 's'} still need usable Column H USD reference values.`, reviewState);
      }
      if (!reviewState.reviewIsCurrent) {
        return this.buildCheckResult('www_usd_references', 'warn', 'Column H USD reference values are filled, but this review step has not been marked complete for the current case data.', reviewState);
      }
      return this.buildCheckResult('www_usd_references', 'pass', `Column H USD references are reviewed for ${reviewState.referenceRowCount || 0} top-level main transaction${(reviewState.referenceRowCount || 0) === 1 ? '' : 's'}.`, reviewState);
    },

    updateWwwUsdReferenceReviewCheckResult() {
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        www_usd_references: this.buildWwwUsdReferenceReviewCheckResult()
      };
      this.persistCaseSupportState();
    },

    stageWwwUsdReferenceReviewAudit(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging USD reference audit…',
          message: 'Building the Column H reference audit and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'USD reference audit staged.'
        }, () => this.stageWwwUsdReferenceReviewAudit({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getWwwUsdReferenceReviewState();
      this.loadOutputRows(buildCaseWwwUsdReferenceAuditRows(state, 'Column H USD reference audit staged'), {
        title: 'Column H USD Reference Audit',
        detail: 'Audit only. Review/valuation helper output staged in Output; Main Data is not modified by this staging action.',
        sourceActionId: 'stage_www_usd_reference_audit',
        target: 'audit-only'
      });
      this.wwwUsdReferenceStatus = 'Column H USD reference audit staged in Output.';
      if (!options.silent) {
        this.scrollToSection('case-output');
        this.showCaseToast(this.wwwUsdReferenceStatus);
      }
      return true;
    },

    markWwwUsdReferenceReviewComplete(options = {}) {
      const state = this.getWwwUsdReferenceReviewState();
      if (!state.canReview) {
        this.wwwUsdReferenceStatus = state.summary || 'Resolve Column H USD reference values before marking this step reviewed.';
        this.showCaseToast(this.wwwUsdReferenceStatus);
        return false;
      }
      const reviewedAtUtc = new Date().toISOString();
      this.caseSupportState.workflowState.whatWentWhereUsdReferenceReview = {
        ...(this.caseSupportState.workflowState.whatWentWhereUsdReferenceReview || {}),
        basisHash: state.currentBasisHash,
        reviewedAtUtc,
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        referenceRowCount: state.referenceRowCount || 0,
        usableReferenceCount: state.usableReferenceCount || 0,
        missingUsdReferenceCount: state.missingUsdReferenceCount || 0,
        source: options.source || 'review_www_usd_references'
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_WWW_USD_REFERENCES_REVIEWED, options.source || 'review_www_usd_references', { silent: true });
      this.updateWwwUsdReferenceReviewCheckResult();
      this.loadOutputRows(buildCaseWwwUsdReferenceAuditRows(state, 'Column H USD references reviewed'), {
        title: 'Column H USD Reference Review Audit',
        detail: 'Column H USD references were marked reviewed for the current case data.',
        sourceActionId: 'review_www_usd_references',
        target: 'audit-only'
      });
      this.wwwUsdReferenceStatus = `Column H USD references reviewed for ${state.referenceRowCount || 0} top-level main transaction${(state.referenceRowCount || 0) === 1 ? '' : 's'}.`;
      this.showCaseToast(this.wwwUsdReferenceStatus);
      return true;
    },

    getWhatWentWhereState(options = {}) {
      const buildOptions = {
        caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
        caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        ...(options || {})
      };
      const cacheKey = this.getCaseComputationCacheKey?.('what-went-where-state', buildOptions) || '';
      return this.getCachedCaseComputation?.('state', cacheKey, () => buildCaseWhatWentWhereState(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        buildOptions
      )) || buildCaseWhatWentWhereState(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        buildOptions
      );
    },

    getWhatWentWhereDefinition() {
      return CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'what_went_where') || null;
    },

    getWhatWentWhereAuditDefinition() {
      return CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'what_went_where_valuation_audit') || null;
    },

    async openWhatWentWhereModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening What Went Where…',
        message: 'Preparing path summaries and valuation-reference review state.',
        target: 'what-went-where-modal',
        finishMessage: 'What Went Where preview ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.whatWentWhereSearch = '';
        const referenceState = this.getWwwUsdReferenceReviewState();
        this.whatWentWhereViewFilter = options.view || (referenceState.canReview ? 'path_endings' : 'references_missing');
        this.showWhatWentWhereModal = true;
        await this.yieldToCaseUi();
        this.refreshWhatWentWhereReview({ silent: true });
        this.initializeWhatWentWhereDrafts();
        return true;
      });
    },

    cancelWhatWentWhereModal() {
      if (this.whatWentWhereEstimateRunning) return;
      this.showWhatWentWhereModal = false;
    },

    initializeWhatWentWhereDrafts() {
      const state = this.getWhatWentWhereState();
      const drafts = { ...(this.whatWentWhereDraftByKey || {}) };
      const selected = { ...(this.whatWentWhereSelectedReferenceKeys || {}) };
      (state.referenceRows || []).forEach((row) => {
        if (drafts[row.key] == null) drafts[row.key] = row.currentColumnH || '';
        if (!row.isUsable && selected[row.key] == null) selected[row.key] = true;
      });
      this.whatWentWhereDraftByKey = drafts;
      this.whatWentWhereSelectedReferenceKeys = selected;
    },

    refreshWhatWentWhereReview(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const state = this.getWhatWentWhereState();
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        what_went_where: this.buildWhatWentWhereCheckResult(state)
      };
      this.persistCaseSupportState();
      this.whatWentWhereStatus = state.summary || 'What Went Where preview refreshed.';
      if (!options.silent) this.showCaseToast(this.whatWentWhereStatus);
      return state;
    },

    buildWhatWentWhereCheckResult(state = null) {
      const reviewState = state || this.getWhatWentWhereState();
      const mainDefinition = this.getWhatWentWhereDefinition();
      const auditDefinition = this.getWhatWentWhereAuditDefinition();
      const mainView = mainDefinition ? this.buildDeliverableView(mainDefinition) : null;
      const auditView = auditDefinition ? this.buildDeliverableView(auditDefinition) : null;
      if ((reviewState.referenceRowCount || 0) === 0) {
        return this.buildCheckResult('what_went_where', 'fail', 'No top-level main transactions were found for What Went Where.', reviewState);
      }
      if ((reviewState.missingUsdReferenceCount || 0) > 0) {
        return this.buildCheckResult('what_went_where', 'fail', `${reviewState.missingUsdReferenceCount} top-level main transaction${reviewState.missingUsdReferenceCount === 1 ? '' : 's'} still need usable Column H USD reference values.`, reviewState);
      }
      const mainCurrent = mainView?.status === 'current';
      const auditCurrent = auditView?.status === 'current';
      if (mainCurrent && auditCurrent) {
        if (!mainView?.reviewed || !auditView?.reviewed) {
          return this.buildCheckResult('what_went_where', 'warn', 'What Went Where and valuation audit are built current, but both have not been marked reviewed yet.', reviewState);
        }
        return this.buildCheckResult('what_went_where', 'pass', `What Went Where is current and reviewed with ${reviewState.generatedPathEndingCount || 0} generated path-ending row${(reviewState.generatedPathEndingCount || 0) === 1 ? '' : 's'}.`, reviewState);
      }
      if (mainView?.status === 'stale' || auditView?.status === 'stale') {
        return this.buildCheckResult('what_went_where', 'fail', 'What Went Where is stale because Main Data or Column H reference values changed. Rebuild it.', reviewState);
      }
      return this.buildCheckResult('what_went_where', 'warn', reviewState.summary || 'What Went Where is ready to build and review.', reviewState);
    },

    updateWhatWentWhereCheckResult() {
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        what_went_where: this.buildWhatWentWhereCheckResult()
      };
      this.persistCaseSupportState();
    },

    getWhatWentWhereDraftValue(row = {}) {
      if (!row?.key) return '';
      return this.whatWentWhereDraftByKey?.[row.key] ?? row.currentColumnH ?? '';
    },

    setWhatWentWhereDraftValue(key = '', value = '') {
      if (!key) return;
      this.whatWentWhereDraftByKey = { ...(this.whatWentWhereDraftByKey || {}), [key]: value };
    },

    setWhatWentWhereReferenceSelected(key = '', selected = true) {
      if (!key) return;
      this.whatWentWhereSelectedReferenceKeys = { ...(this.whatWentWhereSelectedReferenceKeys || {}), [key]: !!selected };
    },

    toggleAllVisibleWhatWentWhereReferences(selected = true) {
      const next = { ...(this.whatWentWhereSelectedReferenceKeys || {}) };
      this.whatWentWhereVisibleReferenceRows.forEach((row) => {
        next[row.key] = !!selected;
      });
      this.whatWentWhereSelectedReferenceKeys = next;
    },

    toggleAllVisibleWwwUsdReferences(selected = true) {
      const next = { ...(this.whatWentWhereSelectedReferenceKeys || {}) };
      this.wwwUsdReferenceVisibleRows.forEach((row) => {
        next[row.key] = !!selected;
      });
      this.whatWentWhereSelectedReferenceKeys = next;
    },

    setColumnHReferenceStatus(message = '') {
      this.whatWentWhereStatus = message;
      if (this.showWwwUsdReferenceModal) this.wwwUsdReferenceStatus = message;
    },

    async estimateWhatWentWhereUsdReferences(options = {}) {
      if (this.whatWentWhereEstimateRunning) return false;
      const state = options.referenceState || (this.showWwwUsdReferenceModal && !this.showWhatWentWhereModal
        ? this.getWwwUsdReferenceReviewState()
        : this.getWhatWentWhereState());
      const selectedOnly = options.selectedOnly !== false;
      const candidates = (state.referenceRows || []).filter((row) => {
        if (selectedOnly && !this.whatWentWhereSelectedReferenceKeys[row.key]) return false;
        if (!selectedOnly && row.isUsable) return false;
        return !!row.referenceToken && Number.isFinite(row.referenceAmount) && row.dateIsValid;
      });
      if (!candidates.length) {
        this.setColumnHReferenceStatus(selectedOnly ? 'No selected reference rows can be estimated.' : 'No missing reference rows can be estimated with the local BTC/ETH/stablecoin reference.');
        this.showCaseToast(this.whatWentWhereStatus);
        return false;
      }
      this.whatWentWhereEstimateRunning = true;
      this.whatWentWhereEstimateProgress = { done: 0, total: candidates.length };
      const drafts = { ...(this.whatWentWhereDraftByKey || {}) };
      const estimates = { ...(this.whatWentWhereEstimateByKey || {}) };
      try {
        for (const row of candidates) {
          const estimate = estimateCaseBuddyHistoricalUsdValue({ symbol: row.referenceToken, timestampUtc: row.utcDate, amount: row.referenceAmount });
          estimates[row.key] = estimate;
          if (estimate?.ok && Number.isFinite(estimate.estimatedUsdValue)) {
            drafts[row.key] = formatCaseWwwUsdCell(estimate.estimatedUsdValue);
          }
          this.whatWentWhereEstimateProgress = { ...this.whatWentWhereEstimateProgress, done: this.whatWentWhereEstimateProgress.done + 1 };
          if (candidates.length > 20 && this.whatWentWhereEstimateProgress.done % 25 === 0) await sleep(0);
        }
        this.whatWentWhereDraftByKey = drafts;
        this.whatWentWhereEstimateByKey = estimates;
        const successCount = candidates.filter(row => estimates[row.key]?.ok).length;
        this.setColumnHReferenceStatus(`Estimated ${successCount}/${candidates.length} Column H value${candidates.length === 1 ? '' : 's'} from the local rough reference.`);
        this.showCaseToast(this.whatWentWhereStatus);
        return true;
      } finally {
        this.whatWentWhereEstimateRunning = false;
      }
    },

    applySelectedWhatWentWhereUsdReferences(options = {}) {
      const state = options.referenceState || (this.showWwwUsdReferenceModal && !this.showWhatWentWhereModal
        ? this.getWwwUsdReferenceReviewState()
        : this.getWhatWentWhereState());
      const selected = (state.referenceRows || []).filter(row => this.whatWentWhereSelectedReferenceKeys[row.key]);
      const updates = selected.map((row) => {
        const draft = normalizeCell(this.whatWentWhereDraftByKey?.[row.key] ?? row.currentColumnH);
        const value = parseCaseWwwUsdReference(draft);
        return { row, draft, value };
      }).filter(item => Number.isFinite(item.value));
      if (!updates.length) {
        this.setColumnHReferenceStatus('No selected rows have a valid draft Column H value to apply.');
        this.showCaseToast(this.whatWentWhereStatus);
        return false;
      }
      const confirmed = options.confirm === false || window.confirm(`Apply ${updates.length} Column H USD reference value${updates.length === 1 ? '' : 's'} to Main Data? Use local estimates only as rough placeholders unless you have independently verified them.`);
      if (!confirmed) return false;
      const mainSheet = this.findLogicalSheetMeta('main');
      if (!mainSheet?.id) {
        this.setColumnHReferenceStatus('Main Data sheet was not found.');
        return false;
      }
      const sheetState = this.getSheetStateById(mainSheet.id);
      const data = normalizeCaseMatrix(sheetState.data);
      let applied = 0;
      updates.forEach(({ row, value }) => {
        if (!Array.isArray(data[row.rowIndex])) return;
        data[row.rowIndex][7] = formatCaseWwwUsdCell(value);
        applied += 1;
      });
      if (!applied) {
        this.setColumnHReferenceStatus('No Column H values were applied.');
        return false;
      }
      this.writeSheetStateById(mainSheet.id, { ...sheetState, data });
      this.markCaseDataMutated('what_went_where_usd_references_applied', { applied });
      const refreshed = this.refreshWwwUsdReferenceReview({ silent: true });
      if (this.showWhatWentWhereModal || this.caseSupportState.checkResultsById?.what_went_where) {
        this.refreshWhatWentWhereReview({ silent: true });
      }
      this.setColumnHReferenceStatus(`Applied ${applied} Column H USD reference value${applied === 1 ? '' : 's'}.`);
      this.showCaseToast(this.whatWentWhereStatus);
      this.loadOutputRows([
        ['Case Buddy Column H USD Reference Audit', '', '', '', '', '', '', '', '', 'Audit only. Main Data Column H was updated directly for selected top-level main transactions.'],
        ['Applied', applied, 'Remaining Missing', refreshed.missingUsdReferenceCount || 0, 'Basis', refreshed.currentBasisHash || ''],
        ['Row', 'Path Label', 'Transaction Hash', 'Column H USD Value', 'Source'],
        ...updates.map(({ row, value }) => [row.rowNumber, row.pathLabel, row.transactionHash, formatCaseWwwUsdCell(value), this.whatWentWhereEstimateByKey?.[row.key]?.resolutionMode || 'manual/draft'])
      ], {
        title: 'Column H USD reference audit',
        detail: `${applied} Column H value${applied === 1 ? '' : 's'} applied to Main Data.`,
        sourceActionId: this.showWwwUsdReferenceModal ? 'review_www_usd_references' : 'review_what_went_where',
        target: 'audit-only'
      });
      return true;
    },

    markWhatWentWhereDeliverableBuiltRecord(deliverableId, options = {}) {
      const definition = deliverableId === 'what_went_where_valuation_audit' ? this.getWhatWentWhereAuditDefinition() : this.getWhatWentWhereDefinition();
      if (!definition) return;
      this.markDeliverableBuilt(definition);
      const state = this.getWhatWentWhereState();
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        [definition.id]: {
          ...(this.caseSupportState.deliverableStateById?.[definition.id] || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: new Date().toISOString(),
          basisHash: state.currentBasisHash || '',
          reviewedAtUtc: options.keepReview ? (this.caseSupportState.deliverableStateById?.[definition.id]?.reviewedAtUtc || '') : ''
        }
      };
      this.updateWhatWentWhereCheckResult();
    },

    stageWhatWentWherePreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging What Went Where…',
          message: 'Building preview rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'What Went Where staged.'
        }, () => this.stageWhatWentWherePreview({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getWhatWentWhereState();
      if (!state.canBuild) {
        this.whatWentWhereStatus = state.summary || 'What Went Where is not ready to build.';
        if (!options.silent) this.showCaseToast(this.whatWentWhereStatus);
        return false;
      }
      this.loadOutputRows(buildCaseWhatWentWhereDeliverableRows(state), {
        title: 'What Went Where preview',
        detail: 'Tool 83-style What Went Where output staged in Output for review. Main Data is not modified.',
        sourceActionId: 'stage_what_went_where',
        sourceDeliverableId: 'what_went_where',
        target: 'deliverable-preview'
      });
      this.caseSupportState.workflowState.whatWentWhereReview = {
        ...(this.caseSupportState.workflowState.whatWentWhereReview || {}),
        builtBasisHash: state.currentBasisHash,
        builtAtUtc: new Date().toISOString(),
        builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        generatedPathEndingCount: state.generatedPathEndingCount || 0,
        valuationUnresolvedCount: state.valuationUnresolvedCount || 0
      };
      this.markWhatWentWhereDeliverableBuiltRecord('what_went_where');
      this.whatWentWhereStatus = `What Went Where staged in Output with ${state.outputRows.length || 0} row${(state.outputRows.length || 0) === 1 ? '' : 's'}.`;
      if (!options.silent) this.showCaseToast(this.whatWentWhereStatus);
      this.scrollToSection('case-output');
      return true;
    },

    stageWhatWentWhereValuationAudit(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging What Went Where audit…',
          message: 'Building preview rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'What Went Where audit staged.'
        }, () => this.stageWhatWentWhereValuationAudit({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getWhatWentWhereState();
      if (!state.canBuild) {
        this.whatWentWhereStatus = state.summary || 'What Went Where valuation audit is not ready to build.';
        if (!options.silent) this.showCaseToast(this.whatWentWhereStatus);
        return false;
      }
      this.loadOutputRows(buildCaseWhatWentWhereValuationAuditRows(state), {
        title: 'What Went Where Valuation Audit preview',
        detail: 'Valuation audit staged in Output for review. Main Data is not modified.',
        sourceActionId: 'stage_what_went_where_audit',
        sourceDeliverableId: 'what_went_where_valuation_audit',
        target: 'deliverable-preview'
      });
      this.caseSupportState.workflowState.whatWentWhereReview = {
        ...(this.caseSupportState.workflowState.whatWentWhereReview || {}),
        builtBasisHash: state.currentBasisHash,
        builtAtUtc: new Date().toISOString(),
        builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        generatedPathEndingCount: state.generatedPathEndingCount || 0,
        valuationUnresolvedCount: state.valuationUnresolvedCount || 0
      };
      this.markWhatWentWhereDeliverableBuiltRecord('what_went_where_valuation_audit');
      this.whatWentWhereStatus = 'What Went Where valuation audit staged in Output.';
      if (!options.silent) this.showCaseToast(this.whatWentWhereStatus);
      this.scrollToSection('case-output');
      return true;
    },

    downloadWhatWentWhereTsv(options = {}) {
      const state = this.getWhatWentWhereState();
      if (!state.canBuild) {
        this.whatWentWhereStatus = state.summary || 'What Went Where is not ready to download.';
        if (!options.silent) this.showCaseToast(this.whatWentWhereStatus);
        return false;
      }
      downloadTextFile('what_went_where.tsv', buildTsv(buildCaseWhatWentWhereDeliverableRows(state)), 'text/tab-separated-values;charset=utf-8;');
      this.markWhatWentWhereDeliverableBuiltRecord('what_went_where');
      this.whatWentWhereStatus = 'What Went Where TSV downloaded.';
      if (!options.silent) this.showCaseToast(this.whatWentWhereStatus);
      return true;
    },

    downloadWhatWentWhereValuationAuditTsv(options = {}) {
      const state = this.getWhatWentWhereState();
      if (!state.canBuild) {
        this.whatWentWhereStatus = state.summary || 'What Went Where valuation audit is not ready to download.';
        if (!options.silent) this.showCaseToast(this.whatWentWhereStatus);
        return false;
      }
      downloadTextFile('what_went_where_valuation_audit.tsv', buildTsv(buildCaseWhatWentWhereValuationAuditRows(state)), 'text/tab-separated-values;charset=utf-8;');
      this.markWhatWentWhereDeliverableBuiltRecord('what_went_where_valuation_audit');
      this.whatWentWhereStatus = 'What Went Where valuation audit downloaded.';
      if (!options.silent) this.showCaseToast(this.whatWentWhereStatus);
      return true;
    },

    markWhatWentWhereReviewComplete(options = {}) {
      const state = this.getWhatWentWhereState();
      if (!state.canBuild) {
        this.whatWentWhereStatus = state.summary || 'Resolve Column H references before marking What Went Where reviewed.';
        this.showCaseToast(this.whatWentWhereStatus);
        return false;
      }
      const mainDefinition = this.getWhatWentWhereDefinition();
      const auditDefinition = this.getWhatWentWhereAuditDefinition();
      const mainView = mainDefinition ? this.buildDeliverableView(mainDefinition) : null;
      const auditView = auditDefinition ? this.buildDeliverableView(auditDefinition) : null;
      if (mainView?.status !== 'current') this.stageWhatWentWherePreview({ silent: true });
      if (auditView?.status !== 'current') this.stageWhatWentWhereValuationAudit({ silent: true });
      const reviewedAtUtc = new Date().toISOString();
      this.caseSupportState.workflowState.whatWentWhereReview = {
        ...(this.caseSupportState.workflowState.whatWentWhereReview || {}),
        basisHash: state.currentBasisHash,
        reviewedAtUtc,
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        generatedPathEndingCount: state.generatedPathEndingCount || 0,
        valuationResolvedCount: state.valuationResolvedCount || 0,
        valuationUnresolvedCount: state.valuationUnresolvedCount || 0
      };
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        what_went_where: {
          ...(this.caseSupportState.deliverableStateById?.what_went_where || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: this.caseSupportState.deliverableStateById?.what_went_where?.builtAtUtc || reviewedAtUtc,
          basisHash: state.currentBasisHash || '',
          reviewedAtUtc
        },
        what_went_where_valuation_audit: {
          ...(this.caseSupportState.deliverableStateById?.what_went_where_valuation_audit || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: this.caseSupportState.deliverableStateById?.what_went_where_valuation_audit?.builtAtUtc || reviewedAtUtc,
          basisHash: state.currentBasisHash || '',
          reviewedAtUtc
        }
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_WHAT_WENT_WHERE_REVIEWED, options.source || 'review_what_went_where', { silent: true });
      this.updateWhatWentWhereCheckResult();
      this.loadOutputRows(buildCaseWhatWentWhereAuditRows(state, 'What Went Where reviewed'), {
        title: 'What Went Where review audit',
        detail: 'What Went Where and its valuation audit were marked reviewed for the current case data.',
        sourceActionId: 'review_what_went_where',
        target: 'audit-only'
      });
      this.whatWentWhereStatus = `What Went Where marked reviewed with ${state.generatedPathEndingCount || 0} generated path-ending row${(state.generatedPathEndingCount || 0) === 1 ? '' : 's'}.`;
      this.showCaseToast(this.whatWentWhereStatus);
      return true;
    },

    toggleWhatWentWhereReviewed(item = {}) {
      const id = item?.id === 'what_went_where_valuation_audit' ? 'what_went_where_valuation_audit' : 'what_went_where';
      const existing = this.caseSupportState.deliverableStateById?.[id] || {};
      const state = this.getWhatWentWhereState();
      const builtIsCurrent = !!existing.builtAtUtc && Number(existing.builtRevision) === (Number(this.caseSupportState.caseDataRevision) || 0) && existing.basisHash === state.currentBasisHash;
      if (!builtIsCurrent) {
        this.showCaseToast('Stage or download the current What Went Where output before marking it reviewed.');
        return false;
      }
      if (existing.reviewedAtUtc) {
        this.caseSupportState.deliverableStateById = {
          ...(this.caseSupportState.deliverableStateById || {}),
          [id]: { ...existing, reviewedAtUtc: '' }
        };
        this.updateWhatWentWhereCheckResult();
        this.showCaseToast('What Went Where review mark cleared.');
        return true;
      }
      return this.markWhatWentWhereReviewComplete({ source: 'deliverables-panel' });
    },

    getCaseConclusionState(options = {}) {
      const whatState = options.whatWentWhereState || this.getWhatWentWhereState(options);
      const buildOptions = {
        caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        ...(options || {}),
        whatWentWhereState: undefined
      };
      const cacheKey = this.getCaseComputationCacheKey?.('case-conclusion-state', {
        ...buildOptions,
        whatWentWhereBasis: whatState?.currentBasisHash || '',
        whatWentWhereReviewed: !!whatState?.reviewIsCurrent
      }) || '';
      return this.getCachedCaseComputation?.('state', cacheKey, () => buildCaseConclusionState(whatState, this.caseSupportState.workflowState || {}, buildOptions)) ||
        buildCaseConclusionState(whatState, this.caseSupportState.workflowState || {}, buildOptions);
    },

    getCaseConclusionDefinition() {
      return CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'conclusion') || null;
    },

    async openCaseConclusionModal() {
      return this.runCaseModalOperation({
        title: 'Opening case conclusion…',
        message: 'Generating the current conclusion draft.',
        target: 'case-conclusion-modal',
        finishMessage: 'Case conclusion draft ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.showCaseConclusionModal = true;
        await this.yieldToCaseUi();
        this.refreshCaseConclusionReview({ silent: true });
        const state = this.getCaseConclusionState();
        this.caseConclusionDraft = state.selectedText || state.generatedText || this.caseConclusionDraft || '';
        return true;
      });
    },

    cancelCaseConclusionModal() {
      this.showCaseConclusionModal = false;
    },

    refreshCaseConclusionReview(options = {}) {
      const state = this.getCaseConclusionState();
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        case_conclusion: this.buildCaseConclusionCheckResult(state)
      };
      this.persistCaseSupportState();
      this.caseConclusionStatus = state.summary || 'Conclusion refreshed.';
      if (!options.silent) this.showCaseToast(this.caseConclusionStatus);
      return state;
    },

    resetCaseConclusionDraftToGenerated() {
      const state = this.getCaseConclusionState();
      this.caseConclusionDraft = state.generatedText || '';
      this.caseConclusionStatus = 'Generated conclusion text restored.';
    },

    buildCaseConclusionCheckResult(state = null) {
      const reviewState = state || this.getCaseConclusionState();
      const definition = this.getCaseConclusionDefinition();
      const view = definition ? this.buildDeliverableView(definition) : null;
      if (!reviewState.whatWentWhereBuildIsCurrent) {
        return this.buildCheckResult('case_conclusion', 'fail', 'Build What Went Where before generating the conclusion.', reviewState);
      }
      if (!reviewState.whatWentWhereReviewIsCurrent) {
        return this.buildCheckResult('case_conclusion', 'fail', 'Review What Went Where before marking the conclusion current.', reviewState);
      }
      if (!reviewState.canBuild) {
        return this.buildCheckResult('case_conclusion', 'warn', reviewState.summary || 'The conclusion is not ready yet.', reviewState);
      }
      if (view?.status === 'stale') {
        return this.buildCheckResult('case_conclusion', 'fail', 'The conclusion is stale because What Went Where or case data changed. Rebuild and review it.', reviewState);
      }
      if (reviewState.reviewIsCurrent && view?.status === 'current' && view?.reviewed) {
        return this.buildCheckResult('case_conclusion', 'pass', 'Case conclusion is current and reviewed.', reviewState);
      }
      return this.buildCheckResult('case_conclusion', 'warn', 'Case conclusion is ready to build/review, but has not been marked current and reviewed yet.', reviewState);
    },

    updateCaseConclusionCheckResult() {
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        case_conclusion: this.buildCaseConclusionCheckResult()
      };
      this.persistCaseSupportState();
    },

    markCaseConclusionDeliverableBuiltRecord(reviewedAtUtc = '') {
      const state = this.getCaseConclusionState();
      const now = new Date().toISOString();
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        conclusion: {
          ...(this.caseSupportState.deliverableStateById?.conclusion || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: now,
          basisHash: state.currentBasisHash || '',
          reviewedAtUtc
        }
      };
      this.persistCaseSupportState();
      return state;
    },

    stageCaseConclusionPreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging case conclusion…',
          message: 'Building preview rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Case conclusion staged.'
        }, () => this.stageCaseConclusionPreview({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getCaseConclusionState();
      if (!state.canBuild) {
        this.caseConclusionStatus = state.summary || 'Conclusion is not ready to stage.';
        this.showCaseToast(this.caseConclusionStatus);
        return false;
      }
      const text = this.caseConclusionDraft || state.selectedText || state.generatedText || '';
      this.loadOutputRows(buildCaseConclusionPreviewRows(state, text), {
        title: 'Case conclusion preview',
        detail: 'Preview/audit only. Conclusion was generated from reviewed What Went Where data.',
        sourceActionId: 'stage_case_conclusion',
        sourceDeliverableId: 'conclusion',
        target: 'output-review'
      });
      this.markCaseConclusionDeliverableBuiltRecord(this.caseSupportState.deliverableStateById?.conclusion?.reviewedAtUtc || '');
      this.updateCaseConclusionCheckResult();
      this.caseConclusionStatus = 'Case conclusion staged in Output.';
      if (!options.silent) this.showCaseToast(this.caseConclusionStatus);
      return true;
    },

    downloadCaseConclusionText(options = {}) {
      const state = this.getCaseConclusionState();
      if (!state.canBuild) {
        this.caseConclusionStatus = state.summary || 'Conclusion is not ready to download.';
        this.showCaseToast(this.caseConclusionStatus);
        return false;
      }
      const text = this.caseConclusionDraft || state.selectedText || state.generatedText || '';
      downloadTextFile('case_conclusion.txt', text, 'text/plain;charset=utf-8;');
      this.markCaseConclusionDeliverableBuiltRecord(this.caseSupportState.deliverableStateById?.conclusion?.reviewedAtUtc || '');
      this.updateCaseConclusionCheckResult();
      if (!options.silent) this.showCaseToast('Case conclusion downloaded.');
      return true;
    },

    async copyCaseConclusionText() {
      const state = this.getCaseConclusionState();
      if (!state.canBuild) {
        this.caseConclusionStatus = state.summary || 'Conclusion is not ready to copy.';
        this.showCaseToast(this.caseConclusionStatus);
        return false;
      }
      const text = this.caseConclusionDraft || state.selectedText || state.generatedText || '';
      const copied = await this.copyTextToClipboard(text, 'Case conclusion copied.');
      this.caseConclusionStatus = copied ? 'Case conclusion copied to clipboard.' : 'Clipboard copy was not available.';
      return copied;
    },

    markCaseConclusionReviewComplete() {
      const state = this.getCaseConclusionState();
      if (!state.canBuild) {
        this.caseConclusionStatus = state.summary || 'Conclusion is not ready to review.';
        this.showCaseToast(this.caseConclusionStatus);
        return false;
      }
      const text = this.caseConclusionDraft || state.selectedText || state.generatedText || '';
      if (!normalizeCell(text)) {
        this.caseConclusionStatus = 'Conclusion text is blank.';
        this.showCaseToast(this.caseConclusionStatus);
        return false;
      }
      const now = new Date().toISOString();
      this.caseSupportState.workflowState.caseConclusionReview = {
        basisHash: state.currentBasisHash,
        reviewedAtUtc: now,
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        selectedText: text,
        rendererVersion: state.rendererVersion
      };
      this.markCaseConclusionDeliverableBuiltRecord(now);
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_CASE_CONCLUSION_REVIEWED, 'review_case_conclusion', { silent: true });
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        case_conclusion: this.buildCaseConclusionCheckResult(this.getCaseConclusionState())
      };
      this.persistCaseSupportState();
      this.loadOutputRows(buildCaseConclusionPreviewRows(this.getCaseConclusionState(), text), {
        title: 'Case conclusion reviewed',
        detail: 'Audit only. Conclusion was marked reviewed for current What Went Where state.',
        sourceActionId: 'review_case_conclusion',
        sourceDeliverableId: 'conclusion',
        target: 'audit-only'
      });
      this.caseConclusionStatus = 'Case conclusion marked reviewed.';
      this.showCaseToast(this.caseConclusionStatus);
      return true;
    },

    toggleCaseConclusionReviewed(item = null) {
      const state = this.getCaseConclusionState();
      if (!state.canBuild) {
        this.showCaseToast(state.summary || 'Conclusion is not ready yet.');
        return false;
      }
      const current = item || this.getCaseConclusionDefinition();
      const existing = current?.id ? this.caseSupportState.deliverableStateById?.[current.id] : this.caseSupportState.deliverableStateById?.conclusion;
      if (existing?.reviewedAtUtc) {
        this.caseSupportState.deliverableStateById = {
          ...(this.caseSupportState.deliverableStateById || {}),
          conclusion: { ...existing, reviewedAtUtc: '' }
        };
        this.caseSupportState.workflowState.caseConclusionReview = {
          ...(this.caseSupportState.workflowState.caseConclusionReview || {}),
          reviewedAtUtc: ''
        };
        this.persistCaseSupportState();
        this.updateCaseConclusionCheckResult();
        return true;
      }
      return this.markCaseConclusionReviewComplete();
    },

    getReportBodyState(options = {}) {
      const buildOptions = {
        caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
        caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        ...(options || {}),
        legendAnalysis: undefined
      };
      const cacheKey = this.getCaseComputationCacheKey?.('report-body-state', {
        ...buildOptions,
        legendBasis: options.legendAnalysis?.currentBasisHash || ''
      }) || '';
      return this.getCachedCaseComputation?.('state', cacheKey, () => {
        const mainData = this.getMatrixForLogicalSheet('main');
        const legendAnalysis = options.legendAnalysis || buildCaseAddressLegendAnalysis(
          mainData,
          this.depositHotWalletEffectiveCurator,
          this.caseSupportState.workflowState || {},
          { labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately }
        );
        return buildCaseReportBodyState(
          mainData,
          this.depositHotWalletEffectiveCurator,
          this.caseSupportState.workflowState || {},
          {
            ...buildOptions,
            legendAnalysis
          }
        );
      }) || (() => {
        const mainData = this.getMatrixForLogicalSheet('main');
        const legendAnalysis = options.legendAnalysis || buildCaseAddressLegendAnalysis(
          mainData,
          this.depositHotWalletEffectiveCurator,
          this.caseSupportState.workflowState || {},
          { labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately }
        );
        return buildCaseReportBodyState(mainData, this.depositHotWalletEffectiveCurator, this.caseSupportState.workflowState || {}, { ...buildOptions, legendAnalysis });
      })();
    },

    getReportBodyDefinition(id = 'report_body') {
      return CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === id) || null;
    },

    getReportBodyVariantId(options = {}) {
      return options.variantId || this.reportBodyActiveVariant || CASE_REPORT_BODY_VARIANT_STANDARD;
    },

    getReportBodyVariantPayload(state = null, variantId = CASE_REPORT_BODY_VARIANT_STANDARD) {
      const reviewState = state || this.getReportBodyState();
      const isCustom = variantId === CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING;
      return {
        id: isCustom ? CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING : CASE_REPORT_BODY_VARIANT_STANDARD,
        deliverableId: isCustom ? 'report_body_spacing_tags' : 'report_body',
        displayName: isCustom ? 'Tool38H custom-spacing report body' : 'Tool38G standard report body',
        plainText: isCustom ? reviewState.customSpacingText : reviewState.generatedText,
        htmlFragment: isCustom ? reviewState.customSpacingHtmlFragment : reviewState.standardHtmlFragment,
        htmlDocument: isCustom ? reviewState.customSpacingHtmlDocument : reviewState.standardHtmlDocument
      };
    },

    async openReportBodyModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening report body…',
        message: 'Rendering the current report body preview.',
        target: 'report-body-modal',
        finishMessage: 'Report body preview ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.reportBodySearch = '';
        this.reportBodyActiveVariant = options.variantId || CASE_REPORT_BODY_VARIANT_STANDARD;
        this.showReportBodyModal = true;
        await this.yieldToCaseUi();
        this.refreshReportBodyReview({ silent: true });
        return true;
      });
    },

    cancelReportBodyModal() {
      this.showReportBodyModal = false;
    },

    refreshReportBodyReview(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const state = this.getReportBodyState();
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        report_body: this.buildReportBodyCheckResult(state)
      };
      this.persistCaseSupportState();
      this.reportBodyStatus = state.summary || 'Report body preview refreshed.';
      if (!options.silent) this.showCaseToast(this.reportBodyStatus);
      return state;
    },

    buildReportBodyCheckResult(state = null) {
      const reviewState = state || this.getReportBodyState();
      const standardDefinition = this.getReportBodyDefinition('report_body');
      const customDefinition = this.getReportBodyDefinition('report_body_spacing_tags');
      const standardView = standardDefinition ? this.buildDeliverableView(standardDefinition) : null;
      const customView = customDefinition ? this.buildDeliverableView(customDefinition) : null;
      if (!reviewState.legendBuildIsCurrent) {
        return this.buildCheckResult('report_body', 'fail', 'Report body needs a current address legend build before it can be reviewed.', reviewState);
      }
      if (!reviewState.legendReviewIsCurrent) {
        return this.buildCheckResult('report_body', 'fail', 'Report body needs the address legend to be reviewed before it can be reviewed.', reviewState);
      }
      if ((reviewState.entryCount || 0) === 0) {
        return this.buildCheckResult('report_body', 'fail', 'No Main Data transaction entries were found for the report body.', reviewState);
      }
      const standardCurrent = standardView?.status === 'current';
      const customCurrent = customView?.status === 'current';
      if (standardCurrent && customCurrent) {
        if (!standardView?.reviewed || !customView?.reviewed) {
          return this.buildCheckResult('report_body', 'warn', 'Report body variants are built current, but both variants have not been marked reviewed yet.', reviewState);
        }
        return this.buildCheckResult('report_body', 'pass', `Report body is current and reviewed with ${reviewState.entryCount || 0} transaction entr${(reviewState.entryCount || 0) === 1 ? 'y' : 'ies'}.`, reviewState);
      }
      if (standardView?.status === 'stale' || customView?.status === 'stale') {
        return this.buildCheckResult('report_body', 'fail', 'Report body is stale because Main Data or the address legend changed. Rebuild it.', reviewState);
      }
      return this.buildCheckResult('report_body', 'warn', reviewState.summary || 'Report body is ready to build and review.', reviewState);
    },

    updateReportBodyCheckResult() {
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        report_body: this.buildReportBodyCheckResult()
      };
      this.persistCaseSupportState();
    },

    markReportBodyDeliverableBuiltRecord(deliverableId = 'report_body', options = {}) {
      const state = this.getReportBodyState();
      const definition = this.getReportBodyDefinition(deliverableId);
      if (!definition) return;
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        [definition.id]: {
          ...(this.caseSupportState.deliverableStateById?.[definition.id] || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: new Date().toISOString(),
          basisHash: state.currentBasisHash || '',
          reviewedAtUtc: options.keepReview ? (this.caseSupportState.deliverableStateById?.[definition.id]?.reviewedAtUtc || '') : ''
        }
      };
      this.updateReportBodyCheckResult();
    },

    stageReportBodyPreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging report body…',
          message: 'Building preview rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Report body staged.'
        }, () => this.stageReportBodyPreview({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getReportBodyState();
      if (!state.canBuild) {
        this.reportBodyStatus = state.summary || 'Report body is not ready to build.';
        if (!options.silent) this.showCaseToast(this.reportBodyStatus);
        return false;
      }
      const variantId = this.getReportBodyVariantId(options);
      const payload = this.getReportBodyVariantPayload(state, variantId);
      this.loadOutputRows(buildCaseReportBodyPreviewRows(state, variantId), {
        title: `${payload.displayName} preview`,
        detail: 'Report body staged in Output for review. Main Data is not modified.',
        sourceActionId: 'stage_report_body',
        sourceDeliverableId: payload.deliverableId,
        target: 'deliverable-preview'
      });
      this.caseSupportState.workflowState.reportBodyReview = {
        ...(this.caseSupportState.workflowState.reportBodyReview || {}),
        builtBasisHash: state.currentBasisHash,
        builtAtUtc: new Date().toISOString(),
        builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        entryCount: state.entryCount || 0,
        topLevelPathCount: state.topLevelPathCount || 0,
        lastVariantId: variantId
      };
      this.markReportBodyDeliverableBuiltRecord(payload.deliverableId);
      this.reportBodyStatus = `${payload.displayName} staged in Output with ${state.entryCount || 0} transaction entr${(state.entryCount || 0) === 1 ? 'y' : 'ies'}.`;
      if (!options.silent) this.showCaseToast(this.reportBodyStatus);
      this.scrollToSection('case-output');
      return true;
    },

    downloadReportBodyText(options = {}) {
      const state = this.getReportBodyState();
      if (!state.canBuild) {
        this.reportBodyStatus = state.summary || 'Report body is not ready to download.';
        if (!options.silent) this.showCaseToast(this.reportBodyStatus);
        return false;
      }
      const variantId = this.getReportBodyVariantId(options);
      const payload = this.getReportBodyVariantPayload(state, variantId);
      const filename = payload.deliverableId === 'report_body_spacing_tags' ? 'report_body_custom_spacing.txt' : 'report_body.txt';
      downloadTextFile(filename, payload.plainText || '', 'text/plain;charset=utf-8;');
      this.markReportBodyDeliverableBuiltRecord(payload.deliverableId);
      this.reportBodyStatus = `${payload.displayName} TXT downloaded.`;
      if (!options.silent) this.showCaseToast(this.reportBodyStatus);
      return true;
    },

    downloadReportBodyHtml(options = {}) {
      const state = this.getReportBodyState();
      if (!state.canBuild) {
        this.reportBodyStatus = state.summary || 'Report body HTML is not ready to download.';
        if (!options.silent) this.showCaseToast(this.reportBodyStatus);
        return false;
      }
      const variantId = this.getReportBodyVariantId(options);
      const payload = this.getReportBodyVariantPayload(state, variantId);
      const filename = payload.deliverableId === 'report_body_spacing_tags' ? 'report_body_custom_spacing.html' : 'report_body.html';
      downloadTextFile(filename, payload.htmlDocument || payload.htmlFragment || '', 'text/html;charset=utf-8;');
      this.markReportBodyDeliverableBuiltRecord(payload.deliverableId);
      this.reportBodyStatus = `${payload.displayName} HTML downloaded.`;
      if (!options.silent) this.showCaseToast(this.reportBodyStatus);
      return true;
    },

    async copyReportBodyText(options = {}) {
      const state = this.getReportBodyState();
      if (!state.canBuild) {
        this.reportBodyStatus = state.summary || 'Report body is not ready to copy.';
        this.showCaseToast(this.reportBodyStatus);
        return false;
      }
      const variantId = this.getReportBodyVariantId(options);
      const payload = this.getReportBodyVariantPayload(state, variantId);
      const copied = await this.copyTextToClipboard(payload.plainText || '', `${payload.displayName} text copied.`);
      if (copied) this.markReportBodyDeliverableBuiltRecord(payload.deliverableId, { keepReview: true });
      this.reportBodyStatus = copied ? `${payload.displayName} text copied.` : 'Could not copy report body text in this browser.';
      return copied;
    },

    async copyReportBodyRich(options = {}) {
      const state = this.getReportBodyState();
      if (!state.canBuild) {
        this.reportBodyStatus = state.summary || 'Report body is not ready to copy.';
        this.showCaseToast(this.reportBodyStatus);
        return false;
      }
      const variantId = this.getReportBodyVariantId(options);
      const payload = this.getReportBodyVariantPayload(state, variantId);
      try {
        if (typeof navigator !== 'undefined' && navigator?.clipboard?.write && typeof ClipboardItem !== 'undefined') {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([payload.htmlDocument || payload.htmlFragment || ''], { type: 'text/html' }),
              'text/plain': new Blob([payload.plainText || ''], { type: 'text/plain' })
            })
          ]);
          this.markReportBodyDeliverableBuiltRecord(payload.deliverableId, { keepReview: true });
          this.reportBodyStatus = `${payload.displayName} rich output copied to clipboard.`;
          this.showCaseToast(this.reportBodyStatus);
          return true;
        }
      } catch {
        // Fall back to text copy below.
      }
      const copied = await this.copyTextToClipboard(payload.plainText || '', `${payload.displayName} text copied because rich clipboard was unavailable.`);
      if (copied) this.markReportBodyDeliverableBuiltRecord(payload.deliverableId, { keepReview: true });
      this.reportBodyStatus = copied ? 'Rich clipboard unavailable; copied text-only report body instead.' : 'Could not copy report body in this browser.';
      return copied;
    },

    markReportBodyReviewComplete(options = {}) {
      const state = this.getReportBodyState();
      if (!state.canBuild) {
        this.reportBodyStatus = state.summary || 'Resolve prerequisites before marking report body reviewed.';
        this.showCaseToast(this.reportBodyStatus);
        return false;
      }
      const standardDefinition = this.getReportBodyDefinition('report_body');
      const customDefinition = this.getReportBodyDefinition('report_body_spacing_tags');
      const standardView = standardDefinition ? this.buildDeliverableView(standardDefinition) : null;
      const customView = customDefinition ? this.buildDeliverableView(customDefinition) : null;
      if (standardView?.status !== 'current') this.stageReportBodyPreview({ variantId: CASE_REPORT_BODY_VARIANT_STANDARD, silent: true });
      if (customView?.status !== 'current') this.stageReportBodyPreview({ variantId: CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING, silent: true });
      const reviewedAtUtc = new Date().toISOString();
      this.caseSupportState.workflowState.reportBodyReview = {
        ...(this.caseSupportState.workflowState.reportBodyReview || {}),
        basisHash: state.currentBasisHash,
        reviewedAtUtc,
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        entryCount: state.entryCount || 0,
        topLevelPathCount: state.topLevelPathCount || 0,
        rendererVersion: state.rendererVersion || CASE_REPORT_BODY_RENDERER_VERSION
      };
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        report_body: {
          ...(this.caseSupportState.deliverableStateById?.report_body || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: this.caseSupportState.deliverableStateById?.report_body?.builtAtUtc || reviewedAtUtc,
          basisHash: state.currentBasisHash || '',
          reviewedAtUtc
        },
        report_body_spacing_tags: {
          ...(this.caseSupportState.deliverableStateById?.report_body_spacing_tags || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: this.caseSupportState.deliverableStateById?.report_body_spacing_tags?.builtAtUtc || reviewedAtUtc,
          basisHash: state.currentBasisHash || '',
          reviewedAtUtc
        }
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_REPORT_BODY_REVIEWED, options.source || 'review_report_body', { silent: true });
      this.updateReportBodyCheckResult();
      this.loadOutputRows(buildCaseReportBodyAuditRows(state, 'Report body reviewed'), {
        title: 'Report body review audit',
        detail: 'Report body variants were marked reviewed for the current case data and address legend.',
        sourceActionId: 'review_report_body',
        target: 'audit-only'
      });
      this.reportBodyStatus = `Report body marked reviewed with ${state.entryCount || 0} rendered transaction entr${(state.entryCount || 0) === 1 ? 'y' : 'ies'}.`;
      this.showCaseToast(this.reportBodyStatus);
      return true;
    },

    toggleReportBodyReviewed(item = {}) {
      const id = item?.id === 'report_body_spacing_tags' || item?.generator === 'case_report_body_spacing_tags' ? 'report_body_spacing_tags' : 'report_body';
      const existing = this.caseSupportState.deliverableStateById?.[id] || {};
      const state = this.getReportBodyState();
      const builtIsCurrent = !!existing.builtAtUtc && Number(existing.builtRevision) === (Number(this.caseSupportState.caseDataRevision) || 0) && existing.basisHash === state.currentBasisHash;
      if (!builtIsCurrent) {
        this.showCaseToast('Stage, copy, or download the current report body before marking it reviewed.');
        return false;
      }
      if (existing.reviewedAtUtc) {
        this.caseSupportState.deliverableStateById = {
          ...(this.caseSupportState.deliverableStateById || {}),
          [id]: { ...existing, reviewedAtUtc: '' }
        };
        this.updateReportBodyCheckResult();
        this.showCaseToast('Report body review mark cleared.');
        return true;
      }
      return this.markReportBodyReviewComplete({ source: 'deliverables-panel' });
    },

    getCaseFlowVisualOptions() {
      return {
        ...CASE_FLOW_VISUALS_DEFAULT_OPTIONS,
        ...(this.caseFlowVisualsOptions || {})
      };
    },

    getCaseInvestigationVisualOptions() {
      return {
        ...CASE_INVESTIGATION_VISUALS_DEFAULT_OPTIONS,
        layoutMode: 'upwardBranches',
        mergeAddressMode: 'none',
        mergeTransactionNodes: false,
        wrapLinearTrunks: false,
        trunkWrapMinLength: 5,
        trunkWrapFirstRowLength: 5,
        trunkWrapOtherRowLength: 4,
        trunkWrapRowVerticalMultiplier: 1.35,
        highlightSwaps: false,
        highlightBridges: false,
        cleanupOverlaps: false,
        labelAddresses: true,
        ...(this.caseInvestigationVisualizerOptions || this.caseInvestigationVisualOptions || {})
      };
    },

    resetCaseFlowVisualOptionsForMode(mode = 'report') {
      const normalizedMode = mode === 'investigation' ? 'investigation' : 'report';
      this.caseFlowVisualsOptions = normalizedMode === 'investigation'
        ? { ...this.getCaseInvestigationVisualOptions() }
        : { ...CASE_FLOW_VISUALS_DEFAULT_OPTIONS };
      return this.caseFlowVisualsOptions;
    },

    caseFlowVisualsIsInvestigationSession() {
      return this.caseFlowVisualsMode === 'investigation' || this.caseFlowVisualsIntent === 'investigation';
    },

    getActiveCaseFlowVisualsState(options = {}) {
      return this.caseFlowVisualsIsInvestigationSession()
        ? this.getCaseInvestigationFlowVisualsState(options)
        : this.getCaseFlowVisualsState(options);
    },

    getCaseInvestigationFlowVisualsState(options = {}) {
      const mainData = this.getMatrixForLogicalSheet('main');
      const rawData = this.getMatrixForLogicalSheet('raw');
      const workflowState = this.caseSupportState.workflowState || {};
      const visualOptions = this.getCaseInvestigationVisualOptions();
      const buildOptions = {
        caseDisplayName: this.caseDisplayName,
        caseDisplayId: this.caseDisplayId,
        caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        visualOptions,
        investigationMode: true,
        projectFileId: this.currentProjectFileId || '',
        ...(options || {})
      };
      const cacheKey = this.getCaseComputationCacheKey?.('case-investigation-flow-visuals-state', buildOptions) || '';
      const producer = () => {
        const baseState = buildCaseFlowVisualsState(mainData, workflowState, buildOptions);
        const sapling = buildSaplingPreparationAnalysis(mainData, rawData, workflowState);
        const preparedRows = (sapling.rows || []).filter(row => !!row.processed);
        const preparedLabels = new Set(preparedRows.map(row => normalizeCell(row.pathLabel).toLowerCase()).filter(Boolean));
        const preparedHashes = new Set(preparedRows.map(row => normalizeCell(row.hash).toLowerCase()).filter(Boolean));
        const preparedStartRows = new Set(preparedRows.map(row => Number(row.blockStartRowIndex)).filter(Number.isInteger));
        const flows = (baseState.flows || []).filter((flow) => {
          const label = normalizeCell(flow.mainLabel).toLowerCase();
          const hash = normalizeCell(flow.mainHash).toLowerCase();
          if (label && preparedLabels.has(label)) return true;
          if (hash && preparedHashes.has(hash)) return true;
          const startRow = Number(flow.mainTxn?.rowIndex ?? flow.mainTxn?.startRow ?? -1);
          return preparedStartRows.has(startRow);
        });
        const visibleEntryIds = new Set(flows.flatMap(flow => (flow.entries || []).map(entry => entry.entryId)));
        const entries = (baseState.entries || []).filter(entry => visibleEntryIds.has(entry.entryId));
        const flowCount = flows.length;
        return {
          ...baseState,
          entries,
          flows,
          flowCount,
          entryCount: entries.length,
          saplingPreparedCount: preparedRows.length,
          preparedSaplingCount: preparedRows.length,
          qualifyingFlowCount: flowCount,
          saplingTotalCount: sapling.total || 0,
          canBuild: flowCount > 0,
          reviewIsCurrent: false,
          signalCurrent: false,
          manualCurrent: false,
          completedAtUtc: '',
          source: 'investigation-visualizer',
          summary: flowCount > 0
            ? `${flowCount} prepared sapling flow${flowCount === 1 ? '' : 's'} can be inspected in the investigation visualizer. Address labels are applied only when already reviewed; missing labels are fine.`
            : preparedRows.length > 0
              ? 'Prepared saplings were detected, but no visualizable path-labeled flows were found. Check Column A path labels for the prepared main transactions.'
              : 'Prepare at least one sapling from Raw Data into Main Data before opening the investigation visualizer.'
        };
      };
      return this.getCachedCaseComputation?.('state', cacheKey, producer) || producer();
    },

    getCaseFlowVisualsState(options = {}) {
      const buildOptions = {
        caseDisplayName: this.caseDisplayName,
        caseDisplayId: this.caseDisplayId,
        caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        visualOptions: this.getCaseFlowVisualOptions(),
        ...(options || {})
      };
      const cacheKey = this.getCaseComputationCacheKey?.('case-flow-visuals-state', buildOptions) || '';
      return this.getCachedCaseComputation?.('state', cacheKey, () => buildCaseFlowVisualsState(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        buildOptions
      )) || buildCaseFlowVisualsState(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        buildOptions
      );
    },

    getCaseFlowVisualsPlaceholderState(options = {}) {
      return this.getCaseFlowVisualsState(options);
    },

    buildCaseFlowVisualsCheckResult(state = null) {
      const reviewState = state || this.getCaseFlowVisualsState();
      if (reviewState.reviewIsCurrent) {
        return this.buildCheckResult('case_flow_visuals', 'pass', reviewState.summary || 'Case flow visuals are marked complete.', reviewState);
      }
      if (reviewState.canBuild) {
        return this.buildCheckResult(
          'case_flow_visuals',
          'warn',
          `${reviewState.flowCount || 0} case flow visual${(reviewState.flowCount || 0) === 1 ? '' : 's'} can be previewed/exported. Open the visual builder, review the flows, and download the image package.`,
          reviewState
        );
      }
      return this.buildCheckResult(
        'case_flow_visuals',
        'fail',
        'No case flow visuals can be built yet because Main Data does not contain transaction entries.',
        reviewState
      );
    },

    updateCaseFlowVisualsCheckResult(state = null) {
      const reviewState = state || this.getCaseFlowVisualsState();
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        case_flow_visuals: this.buildCaseFlowVisualsCheckResult(reviewState)
      };
      this.persistCaseSupportState();
    },

    markCaseFlowVisualsBuiltRecord(options = {}) {
      const state = options.state || this.getCaseFlowVisualsState();
      const reviewedAtUtc = options.reviewed
        ? (options.reviewedAtUtc || new Date().toISOString())
        : (this.caseSupportState.deliverableStateById?.case_flow_visuals?.reviewedAtUtc || '');
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        case_flow_visuals: {
          ...(this.caseSupportState.deliverableStateById?.case_flow_visuals || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: options.builtAtUtc || new Date().toISOString(),
          basisHash: state.currentBasisHash || '',
          rendererVersion: CASE_FLOW_VISUALS_RENDERER_VERSION,
          visualCount: state.flowCount || 0,
          reviewedAtUtc
        }
      };
      this.persistCaseSupportState();
    },

    async openCaseFlowVisualsModal(options = {}) {
      const openVisuals = async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.caseFlowVisualsMode = 'report';
        this.caseFlowVisualsIntent = 'report';
        this.showCaseInvestigationVisualizerModal = false;
        this.showCaseFlowVisualsModal = true;
        this.caseFlowVisualsDisplayMode = 'selected';
        this.caseFlowVisualsFullWindow = false;
        this.resetCaseFlowVisualOptionsForMode('report');
        this.caseFlowVisualsAdvancedOpen = false;
        this.caseFlowVisualsStatus = 'Reading current Main Data and address legend…';
        this.caseFlowVisualsSelectedNodeId = '';
        this.caseFlowVisualsSelectedPathOperationCandidate = null;
        this.caseFlowVisualsActiveIndex = Math.max(0, Number(options.index) || 0);
        await this.yieldToCaseUi({ paint: true, frames: 2, minDelayMs: 12 });
        this.refreshCaseFlowVisualsReview({ silent: true });
        this.renderCaseFlowVisualAtIndex(this.caseFlowVisualsActiveIndex, { silent: true });
        return true;
      };

      if (options.__caseOperationWrapped || this.caseModalOperation?.active) {
        return openVisuals();
      }

      return this.runCaseModalOperation({
        title: 'Opening case flow visuals…',
        message: 'Building Tool87-style recommended visuals from Main Data and the reviewed address legend.',
        target: 'case-flow-visuals-modal',
        finishMessage: 'Case flow visuals ready.'
      }, openVisuals);
    },

    async openCaseInvestigationVisualsModal(options = {}) {
      return this.openCaseInvestigationVisualizer({ ...(options || {}), source: options.source || 'legacy-investigation-visuals-modal' });
    },

    cancelCaseFlowVisualsModal() {
      if (this.caseFlowVisualsDownloading) return;
      this.showCaseFlowVisualsModal = false;
      this.showCaseInvestigationVisualizerModal = false;
      this.caseFlowVisualsFullWindow = false;
      this.caseFlowVisualsSelectedNodeId = '';
      this.caseFlowVisualsSelectedPathOperationCandidate = null;
      this.caseFlowVisualsPointer = { active: false, startX: 0, startY: 0, viewBox: null };
      this.caseFlowVisualsPendingViewport = null;
      if (this.caseFlowVisualsPanFrame && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(this.caseFlowVisualsPanFrame);
      }
      this.caseFlowVisualsPanFrame = 0;
    },

    refreshCaseFlowVisualsReview(options = {}) {
      this.caseFlowVisualsRefreshRunning = true;
      try {
        const state = this.getActiveCaseFlowVisualsState();
        this.caseFlowVisualsState = state;
        if (this.caseFlowVisualsActiveIndex >= state.flows.length) {
          this.caseFlowVisualsActiveIndex = Math.max(0, state.flows.length - 1);
        }
        if (state.canBuild) {
          this.renderCaseFlowVisualAtIndex(this.caseFlowVisualsActiveIndex, { silent: true, state });
        } else {
          this.caseFlowVisualsCurrentVisual = null;
          this.caseFlowVisualsSelectedNodeId = '';
          this.caseFlowVisualsSelectedPathOperationCandidate = null;
          this.caseFlowVisualsStatus = state.summary;
        }
        if (!this.caseFlowVisualsIsInvestigationSession()) {
          this.updateCaseFlowVisualsCheckResult(state);
        }
        if (!options.silent) this.showCaseToast(this.caseFlowVisualsIsInvestigationSession() ? 'Investigation visuals refreshed.' : 'Case flow visuals refreshed.');
        return state;
      } finally {
        this.caseFlowVisualsRefreshRunning = false;
      }
    },

    renderCaseFlowVisualAtIndex(index = 0, options = {}) {
      const state = options.state || this.caseFlowVisualsState || this.getActiveCaseFlowVisualsState();
      const flows = Array.isArray(state.flows) ? state.flows : [];
      if (!flows.length) {
        this.caseFlowVisualsCurrentVisual = null;
        this.caseFlowVisualsSelectedNodeId = '';
        this.caseFlowVisualsSelectedPathOperationCandidate = null;
        this.caseFlowVisualsStatus = state.summary || 'No case flow visuals are available.';
        return null;
      }
      const safeIndex = Math.max(0, Math.min(flows.length - 1, Number(index) || 0));
      const isInvestigation = this.caseFlowVisualsIsInvestigationSession();
      const displayMode = isInvestigation ? (this.caseFlowVisualsDisplayMode || 'selected') : 'selected';
      const visual = displayMode === 'allTogether' || displayMode === 'allStacked'
        ? buildCaseCombinedFlowVisual(flows, state, {
            ...this.getCaseFlowVisualOptions(),
            mode: displayMode === 'allStacked' ? 'stacked' : 'allTogether'
          })
        : buildCaseFlowVisual(flows[safeIndex], state, this.getCaseFlowVisualOptions());
      this.caseFlowVisualsActiveIndex = safeIndex;
      if (isInvestigation && displayMode === 'selected') {
        this.caseInvestigationVisualizerActiveIndex = safeIndex;
      }
      this.caseFlowVisualsState = state;
      this.caseFlowVisualsCurrentVisual = visual;
      this.caseFlowVisualsViewport = { ...(visual.viewBox || { x: 0, y: 0, width: 1200, height: 700 }) };
      this.caseFlowVisualsSelectedNodeId = '';
      this.caseFlowVisualsSelectedPathOperationCandidate = null;
      this.caseFlowVisualsStatus = visual.summary;
      if (!options.silent) this.showCaseToast(displayMode === 'selected' ? `Showing visual ${safeIndex + 1} of ${flows.length}.` : visual.summary);
      return visual;
    },

    setCaseFlowVisualDisplayMode(mode = 'selected') {
      const normalizedMode = ['selected', 'allTogether', 'allStacked'].includes(mode) ? mode : 'selected';
      this.caseFlowVisualsDisplayMode = normalizedMode;
      return this.renderCaseFlowVisualAtIndex(this.caseFlowVisualsActiveIndex);
    },

    toggleCaseFlowVisualsFullWindow() {
      this.caseFlowVisualsFullWindow = !this.caseFlowVisualsFullWindow;
      this.$nextTick?.(() => this.resetCaseFlowVisualViewport());
      return this.caseFlowVisualsFullWindow;
    },

    showPreviousCaseFlowVisual() {
      if (!this.caseFlowVisualCanGoPrevious) return false;
      this.renderCaseFlowVisualAtIndex(this.caseFlowVisualsActiveIndex - 1);
      return true;
    },

    showNextCaseFlowVisual() {
      if (!this.caseFlowVisualCanGoNext) return false;
      this.renderCaseFlowVisualAtIndex(this.caseFlowVisualsActiveIndex + 1);
      return true;
    },

    refreshCurrentCaseFlowVisual() {
      return this.renderCaseFlowVisualAtIndex(this.caseFlowVisualsActiveIndex);
    },

    caseFlowVisualNodeTransform(node = {}) {
      return `translate(${Number(node.x) || 0} ${Number(node.y) || 0})`;
    },

    caseFlowVisualNodeClass(node = {}) {
      return caseFlowVisualNodeClass(node, this.caseFlowVisualsSelectedNodeId);
    },

    caseFlowVisualEdgeClass(edge = {}) {
      return caseFlowVisualEdgeClass(edge);
    },

    caseFlowVisualEdgePath(edge = {}) {
      return caseFlowVisualEdgePath(edge, this.caseFlowVisualNodeMap || new Map());
    },

    selectCaseFlowVisualNode(node = null) {
      if (!node?.id) return;
      this.caseFlowVisualsSelectedNodeId = node.id;
      this.caseFlowVisualsSelectedPathOperationCandidate = this.findPathOperationCandidateForCaseFlowVisualNode(node);
    },

    clearCaseFlowVisualSelection() {
      this.caseFlowVisualsSelectedNodeId = '';
      this.caseFlowVisualsSelectedPathOperationCandidate = null;
    },

    getCaseFlowVisualNodeSpreadsheetRowIndexes(node = null) {
      const rows = [];
      const pushRowNumber = (value) => {
        const number = Number.parseInt(String(value ?? '').replace(/[^0-9]/g, ''), 10);
        if (Number.isFinite(number) && number > 0 && !rows.includes(number - 1)) rows.push(number - 1);
      };
      if (!node) return rows;
      if (Number.isInteger(node.rowIndex)) rows.push(node.rowIndex);
      if (Number.isFinite(Number(node.spreadsheetRowNumber))) pushRowNumber(node.spreadsheetRowNumber);
      if (Array.isArray(node.rowNumbers)) node.rowNumbers.forEach(pushRowNumber);
      if (node.sourceRows) String(node.sourceRows).split(/[,;\s]+/).forEach(pushRowNumber);
      (Array.isArray(node.detailRows) ? node.detailRows : []).forEach((row) => {
        const label = normalizeCell(row?.[0]).toLowerCase();
        if (label === 'spreadsheet rows' || label === 'row' || label === 'rows') {
          String(row?.[1] || '').split(/[,;\s]+/).forEach(pushRowNumber);
        }
      });
      return rows.sort((a, b) => a - b);
    },

    findPathOperationCandidateForCaseFlowVisualNode(node = null) {
      const rowIndexes = this.getCaseFlowVisualNodeSpreadsheetRowIndexes(node);
      if (!rowIndexes.length) return null;
      const analysis = buildPathOperationAnalysis(this.getMatrixForLogicalSheet('main'), this.getMatrixForLogicalSheet('raw'));
      for (const rowIndex of rowIndexes) {
        const exact = findExactPathOperationCandidateForRow(analysis, rowIndex);
        if (exact) return exact;
      }
      for (const rowIndex of rowIndexes) {
        const candidate = findPathOperationCandidateForRow(analysis, rowIndex);
        if (candidate) return candidate;
      }
      return null;
    },

    openCaseFlowVisualPathOperation(action = 'prune') {
      const normalizedAction = ['prune', 'graft', 'bridge'].includes(action) ? action : 'prune';
      const candidate = this.caseFlowVisualsSelectedPathOperationCandidate || this.findPathOperationCandidateForCaseFlowVisualNode(this.caseFlowVisualSelectedNode);
      if (!candidate) {
        this.showCaseToast('Select a transaction/output node that maps to a qualified Main Data path-operation row.');
        return false;
      }
      if (normalizedAction !== 'prune' && !candidate.isPathEnding) {
        this.showCaseToast('Graft and Bridge graft require a selected Path ends row.');
        return false;
      }
      this.caseFlowVisualsFullWindow = false;
      this.showCaseFlowVisualsModal = false;
      return this.openPathOperationModal({ candidateId: candidate.id, action: normalizedAction, source: 'case-visualizer' });
    },


    cloneCaseFlowVisualSnapshot() {
      if (!this.caseFlowVisualsCurrentVisual) return null;
      try {
        return {
          visual: JSON.parse(JSON.stringify(this.caseFlowVisualsCurrentVisual)),
          viewport: JSON.parse(JSON.stringify(this.caseFlowVisualsViewport || {})),
          selectedNodeId: this.caseFlowVisualsSelectedNodeId || ''
        };
      } catch {
        return null;
      }
    },

    restoreCaseFlowVisualSnapshot(snapshot = null) {
      if (!snapshot?.visual) return false;
      const visual = JSON.parse(JSON.stringify(snapshot.visual));
      const lookup = {};
      (Array.isArray(visual.nodes) ? visual.nodes : []).forEach((node) => { if (node?.id) lookup[node.id] = node; });
      visual.lookup = lookup;
      this.caseFlowVisualsCurrentVisual = visual;
      this.caseFlowVisualsViewport = snapshot.viewport || visual.viewBox || { x: 0, y: 0, width: 1200, height: 700 };
      this.caseFlowVisualsSelectedNodeId = snapshot.selectedNodeId || '';
      this.caseFlowVisualsSelectedPathOperationCandidate = this.findPathOperationCandidateForCaseFlowVisualNode(this.caseFlowVisualSelectedNode);
      return true;
    },

    pushCaseFlowVisualUndoSnapshot(reason = 'visual edit') {
      const snapshot = this.cloneCaseFlowVisualSnapshot();
      if (!snapshot) return false;
      this.caseFlowVisualsUndoStack = [
        ...(Array.isArray(this.caseFlowVisualsUndoStack) ? this.caseFlowVisualsUndoStack : []),
        { ...snapshot, reason }
      ].slice(-24);
      this.caseFlowVisualsRedoStack = [];
      return true;
    },

    deleteSelectedCaseFlowVisualBranch() {
      const visual = this.caseFlowVisualsCurrentVisual;
      const selectedId = this.caseFlowVisualsSelectedNodeId;
      if (!visual || !selectedId) {
        this.showCaseToast('Select a visual node before using Delete visual branch.');
        return false;
      }
      const nodes = Array.isArray(visual.nodes) ? visual.nodes : [];
      const edges = Array.isArray(visual.edges) ? visual.edges : [];
      if (!nodes.some(node => node.id === selectedId)) return false;
      this.pushCaseFlowVisualUndoSnapshot('delete visual branch');
      const removeIds = new Set([selectedId]);
      const queue = [selectedId];
      while (queue.length) {
        const current = queue.shift();
        edges.forEach((edge) => {
          if (edge.from !== current || removeIds.has(edge.to)) return;
          removeIds.add(edge.to);
          queue.push(edge.to);
        });
      }
      const nextNodes = nodes.filter(node => !removeIds.has(node.id));
      const nextEdges = edges.filter(edge => !removeIds.has(edge.from) && !removeIds.has(edge.to));
      const lookup = {};
      nextNodes.forEach((node) => { if (node?.id) lookup[node.id] = node; });
      this.caseFlowVisualsCurrentVisual = {
        ...visual,
        nodes: nextNodes,
        edges: nextEdges,
        lookup,
        summary: `Deleted ${nodes.length - nextNodes.length} visual node${nodes.length - nextNodes.length === 1 ? '' : 's'} from the current view only. Main Data was not changed.`
      };
      this.caseFlowVisualsSelectedNodeId = '';
      this.caseFlowVisualsSelectedPathOperationCandidate = null;
      this.caseFlowVisualsStatus = this.caseFlowVisualsCurrentVisual.summary;
      this.showCaseToast(this.caseFlowVisualsStatus);
      return true;
    },

    undoCaseFlowVisualEdit() {
      const stack = Array.isArray(this.caseFlowVisualsUndoStack) ? this.caseFlowVisualsUndoStack : [];
      if (!stack.length) return false;
      const current = this.cloneCaseFlowVisualSnapshot();
      const previous = stack[stack.length - 1];
      this.caseFlowVisualsUndoStack = stack.slice(0, -1);
      if (current) this.caseFlowVisualsRedoStack = [...(Array.isArray(this.caseFlowVisualsRedoStack) ? this.caseFlowVisualsRedoStack : []), current].slice(-24);
      this.restoreCaseFlowVisualSnapshot(previous);
      this.caseFlowVisualsStatus = 'Undid the last visual-only edit.';
      return true;
    },

    redoCaseFlowVisualEdit() {
      const stack = Array.isArray(this.caseFlowVisualsRedoStack) ? this.caseFlowVisualsRedoStack : [];
      if (!stack.length) return false;
      const current = this.cloneCaseFlowVisualSnapshot();
      const next = stack[stack.length - 1];
      this.caseFlowVisualsRedoStack = stack.slice(0, -1);
      if (current) this.caseFlowVisualsUndoStack = [...(Array.isArray(this.caseFlowVisualsUndoStack) ? this.caseFlowVisualsUndoStack : []), current].slice(-24);
      this.restoreCaseFlowVisualSnapshot(next);
      this.caseFlowVisualsStatus = 'Redid the visual-only edit.';
      return true;
    },

    toggleCaseFlowVisualFullWindow() {
      this.caseFlowVisualsFullWindow = !this.caseFlowVisualsFullWindow;
      this.$nextTick?.(() => this.resetCaseFlowVisualViewport());
      return this.caseFlowVisualsFullWindow;
    },

    resetCaseFlowVisualViewport() {
      const viewBox = this.caseFlowVisualsCurrentVisual?.viewBox;
      if (!viewBox) return;
      this.caseFlowVisualsViewport = { ...viewBox };
    },

    zoomCaseFlowVisual(factor = 1) {
      const current = this.caseFlowVisualsViewport || this.caseFlowVisualsCurrentVisual?.viewBox;
      if (!current) return;
      const safeFactor = Math.max(0.2, Math.min(5, Number(factor) || 1));
      const centerX = current.x + current.width / 2;
      const centerY = current.y + current.height / 2;
      const width = Math.max(320, current.width * safeFactor);
      const height = Math.max(240, current.height * safeFactor);
      this.caseFlowVisualsViewport = {
        x: centerX - width / 2,
        y: centerY - height / 2,
        width,
        height
      };
    },

    onCaseFlowVisualWheel(event) {
      const current = this.caseFlowVisualsViewport || this.caseFlowVisualsCurrentVisual?.viewBox;
      if (!current) return;
      const factor = event.deltaY < 0 ? 0.86 : 1.16;
      const svg = this.$refs.caseFlowVisualSvg;
      const rect = svg?.getBoundingClientRect?.();
      const anchorX = rect ? current.x + ((event.clientX - rect.left) / rect.width) * current.width : current.x + current.width / 2;
      const anchorY = rect ? current.y + ((event.clientY - rect.top) / rect.height) * current.height : current.y + current.height / 2;
      const width = Math.max(320, current.width * factor);
      const height = Math.max(240, current.height * factor);
      this.caseFlowVisualsViewport = {
        x: anchorX - ((anchorX - current.x) / current.width) * width,
        y: anchorY - ((anchorY - current.y) / current.height) * height,
        width,
        height
      };
    },

    onCaseFlowVisualPointerDown(event) {
      if (event.target?.closest?.('.case-flow-visual-node')) return;
      const current = this.caseFlowVisualsViewport || this.caseFlowVisualsCurrentVisual?.viewBox;
      if (!current) return;
      this.caseFlowVisualsPointer = {
        active: true,
        startX: event.clientX,
        startY: event.clientY,
        viewBox: { ...current }
      };
      event.currentTarget?.setPointerCapture?.(event.pointerId);
    },

    onCaseFlowVisualPointerMove(event) {
      const pointer = this.caseFlowVisualsPointer || {};
      if (!pointer.active || !pointer.viewBox) return;
      const svg = this.$refs.caseFlowVisualSvg;
      const rect = svg?.getBoundingClientRect?.();
      if (!rect?.width || !rect?.height) return;
      const dx = ((event.clientX - pointer.startX) / rect.width) * pointer.viewBox.width;
      const dy = ((event.clientY - pointer.startY) / rect.height) * pointer.viewBox.height;
      this.caseFlowVisualsPendingViewport = {
        ...pointer.viewBox,
        x: pointer.viewBox.x - dx,
        y: pointer.viewBox.y - dy
      };
      if (this.caseFlowVisualsPanFrame) return;
      const applyViewport = () => {
        this.caseFlowVisualsPanFrame = 0;
        if (this.caseFlowVisualsPendingViewport) {
          this.caseFlowVisualsViewport = this.caseFlowVisualsPendingViewport;
          this.caseFlowVisualsPendingViewport = null;
        }
      };
      if (typeof window.requestAnimationFrame === 'function') {
        this.caseFlowVisualsPanFrame = window.requestAnimationFrame(applyViewport);
      } else {
        this.caseFlowVisualsPanFrame = window.setTimeout(applyViewport, 16);
      }
    },

    onCaseFlowVisualPointerUp(event) {
      if (!this.caseFlowVisualsPointer?.active) return;
      event.currentTarget?.releasePointerCapture?.(event.pointerId);
      if (this.caseFlowVisualsPendingViewport) {
        this.caseFlowVisualsViewport = this.caseFlowVisualsPendingViewport;
        this.caseFlowVisualsPendingViewport = null;
      }
      this.caseFlowVisualsPointer = { active: false, startX: 0, startY: 0, viewBox: null };
    },

    serializedCurrentCaseFlowVisualSvg() {
      if (!this.caseFlowVisualsCurrentVisual) return '';
      return serializeCaseFlowVisualSvg(this.caseFlowVisualsCurrentVisual, {
        title: `Case Buddy Flow ${this.caseFlowVisualsCurrentVisual.mainLabel || ''}`,
        selectedNodeId: this.caseFlowVisualsSelectedNodeId
      });
    },

    downloadCurrentCaseFlowVisualSvg() {
      const svgText = this.serializedCurrentCaseFlowVisualSvg();
      if (!svgText) {
        this.showCaseToast('No current visual is available to download.');
        return false;
      }
      const flow = this.caseFlowVisualCurrentFlow || {};
      const filename = `case-buddy-flow-${String(this.caseFlowVisualsActiveIndex + 1).padStart(3, '0')}-${flow.mainLabel || 'visual'}.svg`.replace(/[^a-z0-9._-]+/gi, '_');
      downloadCaseFlowVisualBlob(svgText, filename, 'image/svg+xml;charset=utf-8');
      if (!this.caseFlowVisualsIsInvestigationSession()) {
        this.markCaseFlowVisualsBuiltRecord({ state: this.caseFlowVisualsState || this.getCaseFlowVisualsState() });
      }
      this.showCaseToast('Current flow SVG downloaded.');
      return true;
    },

    async downloadCaseFlowVisualsBundle(format = 'svg', options = {}) {
      if (this.caseFlowVisualsDownloading) return false;
      const normalizedFormat = String(format || 'svg').toLowerCase() === 'jpg' ? 'jpeg' : String(format || 'svg').toLowerCase();
      this.caseFlowVisualsDownloading = true;
      this.caseFlowVisualsDownloadFormat = normalizedFormat;
      const token = this.beginCaseModalOperation({
        title: `Building ${normalizedFormat === 'jpeg' ? 'JPG' : normalizedFormat.toUpperCase()} visual package…`,
        message: 'Preparing Tool87-style recommended visuals from the current case.',
        target: 'case-flow-visuals-download',
        replaceActive: true
      });
      await this.yieldToCaseUi({ paint: true, frames: 2, minDelayMs: 24 });
      try {
        const state = this.refreshCaseFlowVisualsReview({ silent: true });
        if (!state.canBuild) {
          this.showCaseToast(state.summary || 'No visuals are available to export.');
          return false;
        }
        const bundle = await buildCaseFlowVisualBundleFiles(state, {
          format: normalizedFormat,
          visualOptions: this.getCaseFlowVisualOptions(),
          onProgress: async ({ index, total, flow, phase }) => {
            this.updateCaseModalOperation({
              message: `${phase === 'rasterize' ? 'Rendering image' : 'Building visual'} ${index + 1} of ${total}…`,
              detail: flow?.mainLabel ? `Flow ${flow.mainLabel}` : ''
            }, token);
            await this.yieldToCaseUi({ paint: true, minDelayMs: 8 });
          }
        });
        this.updateCaseModalOperation({ message: 'Compressing visual package…', detail: '' }, token);
        await this.yieldToCaseUi({ paint: true, minDelayMs: 8 });
        const zipBlob = createCaseFlowVisualZipBlob(bundle.files);
        const filename = buildCaseFlowVisualsPackageFilename({ ...state, caseDisplayId: this.caseDisplayId, caseDisplayName: this.caseDisplayName }, normalizedFormat === 'jpeg' ? 'jpg' : normalizedFormat);
        downloadCaseFlowVisualBlob(zipBlob, filename, 'application/zip');
        if (!this.caseFlowVisualsIsInvestigationSession()) {
          this.markCaseFlowVisualsBuiltRecord({ state });
          this.updateCaseFlowVisualsCheckResult(state);
          if (options.markReviewed) {
            this.markCaseFlowVisualsDone({ source: `download_${normalizedFormat}`, silent: true });
          }
        }
        this.caseFlowVisualsStatus = `Downloaded ${state.flowCount} visual${state.flowCount === 1 ? '' : 's'} as ${filename}.`;
        this.showCaseToast(this.caseFlowVisualsStatus);
        return true;
      } catch (error) {
        this.caseFlowVisualsStatus = `Visual package export failed: ${error?.message || error}`;
        this.showCaseToast(this.caseFlowVisualsStatus);
        throw error;
      } finally {
        this.caseFlowVisualsDownloading = false;
        this.caseFlowVisualsDownloadFormat = '';
        this.finishCaseModalOperation(token, { message: 'Visual package ready.', hideDelayMs: 700 });
      }
    },

    stageCaseFlowVisualsPlaceholder(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging visuals package summary…',
          message: 'Building summary rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Visuals package summary staged.'
        }, () => this.stageCaseFlowVisualsPlaceholder({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getCaseFlowVisualsState();
      const rows = buildCaseFlowVisualsPackageRows(state, {
        caseDisplayName: this.caseDisplayName,
        caseDisplayId: this.caseDisplayId,
        caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        reviewIsCurrent: state.reviewIsCurrent,
        completedAtUtc: state.completedAtUtc,
        source: state.source,
        currentBasisHash: state.currentBasisHash
      });
      this.loadOutputRows(rows, {
        title: 'Case flow visuals package summary',
        detail: 'Tool87-style case flow visual package summary. Main Data is not modified.',
        sourceActionId: 'case_flow_visuals_summary',
        sourceDeliverableId: 'case_flow_visuals',
        target: 'deliverable-preview'
      });
      this.markCaseFlowVisualsBuiltRecord({ state });
      this.updateCaseFlowVisualsCheckResult(state);
      if (!options.silent) {
        this.scrollToSection('case-output');
        this.showCaseToast('Case flow visuals package summary staged in Output/Review.');
      }
      return true;
    },

    downloadCaseFlowVisualsGuidanceTsv(options = {}) {
      const state = this.getCaseFlowVisualsState();
      const rows = buildCaseFlowVisualsPackageRows(state, {
        caseDisplayName: this.caseDisplayName,
        caseDisplayId: this.caseDisplayId,
        caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        reviewIsCurrent: state.reviewIsCurrent,
        completedAtUtc: state.completedAtUtc,
        source: state.source,
        currentBasisHash: state.currentBasisHash
      });
      downloadTextFile('case_flow_visuals_package_summary.tsv', buildTsv(rows), 'text/tab-separated-values;charset=utf-8;');
      this.markCaseFlowVisualsBuiltRecord({ state });
      this.updateCaseFlowVisualsCheckResult(state);
      if (!options.silent) this.showCaseToast('Case flow visuals package summary TSV downloaded.');
      return true;
    },

    markCaseFlowVisualsDone(options = {}) {
      const completedAtUtc = new Date().toISOString();
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_CASE_FLOW_VISUALS_CREATED, options.source || 'case_flow_visuals_done', { silent: true });
      const state = this.getCaseFlowVisualsState();
      this.caseFlowVisualsState = state;
      this.markCaseFlowVisualsBuiltRecord({ state, reviewed: true, reviewedAtUtc: completedAtUtc });
      this.updateCaseFlowVisualsCheckResult(state);
      if (!options.silent) this.showCaseToast('Case flow visuals marked done for this case revision.');
      return true;
    },

    toggleCaseFlowVisualsReviewed() {
      const existing = this.caseSupportState.deliverableStateById?.case_flow_visuals || {};
      if (existing.reviewedAtUtc) {
        this.caseSupportState.deliverableStateById = {
          ...(this.caseSupportState.deliverableStateById || {}),
          case_flow_visuals: { ...existing, reviewedAtUtc: '' }
        };
        const signals = { ...(this.caseSupportState.workflowState.signalCompleteByCode || {}) };
        delete signals[WORKFLOW_SIGNAL_CASE_FLOW_VISUALS_CREATED];
        this.caseSupportState.workflowState.signalCompleteByCode = signals;
        this.updateCaseFlowVisualsCheckResult();
        this.showCaseToast('Case flow visuals completion mark cleared.');
        return true;
      }
      return this.markCaseFlowVisualsDone({ source: 'deliverables-panel' });
    },

    getCaseInvestigationVisualizerOptions() {
      return {
        ...CASE_INVESTIGATION_VISUALS_DEFAULT_OPTIONS,
        layoutMode: 'upwardBranches',
        mergeAddressMode: 'none',
        mergeTransactionNodes: false,
        wrapLinearTrunks: false,
        trunkWrapMinLength: 5,
        trunkWrapFirstRowLength: 5,
        trunkWrapOtherRowLength: 4,
        trunkWrapRowVerticalMultiplier: 1.35,
        highlightSwaps: false,
        highlightBridges: false,
        cleanupOverlaps: false,
        labelAddresses: true,
        showFundsFollowedBars: true,
        ...(this.caseInvestigationVisualizerOptions || {})
      };
    },

    getCaseInvestigationVisualizerState(options = {}) {
      this.ensureCaseWorkbookScaffold?.({ silent: true });
      const mainData = this.getMatrixForLogicalSheet('main');
      const rawData = this.getMatrixForLogicalSheet('raw');
      const workflowState = this.caseSupportState.workflowState || {};
      const visualOptions = this.getCaseInvestigationVisualizerOptions();
      const buildOptions = {
        caseDisplayName: this.caseDisplayName,
        caseDisplayId: this.caseDisplayId,
        caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        visualOptions,
        investigationMode: true,
        projectFileId: this.currentProjectFileId || '',
        ...(options || {})
      };
      const cacheKey = this.getCaseComputationCacheKey?.('case-investigation-visualizer-state', buildOptions) || '';
      const producer = () => {
        const baseState = buildCaseFlowVisualsState(mainData, workflowState, buildOptions);
        const sapling = buildSaplingPreparationAnalysis(mainData, rawData, workflowState);
        const preparedRows = (Array.isArray(sapling.rows) ? sapling.rows : []).filter(row => !!row.processed);
        const preparedLabels = new Set(preparedRows.map(row => normalizeComparisonKey(row.pathLabel)).filter(Boolean));
        const preparedHashes = new Set(preparedRows.map(row => normalizeComparisonKey(row.hash)).filter(Boolean));
        const preparedStartRows = new Set(preparedRows.map(row => Number(row.rowIndex)).filter(Number.isInteger));
        const flows = (Array.isArray(baseState.flows) ? baseState.flows : []).filter((flow) => {
          const mainTxn = flow?.mainTxn || {};
          const rowIndex = Number(mainTxn.rowIndex);
          const label = normalizeComparisonKey(flow?.mainLabel || mainTxn.pathLabel);
          const hash = normalizeComparisonKey(flow?.mainHash || mainTxn.transactionHash || mainTxn.hash);
          return (Number.isInteger(rowIndex) && preparedStartRows.has(rowIndex)) ||
            (!!label && preparedLabels.has(label)) ||
            (!!hash && preparedHashes.has(hash));
        });
        const visibleEntryIds = new Set(flows.flatMap(flow => (Array.isArray(flow.entries) ? flow.entries : []).map(entry => entry.entryId).filter(Boolean)));
        const entries = (Array.isArray(baseState.entries) ? baseState.entries : []).filter(entry => visibleEntryIds.has(entry.entryId));
        const labelCount = baseState.addressLabelCount || 0;
        const flowCount = flows.length;
        const currentBasisHash = `case-investigation-visualizer-${caseBuddyHashString(JSON.stringify({
          base: baseState.currentBasisHash || '',
          projectFileId: buildOptions.projectFileId || '',
          revision: buildOptions.caseDataRevision,
          visualOptions,
          prepared: preparedRows.map(row => [row.rowIndex, row.pathLabel, row.hash, row.status])
        }))}`;
        const summary = flowCount > 0
          ? `${flowCount} prepared sapling flow${flowCount === 1 ? '' : 's'} can be inspected now. This interim view keeps report cleanup, same-address merge, and automatic polishing off unless you enable them.`
          : preparedRows.length > 0
            ? `${preparedRows.length} prepared sapling${preparedRows.length === 1 ? '' : 's'} detected, but no matching visual flow could be built from current path labels.`
            : 'Prepare at least one sapling from Raw Data into Main Data before opening the investigation visualizer.';
        return {
          ...baseState,
          currentBasisHash,
          projectFileId: buildOptions.projectFileId || '',
          caseFileId: buildOptions.projectFileId || '',
          entries,
          flows,
          flowCount,
          qualifyingFlowCount: flowCount,
          entryCount: entries.length,
          addressLabelCount: labelCount,
          preparedSaplingCount: preparedRows.length,
          saplingPreparedCount: preparedRows.length,
          saplingTotalCount: sapling.total || 0,
          canBuild: flowCount > 0,
          reviewIsCurrent: false,
          signalCurrent: false,
          manualCurrent: false,
          completedAtUtc: '',
          source: 'investigation-visualizer',
          sapling,
          preparedRows,
          summary
        };
      };
      return this.getCachedCaseComputation?.('state', cacheKey, producer) || producer();
    },

    getCaseInvestigationVisualizerReadiness() {
      const preparedFromSummary = Number(this.caseSummary?.saplingPreparedMainTransactions) || 0;
      let state = null;
      try {
        state = this.getCaseInvestigationVisualizerState();
      } catch {
        state = null;
      }
      const count = state ? (state.qualifyingFlowCount || state.flowCount || 0) : 0;
      const prepared = state ? (state.preparedSaplingCount || state.saplingPreparedCount || preparedFromSummary || 0) : preparedFromSummary;
      const canOpen = count > 0;
      const summary = state?.summary || (canOpen
        ? `${count} prepared sapling flow${count === 1 ? '' : 's'} can be visualized.`
        : prepared > 0
          ? `${prepared} prepared sapling${prepared === 1 ? '' : 's'} detected, but no visualizable path-labeled flow is available yet.`
          : 'Prepare at least one sapling before visualizing the case.');
      return {
        canOpen,
        count,
        prepared,
        summary,
        title: canOpen ? summary : summary || 'Prepare at least one sapling before visualizing the case.'
      };
    },

    caseInvestigationVisualizerStateIsCurrent(state = null) {
      if (!state || !state.currentBasisHash) return false;
      const currentFileId = normalizeCell(this.currentProjectFileId);
      const stateFileId = normalizeCell(state.projectFileId || state.caseFileId);
      if (currentFileId && stateFileId && currentFileId !== stateFileId) return false;
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      return Number(state.caseDataRevision) === currentRevision;
    },

    caseInvestigationVisualizerStateMatchesCurrentCase(state = null) {
      return this.caseInvestigationVisualizerStateIsCurrent(state);
    },

    resetCaseInvestigationVisualizerState(options = {}) {
      if (options.close !== false) {
        this.showCaseInvestigationVisualizerModal = false;
        this.caseInvestigationVisualizerFullscreen = false;
        this.syncCaseInvestigationVisualizerPageScrollLock?.();
      }
      this.finishCaseInvestigationVisualizerAdvancedResize?.();
      this.finishCaseInvestigationVisualizerSidePanelResize?.();
      this.clearCaseInvestigationVisualizerPanFrame?.();
      this.caseInvestigationVisualizerPendingViewport = null;
      this.caseInvestigationVisualizerStatus = '';
      this.caseInvestigationVisualizerRefreshRunning = false;
      this.caseInvestigationVisualizerDownloading = false;
      this.caseInvestigationVisualizerViewMode = 'single';
      this.caseInvestigationVisualizerActiveIndex = 0;
      this.caseInvestigationVisualizerLastFlowKey = '';
      this.caseInvestigationVisualizerState = null;
      this.caseInvestigationVisualizerCurrentVisual = null;
      this.caseInvestigationVisualizerSelectedNodeId = '';
      this.caseInvestigationVisualizerSelectedNodeIds = [];
      this.caseInvestigationVisualizerSelectedCandidate = null;
      this.caseInvestigationVisualizerHiddenNodeIds = [];
      this.caseInvestigationVisualizerViewport = { x: 0, y: 0, width: 1200, height: 700 };
      this.clearCaseInvestigationVisualizerVisualHistory?.();
      this.resetCaseInvestigationVisualizerInteraction?.({ clearSelection: true });
      this.resetCaseHopSolverPlaceholderState?.({ close: true, force: true, reason: options.reason || 'visualizer-reset' });
      return true;
    },

    async openCaseInvestigationVisualizer(options = {}) {
      const openVisualizer = async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.showCaseFlowVisualsModal = false;
        this.caseFlowVisualsMode = 'report';
        if (!this.caseInvestigationVisualizerStateIsCurrent(this.caseInvestigationVisualizerState)) {
          this.resetCaseInvestigationVisualizerState({ close: false, reason: 'stale-investigation-state' });
        }
        this.showCaseInvestigationVisualizerModal = true;
        this.syncCaseInvestigationVisualizerPageScrollLock?.();
        this.caseInvestigationVisualizerStatus = 'Reading prepared saplings from Main Data…';
        this.caseInvestigationVisualizerAdvancedOpen = true;
        this.caseInvestigationVisualizerSelectedNodeId = '';
        this.caseInvestigationVisualizerSelectedNodeIds = [];
        this.caseInvestigationVisualizerSelectedCandidate = null;
        this.caseInvestigationVisualizerHiddenNodeIds = [];
        this.caseInvestigationVisualizerCurrentVisual = null;
        this.caseInvestigationVisualizerState = null;
        this.clearCaseInvestigationVisualizerVisualHistory?.();
        this.resetCaseInvestigationVisualizerInteraction?.({ clearSelection: true });
        await this.yieldToCaseUi({ paint: true, frames: 2, minDelayMs: 12 });
        const state = this.refreshCaseInvestigationVisualizer({ silent: true, preserveFlow: true });
        if (!state.canBuild) {
          this.caseInvestigationVisualizerStatus = state.summary;
          if (!options.silent) this.showCaseToast(state.summary);
          return false;
        }
        return true;
      };

      if (options.__caseOperationWrapped || this.caseModalOperation?.active) {
        return openVisualizer();
      }

      return this.runCaseModalOperation({
        title: 'Opening investigation visualizer…',
        message: 'Building an interim Tool87-style visual workspace from prepared saplings.',
        target: 'case-investigation-visualizer-modal',
        finishMessage: 'Investigation visualizer ready.'
      }, openVisualizer);
    },

    setCaseInvestigationVisualizerPageScrollLock(locked = false, options = {}) {
      if (typeof document === 'undefined') return false;
      const body = document.body;
      const root = document.documentElement;
      if (!body || !root) return false;

      if (locked) {
        if (!this.caseInvestigationVisualizerScrollLockSnapshot) {
          const scrollbarWidth = typeof window !== 'undefined'
            ? Math.max(0, (window.innerWidth || 0) - (root.clientWidth || 0))
            : 0;
          this.caseInvestigationVisualizerScrollLockSnapshot = {
            bodyOverflow: body.style.overflow || '',
            bodyPaddingRight: body.style.paddingRight || '',
            bodyOverscrollBehavior: body.style.overscrollBehavior || '',
            rootOverflow: root.style.overflow || '',
            rootOverscrollBehavior: root.style.overscrollBehavior || '',
            scrollbarWidth
          };
        }

        body.classList.add('case-investigation-visualizer-page-locked');
        root.classList.add('case-investigation-visualizer-page-locked');
        body.style.overflow = 'hidden';
        root.style.overflow = 'hidden';
        body.style.overscrollBehavior = 'contain';
        root.style.overscrollBehavior = 'contain';
        const scrollbarWidth = Number(this.caseInvestigationVisualizerScrollLockSnapshot?.scrollbarWidth) || 0;
        if (scrollbarWidth > 0 && !this.caseInvestigationVisualizerScrollLockSnapshot?.bodyPaddingRight) {
          body.style.paddingRight = `${scrollbarWidth}px`;
        }
        return true;
      }

      const snapshot = this.caseInvestigationVisualizerScrollLockSnapshot;
      if (!snapshot && !options.force) return false;
      body.classList.remove('case-investigation-visualizer-page-locked');
      root.classList.remove('case-investigation-visualizer-page-locked');
      body.style.overflow = snapshot?.bodyOverflow || '';
      body.style.paddingRight = snapshot?.bodyPaddingRight || '';
      body.style.overscrollBehavior = snapshot?.bodyOverscrollBehavior || '';
      root.style.overflow = snapshot?.rootOverflow || '';
      root.style.overscrollBehavior = snapshot?.rootOverscrollBehavior || '';
      this.caseInvestigationVisualizerScrollLockSnapshot = null;
      return true;
    },

    syncCaseInvestigationVisualizerPageScrollLock() {
      return this.setCaseInvestigationVisualizerPageScrollLock(!!this.showCaseInvestigationVisualizerModal);
    },

    getCaseInvestigationVisualizerAdvancedHeightBounds() {
      const viewportHeight = typeof window === 'undefined' ? 900 : (window.innerHeight || 900);
      const min = this.caseInvestigationVisualizerFullscreen ? 84 : 118;
      const maxCap = this.caseInvestigationVisualizerFullscreen ? 520 : 620;
      const ratioCap = this.caseInvestigationVisualizerFullscreen ? 0.42 : 0.52;
      const max = Math.max(min + 42, Math.min(maxCap, Math.round(viewportHeight * ratioCap)));
      return { min, max };
    },

    clampCaseInvestigationVisualizerAdvancedHeight(value = this.caseInvestigationVisualizerAdvancedHeight) {
      const bounds = this.getCaseInvestigationVisualizerAdvancedHeightBounds();
      const fallback = this.caseInvestigationVisualizerFullscreen ? 118 : 220;
      const numeric = Number(value);
      const height = Number.isFinite(numeric) ? numeric : fallback;
      const clamped = Math.max(bounds.min, Math.min(bounds.max, height));
      this.caseInvestigationVisualizerAdvancedHeight = clamped;
      return clamped;
    },

    setCaseInvestigationVisualizerAdvancedHeight(value = this.caseInvestigationVisualizerAdvancedHeight) {
      this.caseInvestigationVisualizerAdvancedOpen = true;
      return this.clampCaseInvestigationVisualizerAdvancedHeight(value);
    },

    beginCaseInvestigationVisualizerAdvancedResize(event) {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      if (!event) return false;
      event.preventDefault?.();
      event.stopPropagation?.();
      this.caseInvestigationVisualizerAdvancedOpen = true;
      const height = this.clampCaseInvestigationVisualizerAdvancedHeight();
      this.caseInvestigationVisualizerAdvancedResizeStart = {
        y: Number(event.clientY) || 0,
        height
      };
      this.caseInvestigationVisualizerAdvancedResizing = true;
      if (event.currentTarget?.setPointerCapture && event.pointerId != null) {
        try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* Pointer capture is optional. */ }
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('pointermove', this.caseInvestigationVisualizerAdvancedResizePointerMove, { passive: false });
        document.addEventListener('pointerup', this.finishCaseInvestigationVisualizerAdvancedResize, { once: true });
        document.addEventListener('pointercancel', this.finishCaseInvestigationVisualizerAdvancedResize, { once: true });
      }
      return true;
    },

    caseInvestigationVisualizerAdvancedResizePointerMove(event) {
      if (!this.caseInvestigationVisualizerAdvancedResizing) return;
      event?.preventDefault?.();
      const start = this.caseInvestigationVisualizerAdvancedResizeStart || { y: 0, height: 118 };
      const delta = (Number(start.y) || 0) - (Number(event?.clientY) || 0);
      this.setCaseInvestigationVisualizerAdvancedHeight((Number(start.height) || 118) + delta);
    },

    finishCaseInvestigationVisualizerAdvancedResize() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('pointermove', this.caseInvestigationVisualizerAdvancedResizePointerMove);
        document.removeEventListener('pointerup', this.finishCaseInvestigationVisualizerAdvancedResize);
        document.removeEventListener('pointercancel', this.finishCaseInvestigationVisualizerAdvancedResize);
      }
      this.caseInvestigationVisualizerAdvancedResizing = false;
      this.clampCaseInvestigationVisualizerAdvancedHeight();
    },

    adjustCaseInvestigationVisualizerAdvancedHeightFromKeyboard(event) {
      const key = String(event?.key || '');
      if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) return;
      event.preventDefault?.();
      event.stopPropagation?.();
      const bounds = this.getCaseInvestigationVisualizerAdvancedHeightBounds();
      if (key === 'Home') {
        this.setCaseInvestigationVisualizerAdvancedHeight(bounds.min);
        return;
      }
      if (key === 'End') {
        this.setCaseInvestigationVisualizerAdvancedHeight(bounds.max);
        return;
      }
      const step = event?.shiftKey ? 48 : 22;
      const direction = key === 'ArrowUp' ? 1 : -1;
      this.setCaseInvestigationVisualizerAdvancedHeight((Number(this.caseInvestigationVisualizerAdvancedHeight) || bounds.min) + (step * direction));
    },

    getCaseInvestigationVisualizerSidePanelWidthBounds() {
      const viewportWidth = typeof window === 'undefined' ? 1440 : (window.innerWidth || 1440);
      const min = viewportWidth < 980 ? 240 : 280;
      const maxByRatio = this.caseInvestigationVisualizerFullscreen
        ? Math.round(viewportWidth * 0.44)
        : Math.round(viewportWidth * 0.46);
      const maxBySpace = Math.max(min, viewportWidth - (this.caseInvestigationVisualizerFullscreen ? 560 : 620));
      const max = Math.max(min + 40, Math.min(maxByRatio, maxBySpace, this.caseInvestigationVisualizerFullscreen ? 660 : 600));
      return { min, max };
    },

    clampCaseInvestigationVisualizerSidePanelWidth(value = this.caseInvestigationVisualizerSidePanelWidth, options = {}) {
      const bounds = this.getCaseInvestigationVisualizerSidePanelWidthBounds();
      const fallback = this.caseInvestigationVisualizerFullscreen ? 360 : 390;
      const numeric = Number(value);
      const width = Number.isFinite(numeric) ? numeric : fallback;
      const clamped = Math.round(Math.max(bounds.min, Math.min(bounds.max, width)));
      if (options.commit !== false) this.caseInvestigationVisualizerSidePanelWidth = clamped;
      return clamped;
    },

    resetCaseInvestigationVisualizerSidePanelWidth() {
      this.caseInvestigationVisualizerSidePanelWidth = this.clampCaseInvestigationVisualizerSidePanelWidth(
        this.caseInvestigationVisualizerFullscreen ? 360 : 390
      );
    },

    beginCaseInvestigationVisualizerSidePanelResize(event) {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      if (!event) return false;
      event.preventDefault?.();
      event.stopPropagation?.();
      const width = this.clampCaseInvestigationVisualizerSidePanelWidth();
      this.caseInvestigationVisualizerSidePanelResizeStart = {
        x: Number(event.clientX) || 0,
        width
      };
      this.caseInvestigationVisualizerSidePanelResizing = true;
      if (event.currentTarget?.setPointerCapture && event.pointerId != null) {
        try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* Pointer capture is optional. */ }
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('pointermove', this.caseInvestigationVisualizerSidePanelResizePointerMove, { passive: false });
        document.addEventListener('pointerup', this.finishCaseInvestigationVisualizerSidePanelResize, { once: true });
        document.addEventListener('pointercancel', this.finishCaseInvestigationVisualizerSidePanelResize, { once: true });
      }
      return true;
    },

    caseInvestigationVisualizerSidePanelResizePointerMove(event) {
      if (!this.caseInvestigationVisualizerSidePanelResizing) return;
      event?.preventDefault?.();
      const start = this.caseInvestigationVisualizerSidePanelResizeStart || { x: 0, width: 390 };
      const delta = (Number(start.x) || 0) - (Number(event?.clientX) || 0);
      this.caseInvestigationVisualizerSidePanelWidth = this.clampCaseInvestigationVisualizerSidePanelWidth((Number(start.width) || 390) + delta);
    },

    finishCaseInvestigationVisualizerSidePanelResize() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('pointermove', this.caseInvestigationVisualizerSidePanelResizePointerMove);
        document.removeEventListener('pointerup', this.finishCaseInvestigationVisualizerSidePanelResize);
        document.removeEventListener('pointercancel', this.finishCaseInvestigationVisualizerSidePanelResize);
      }
      this.caseInvestigationVisualizerSidePanelResizing = false;
      this.clampCaseInvestigationVisualizerSidePanelWidth();
    },

    adjustCaseInvestigationVisualizerSidePanelWidthFromKeyboard(event) {
      const key = String(event?.key || '');
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;
      event.preventDefault?.();
      event.stopPropagation?.();
      const bounds = this.getCaseInvestigationVisualizerSidePanelWidthBounds();
      if (key === 'Home') {
        this.caseInvestigationVisualizerSidePanelWidth = bounds.min;
        return;
      }
      if (key === 'End') {
        this.caseInvestigationVisualizerSidePanelWidth = bounds.max;
        return;
      }
      const step = event?.shiftKey ? 64 : 24;
      const direction = key === 'ArrowLeft' ? 1 : -1;
      this.caseInvestigationVisualizerSidePanelWidth = this.clampCaseInvestigationVisualizerSidePanelWidth(
        (Number(this.caseInvestigationVisualizerSidePanelWidth) || bounds.min) + (step * direction)
      );
    },

    caseInvestigationVisualizerIsLocked() {
      return !!this.caseHopSolverPlaceholderRunning || !!this.caseInvestigationHopSolverPlaceholder?.busy;
    },

    showCaseInvestigationVisualizerLockedToast() {
      this.showCaseToast('Solve Hop placeholder is running. Use Request stop before changing visualizer state.');
      return false;
    },

    cancelCaseInvestigationVisualizer() {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      if (this.caseInvestigationVisualizerDownloading) return;
      this.showCaseInvestigationVisualizerModal = false;
      this.caseInvestigationVisualizerFullscreen = false;
      this.finishCaseInvestigationVisualizerAdvancedResize?.();
      this.finishCaseInvestigationVisualizerSidePanelResize?.();
      this.syncCaseInvestigationVisualizerPageScrollLock?.();
      this.caseInvestigationVisualizerSelectedNodeId = '';
      this.caseInvestigationVisualizerSelectedNodeIds = [];
      this.caseInvestigationVisualizerSelectedCandidate = null;
      this.resetCaseInvestigationVisualizerInteraction?.({ clearSelection: true });
    },

    refreshCaseInvestigationVisualizer(options = {}) {
      if (this.caseInvestigationVisualizerIsLocked?.()) {
        this.showCaseInvestigationVisualizerLockedToast();
        return this.caseInvestigationVisualizerState || null;
      }
      this.caseInvestigationVisualizerRefreshRunning = true;
      try {
        const previousKey = options.preserveFlow !== false
          ? (this.caseInvestigationVisualizerLastFlowKey || this.caseInvestigationVisualizerCurrentFlow?.key || '')
          : '';
        const state = this.getCaseInvestigationVisualizerState();
        const flows = Array.isArray(state.flows) ? state.flows : [];
        let nextIndex = Math.max(0, Math.min(flows.length - 1, Number(this.caseInvestigationVisualizerActiveIndex) || 0));
        if (previousKey) {
          const found = flows.findIndex(flow => flow.key === previousKey || flow.mainLabel === previousKey);
          if (found >= 0) nextIndex = found;
        }
        this.caseInvestigationVisualizerState = state;
        this.caseInvestigationVisualizerActiveIndex = nextIndex;
        if (state.canBuild) {
          this.renderCaseInvestigationVisualizerAtIndex(nextIndex, { silent: true, state });
        } else {
          this.caseInvestigationVisualizerCurrentVisual = null;
          this.caseInvestigationVisualizerActiveIndex = 0;
          this.caseInvestigationVisualizerLastFlowKey = '';
          this.caseInvestigationVisualizerSelectedNodeId = '';
          this.caseInvestigationVisualizerSelectedNodeIds = [];
          this.caseInvestigationVisualizerSelectedCandidate = null;
          this.caseInvestigationVisualizerHiddenNodeIds = [];
          this.caseInvestigationVisualizerViewport = { x: 0, y: 0, width: 1200, height: 700 };
          this.clearCaseInvestigationVisualizerVisualHistory?.();
          this.resetCaseInvestigationVisualizerInteraction?.({ clearSelection: true });
          this.caseInvestigationVisualizerStatus = state.summary;
        }
        if (!options.silent) this.showCaseToast('Investigation visualizer refreshed.');
        return state;
      } finally {
        this.caseInvestigationVisualizerRefreshRunning = false;
      }
    },

    buildCaseInvestigationVisualizerAllTogether(state = {}) {
      return buildCaseFlowAggregateVisual(Array.isArray(state.flows) ? state.flows : [], state, {
        ...this.getCaseInvestigationVisualizerOptions(),
        aggregateMode: 'allTogether'
      });
    },

    buildCaseInvestigationVisualizerStacked(state = {}) {
      return buildCaseFlowAggregateVisual(Array.isArray(state.flows) ? state.flows : [], state, {
        ...this.getCaseInvestigationVisualizerOptions(),
        aggregateMode: 'allStacked'
      });
    },

    renderCaseInvestigationVisualizerAtIndex(index = 0, options = {}) {
      if (this.caseInvestigationVisualizerIsLocked?.()) {
        this.showCaseInvestigationVisualizerLockedToast();
        return this.caseInvestigationVisualizerCurrentVisual || null;
      }
      const state = options.state || this.caseInvestigationVisualizerState || this.getCaseInvestigationVisualizerState();
      const flows = Array.isArray(state.flows) ? state.flows : [];
      if (!flows.length) {
        this.caseInvestigationVisualizerCurrentVisual = null;
        this.caseInvestigationVisualizerStatus = state.summary || 'No prepared sapling visuals are available.';
        return null;
      }
      const safeIndex = Math.max(0, Math.min(flows.length - 1, Number(index) || 0));
      const mode = options.mode || this.caseInvestigationVisualizerViewMode || 'single';
      if (options.mode) this.caseInvestigationVisualizerViewMode = mode;
      let visual = null;
      if (mode === 'allTogether') {
        visual = this.buildCaseInvestigationVisualizerAllTogether(state);
      } else if (mode === 'stacked') {
        visual = this.buildCaseInvestigationVisualizerStacked(state);
      } else {
        visual = buildCaseFlowVisual(flows[safeIndex], state, this.getCaseInvestigationVisualizerOptions());
      }
      this.caseInvestigationVisualizerActiveIndex = safeIndex;
      this.caseInvestigationVisualizerLastFlowKey = flows[safeIndex]?.key || flows[safeIndex]?.mainLabel || '';
      this.caseInvestigationVisualizerState = state;
      this.caseInvestigationVisualizerCurrentVisual = visual;
      this.caseInvestigationVisualizerViewport = { ...(visual?.viewBox || { x: 0, y: 0, width: 1200, height: 700 }) };
      this.caseInvestigationVisualizerSelectedNodeId = '';
      this.caseInvestigationVisualizerSelectedNodeIds = [];
      this.caseInvestigationVisualizerSelectedCandidate = null;
      this.caseInvestigationVisualizerHiddenNodeIds = [];
      this.resetCaseInvestigationVisualizerInteraction?.({ clearSelection: true });
      this.clearCaseInvestigationVisualizerVisualHistory?.();
      this.caseInvestigationVisualizerStatus = visual?.summary || state.summary;
      if (!options.silent) this.showCaseToast(`Showing ${this.caseInvestigationVisualizerCurrentLabel}.`);
      return visual;
    },

    refreshCurrentCaseInvestigationVisualizer() {
      return this.renderCaseInvestigationVisualizerAtIndex(this.caseInvestigationVisualizerActiveIndex);
    },

    showPreviousCaseInvestigationVisual() {
      if (!this.caseInvestigationVisualizerCanGoPrevious) return false;
      this.renderCaseInvestigationVisualizerAtIndex(this.caseInvestigationVisualizerActiveIndex - 1);
      return true;
    },

    showNextCaseInvestigationVisual() {
      if (!this.caseInvestigationVisualizerCanGoNext) return false;
      this.renderCaseInvestigationVisualizerAtIndex(this.caseInvestigationVisualizerActiveIndex + 1);
      return true;
    },

    caseInvestigationVisualizerNodeClass(node = {}) {
      const selected = this.caseInvestigationVisualizerSelectedNodeIdSet || new Set();
      const classes = caseFlowVisualNodeClass(node, selected.has(node?.id) ? node.id : this.caseInvestigationVisualizerSelectedNodeId)
        .split(/\s+/)
        .filter(Boolean);
      if (this.isCaseInvestigationVisualizerSelectableNode(node)) classes.push('is-selectable');
      if (selected.has(node?.id) && !classes.includes('is-selected')) classes.push('is-selected');
      if (this.caseInvestigationVisualizerSnapGuide?.targetNodeId && this.caseInvestigationVisualizerSnapGuide.targetNodeId === node?.id) classes.push('is-snap-target');
      return Array.from(new Set(classes)).join(' ');
    },

    caseFlowVisualEdgePathForInvestigation(edge = {}) {
      return caseFlowVisualEdgePath(edge, this.caseInvestigationVisualizerNodeMap || new Map());
    },

    isCaseInvestigationVisualizerSelectableNode(node = null) {
      return !!node && ['tx', 'address', 'amount'].includes(node.type);
    },

    isCaseInvestigationVisualizerSelectableNodeId(nodeId = '') {
      if (!nodeId) return false;
      const node = this.caseInvestigationVisualizerNodeMap?.get?.(nodeId)
        || (this.caseInvestigationVisualizerVisibleNodes || []).find(candidate => candidate.id === nodeId)
        || null;
      return this.isCaseInvestigationVisualizerSelectableNode(node);
    },

    uniqueCaseInvestigationVisualizerSelectableNodeIds(nodeIds = []) {
      const seen = new Set();
      const output = [];
      (Array.isArray(nodeIds) ? nodeIds : []).forEach((nodeId) => {
        const id = normalizeCell(nodeId);
        if (!id || seen.has(id) || !this.isCaseInvestigationVisualizerSelectableNodeId(id)) return;
        seen.add(id);
        output.push(id);
      });
      return output;
    },

    setCaseInvestigationVisualizerSelection(nodeIds = []) {
      const ids = this.uniqueCaseInvestigationVisualizerSelectableNodeIds(nodeIds);
      this.caseInvestigationVisualizerSelectedNodeIds = ids;
      this.caseInvestigationVisualizerSelectedNodeId = ids[0] || '';
      if (ids.length === 1) {
        const node = this.caseInvestigationVisualizerNodeMap?.get?.(ids[0]) || null;
        this.caseInvestigationVisualizerSelectedCandidate = this.findPathOperationCandidateForInvestigationVisualizerNode(node);
      } else {
        this.caseInvestigationVisualizerSelectedCandidate = null;
      }
      return ids;
    },

    getCaseInvestigationVisualizerRowIndexesForNode(node = null) {
      const rows = [];
      const pushRowNumber = (value) => {
        const number = Number.parseInt(String(value ?? '').replace(/[^0-9]/g, ''), 10);
        if (Number.isFinite(number) && number > 0 && !rows.includes(number - 1)) rows.push(number - 1);
      };
      const targetNode = node || null;
      if (!targetNode) return rows;
      if (Number.isInteger(targetNode.rowIndex)) rows.push(targetNode.rowIndex);
      if (Number.isFinite(Number(targetNode.spreadsheetRowNumber))) pushRowNumber(targetNode.spreadsheetRowNumber);
      if (Array.isArray(targetNode.rowNumbers)) targetNode.rowNumbers.forEach(pushRowNumber);
      if (targetNode.sourceRows) String(targetNode.sourceRows).split(/[,;\s]+/).forEach(pushRowNumber);
      (Array.isArray(targetNode.detailRows) ? targetNode.detailRows : []).forEach((row) => {
        const label = normalizeCell(row?.[0]).toLowerCase();
        if (label === 'spreadsheet rows' || label === 'row' || label === 'rows') {
          String(row?.[1] || '').split(/[,;\s]+/).forEach(pushRowNumber);
        }
      });
      if (rows.length === 0 && targetNode.ownerTxGroupId) {
        const owner = (this.caseInvestigationVisualizerNodes || []).find(candidate => candidate.id === targetNode.ownerTxGroupId || candidate.originalNodeId === targetNode.ownerTxGroupId);
        if (owner && owner !== targetNode) {
          this.getCaseInvestigationVisualizerRowIndexesForNode(owner).forEach(rowIndex => {
            if (!rows.includes(rowIndex)) rows.push(rowIndex);
          });
        }
      }
      return rows.sort((a, b) => a - b);
    },

    findPathOperationCandidateForInvestigationVisualizerNode(node = null) {
      const rowIndexes = this.getCaseInvestigationVisualizerRowIndexesForNode(node);
      if (!rowIndexes.length) return null;
      const analysis = buildPathOperationAnalysis(this.getMatrixForLogicalSheet('main'), this.getMatrixForLogicalSheet('raw'));
      for (const rowIndex of rowIndexes) {
        const exact = findExactPathOperationCandidateForRow(analysis, rowIndex);
        if (exact) return exact;
      }
      for (const rowIndex of rowIndexes) {
        const candidate = findPathOperationCandidateForRow(analysis, rowIndex);
        if (candidate) return candidate;
      }
      return null;
    },

    selectCaseInvestigationVisualizerNode(node = null, options = {}) {
      if (!node?.id) return [];
      if (options.append) {
        const current = new Set(this.caseInvestigationVisualizerSelectedNodeIds || []);
        current.add(node.id);
        return this.setCaseInvestigationVisualizerSelection(Array.from(current));
      }
      return this.setCaseInvestigationVisualizerSelection([node.id]);
    },

    onCaseInvestigationVisualizerNodeClick(node = null) {
      if (this.caseInvestigationVisualizerSuppressNextNodeClick) {
        this.caseInvestigationVisualizerSuppressNextNodeClick = false;
        return;
      }
      if (!node?.id) return;
      const selected = this.caseInvestigationVisualizerSelectedNodeIdSet || new Set();
      if (selected.has(node.id) && this.caseInvestigationVisualizerSelectedCount > 1) return;
      this.selectCaseInvestigationVisualizerNode(node);
    },

    clearCaseInvestigationVisualizerSelection() {
      this.caseInvestigationVisualizerSelectedNodeId = '';
      this.caseInvestigationVisualizerSelectedNodeIds = [];
      this.caseInvestigationVisualizerSelectedCandidate = null;
      this.caseInvestigationVisualizerSelectionBox = { active: false, startX: 0, startY: 0, endX: 0, endY: 0 };
    },

    clearCaseInvestigationVisualizerSnapGuide() {
      this.caseInvestigationVisualizerSnapGuide = { active: false, axis: '', x1: 0, y1: 0, x2: 0, y2: 0, targetX: 0, targetY: 0, targetNodeId: '' };
    },

    resetCaseInvestigationVisualizerInteraction(options = {}) {
      this.caseInvestigationVisualizerPointer = { active: false, startX: 0, startY: 0, viewBox: null };
      this.caseInvestigationVisualizerInteraction = { mode: '', pointerId: null };
      this.caseInvestigationVisualizerDraggingNodes = false;
      this.caseInvestigationVisualizerSelectingNodes = false;
      this.caseInvestigationVisualizerPendingViewport = null;
      this.caseInvestigationVisualizerSelectionBox = { active: false, startX: 0, startY: 0, endX: 0, endY: 0 };
      this.clearCaseInvestigationVisualizerSnapGuide();
      this.clearCaseInvestigationVisualizerPanFrame();
      if (options.clearSelection) this.clearCaseInvestigationVisualizerSelection();
    },

    collectCaseInvestigationVisualizerBranchNodeIds(rootId = '') {
      const startId = normalizeCell(rootId);
      if (!startId) return [];
      const edges = Array.isArray(this.caseInvestigationVisualizerCurrentVisual?.edges)
        ? this.caseInvestigationVisualizerCurrentVisual.edges
        : [];
      const hidden = this.caseInvestigationVisualizerHiddenNodeSet || new Set();
      const childrenById = new Map();
      edges.forEach((edge) => {
        const from = normalizeCell(edge?.from);
        const to = normalizeCell(edge?.to);
        if (!from || !to || hidden.has(from) || hidden.has(to)) return;
        if (!childrenById.has(from)) childrenById.set(from, []);
        childrenById.get(from).push(to);
      });
      const collected = [];
      const seen = new Set();
      const stack = [startId];
      while (stack.length) {
        const id = stack.pop();
        if (!id || seen.has(id) || hidden.has(id)) continue;
        seen.add(id);
        collected.push(id);
        (childrenById.get(id) || []).forEach(childId => stack.push(childId));
      }
      return collected;
    },

    hideSelectedCaseInvestigationVisualizerNode(options = {}) {
      return this.deleteSelectedCaseInvestigationVisualizerBranches(options);
    },

    deleteSelectedCaseInvestigationVisualizerBranches(options = {}) {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      const selectedIds = this.uniqueCaseInvestigationVisualizerSelectableNodeIds(this.caseInvestigationVisualizerSelectedNodeIds || []);
      if (!selectedIds.length) return false;
      const before = this.captureCaseInvestigationVisualizerVisualSnapshot('Delete selected visual branch');
      const hidden = new Set(this.caseInvestigationVisualizerHiddenNodeIds || []);
      selectedIds.forEach((id) => {
        const branchIds = options.branch === false ? [id] : this.collectCaseInvestigationVisualizerBranchNodeIds(id);
        branchIds.forEach(nodeId => hidden.add(nodeId));
      });
      const changedCount = hidden.size - (this.caseInvestigationVisualizerHiddenNodeIds || []).length;
      if (changedCount <= 0) return false;
      this.caseInvestigationVisualizerHiddenNodeIds = Array.from(hidden);
      this.clearCaseInvestigationVisualizerSelection();
      this.commitCaseInvestigationVisualizerVisualSnapshot(before, 'Delete selected visual branch');
      this.caseInvestigationVisualizerStatus = `Deleted ${changedCount} visual node${changedCount === 1 ? '' : 's'} from this investigation view. Main Data was not changed; Undo can restore the view.`;
      return true;
    },

    restoreCaseInvestigationVisualizerHiddenNodes() {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      if (!this.caseInvestigationVisualizerHiddenNodeIds?.length) return false;
      const before = this.captureCaseInvestigationVisualizerVisualSnapshot('Restore hidden visual nodes');
      this.caseInvestigationVisualizerHiddenNodeIds = [];
      this.commitCaseInvestigationVisualizerVisualSnapshot(before, 'Restore hidden visual nodes');
      this.caseInvestigationVisualizerStatus = 'Restored all hidden visual nodes in this investigation view.';
      return true;
    },

    resetCaseInvestigationVisualizerViewport() {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      const viewBox = this.caseInvestigationVisualizerCurrentVisual?.viewBox;
      if (!viewBox) return;
      this.caseInvestigationVisualizerViewport = { ...viewBox };
    },

    resetCaseInvestigationVisualizerNodePositions() {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      const state = this.caseInvestigationVisualizerState || this.getCaseInvestigationVisualizerState();
      const flows = Array.isArray(state?.flows) ? state.flows : [];
      if (!flows.length) return false;
      const before = this.captureCaseInvestigationVisualizerVisualSnapshot('Reset visual node positions');
      const safeIndex = Math.max(0, Math.min(flows.length - 1, Number(this.caseInvestigationVisualizerActiveIndex) || 0));
      const mode = this.caseInvestigationVisualizerViewMode || 'single';
      let visual = null;
      if (mode === 'allTogether') {
        visual = this.buildCaseInvestigationVisualizerAllTogether(state);
      } else if (mode === 'stacked') {
        visual = this.buildCaseInvestigationVisualizerStacked(state);
      } else {
        visual = buildCaseFlowVisual(flows[safeIndex], state, this.getCaseInvestigationVisualizerOptions());
      }
      if (!visual) return false;
      this.caseInvestigationVisualizerCurrentVisual = visual;
      this.caseInvestigationVisualizerViewport = { ...(visual.viewBox || { x: 0, y: 0, width: 1200, height: 700 }) };
      this.caseInvestigationVisualizerHiddenNodeIds = [];
      this.resetCaseInvestigationVisualizerInteraction({ clearSelection: true });
      this.commitCaseInvestigationVisualizerVisualSnapshot(before, 'Reset visual node positions');
      this.caseInvestigationVisualizerStatus = 'Reset the current investigation visual to the renderer positions. Main Data was not changed; Undo can restore manual placement.';
      return true;
    },

    clearCaseInvestigationVisualizerVisualHistory() {
      this.caseInvestigationVisualizerUndoStack = [];
      this.caseInvestigationVisualizerRedoStack = [];
    },

    cloneCaseInvestigationVisualizerHistoryValue(value) {
      try {
        return JSON.parse(JSON.stringify(value || null));
      } catch {
        if (Array.isArray(value)) return value.map(item => ({ ...(item || {}) }));
        return value && typeof value === 'object' ? { ...value } : value;
      }
    },

    captureCaseInvestigationVisualizerVisualSnapshot(label = 'Visual edit') {
      const visual = this.caseInvestigationVisualizerCurrentVisual || {};
      return {
        label,
        visualNodes: this.cloneCaseInvestigationVisualizerHistoryValue(visual.nodes || []),
        visualEdges: this.cloneCaseInvestigationVisualizerHistoryValue(visual.edges || []),
        selectedNodeIds: [...(this.caseInvestigationVisualizerSelectedNodeIds || [])],
        selectedNodeId: this.caseInvestigationVisualizerSelectedNodeId || '',
        hiddenNodeIds: [...(this.caseInvestigationVisualizerHiddenNodeIds || [])],
        viewport: { ...(this.caseInvestigationVisualizerViewport || visual.viewBox || { x: 0, y: 0, width: 1200, height: 700 }) },
        status: this.caseInvestigationVisualizerStatus || ''
      };
    },

    commitCaseInvestigationVisualizerVisualSnapshot(snapshot = null, label = 'Visual edit') {
      if (!snapshot) return;
      const entry = { ...snapshot, label };
      this.caseInvestigationVisualizerUndoStack = [...(this.caseInvestigationVisualizerUndoStack || []), entry].slice(-Math.max(1, Number(this.caseInvestigationVisualizerHistoryLimit) || 40));
      this.caseInvestigationVisualizerRedoStack = [];
    },

    restoreCaseInvestigationVisualizerVisualSnapshot(snapshot = null) {
      if (!snapshot || !this.caseInvestigationVisualizerCurrentVisual) return false;
      this.caseInvestigationVisualizerCurrentVisual = {
        ...this.caseInvestigationVisualizerCurrentVisual,
        nodes: this.cloneCaseInvestigationVisualizerHistoryValue(snapshot.visualNodes || []),
        edges: this.cloneCaseInvestigationVisualizerHistoryValue(snapshot.visualEdges || [])
      };
      this.caseInvestigationVisualizerHiddenNodeIds = [...(snapshot.hiddenNodeIds || [])];
      this.caseInvestigationVisualizerViewport = { ...(snapshot.viewport || this.caseInvestigationVisualizerCurrentVisual.viewBox || { x: 0, y: 0, width: 1200, height: 700 }) };
      this.resetCaseInvestigationVisualizerInteraction({ clearSelection: false });
      this.setCaseInvestigationVisualizerSelection(snapshot.selectedNodeIds || (snapshot.selectedNodeId ? [snapshot.selectedNodeId] : []));
      return true;
    },

    undoCaseInvestigationVisualizerVisualEdit() {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      if (!this.caseInvestigationVisualizerCanUndoVisualEdit) return false;
      const current = this.captureCaseInvestigationVisualizerVisualSnapshot('Redo visual edit');
      const stack = [...(this.caseInvestigationVisualizerUndoStack || [])];
      const previous = stack.pop();
      this.caseInvestigationVisualizerUndoStack = stack;
      this.caseInvestigationVisualizerRedoStack = [...(this.caseInvestigationVisualizerRedoStack || []), current].slice(-Math.max(1, Number(this.caseInvestigationVisualizerHistoryLimit) || 40));
      this.restoreCaseInvestigationVisualizerVisualSnapshot(previous);
      this.caseInvestigationVisualizerStatus = `Undid visual edit${previous?.label ? `: ${previous.label}` : ''}. Source data was not changed.`;
      return true;
    },

    redoCaseInvestigationVisualizerVisualEdit() {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      if (!this.caseInvestigationVisualizerCanRedoVisualEdit) return false;
      const current = this.captureCaseInvestigationVisualizerVisualSnapshot('Undo visual edit');
      const stack = [...(this.caseInvestigationVisualizerRedoStack || [])];
      const next = stack.pop();
      this.caseInvestigationVisualizerRedoStack = stack;
      this.caseInvestigationVisualizerUndoStack = [...(this.caseInvestigationVisualizerUndoStack || []), current].slice(-Math.max(1, Number(this.caseInvestigationVisualizerHistoryLimit) || 40));
      this.restoreCaseInvestigationVisualizerVisualSnapshot(next);
      this.caseInvestigationVisualizerStatus = `Redid visual edit${next?.label ? `: ${next.label}` : ''}. Source data was not changed.`;
      return true;
    },

    clearCaseInvestigationVisualizerPanFrame() {
      const frame = this.caseInvestigationVisualizerPanFrame;
      if (!frame) return;
      if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frame);
      if (typeof window.clearTimeout === 'function') window.clearTimeout(frame);
      this.caseInvestigationVisualizerPanFrame = 0;
    },

    getCaseInvestigationVisualizerScreenToSvgMatrix() {
      const svg = this.$refs.caseInvestigationVisualizerSvg;
      if (!svg || typeof svg.getScreenCTM !== 'function') return null;
      try {
        const matrix = svg.getScreenCTM();
        if (!matrix || typeof matrix.inverse !== 'function') return null;
        const inverse = matrix.inverse();
        return {
          a: Number(inverse.a),
          b: Number(inverse.b),
          c: Number(inverse.c),
          d: Number(inverse.d),
          e: Number(inverse.e),
          f: Number(inverse.f)
        };
      } catch {
        return null;
      }
    },

    transformCaseInvestigationVisualizerScreenPoint(x = 0, y = 0, matrix = null) {
      if (!matrix) return null;
      const screenX = Number(x);
      const screenY = Number(y);
      const values = [matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f, screenX, screenY];
      if (!values.every(value => Number.isFinite(value))) return null;
      return {
        x: matrix.a * screenX + matrix.c * screenY + matrix.e,
        y: matrix.b * screenX + matrix.d * screenY + matrix.f
      };
    },

    getCaseInvestigationVisualizerSvgPointFromEvent(event = null) {
      if (!event) return null;
      const matrix = this.getCaseInvestigationVisualizerScreenToSvgMatrix();
      const point = this.transformCaseInvestigationVisualizerScreenPoint(event.clientX, event.clientY, matrix);
      if (point) return point;
      const current = this.caseInvestigationVisualizerViewport || this.caseInvestigationVisualizerCurrentVisual?.viewBox;
      const svg = this.$refs.caseInvestigationVisualizerSvg;
      const rect = svg?.getBoundingClientRect?.();
      if (!current || !rect?.width || !rect?.height) return { x: 0, y: 0 };
      return {
        x: current.x + ((event.clientX - rect.left) / rect.width) * current.width,
        y: current.y + ((event.clientY - rect.top) / rect.height) * current.height
      };
    },

    pointerMovedBeyondCaseInvestigationVisualizerThreshold(startClient = null, event = null, threshold = 4) {
      if (!startClient || !event) return false;
      const dx = Number(event.clientX) - Number(startClient.x);
      const dy = Number(event.clientY) - Number(startClient.y);
      return Math.sqrt((dx * dx) + (dy * dy)) >= threshold;
    },

    captureCaseInvestigationVisualizerPointer(event = null) {
      if (!event?.currentTarget?.setPointerCapture) return;
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Pointer may already be captured or unsupported; interaction still works.
      }
    },

    releaseCaseInvestigationVisualizerPointer(event = null) {
      if (!event?.currentTarget?.releasePointerCapture) return;
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // Pointer may already be released.
      }
    },

    findCaseInvestigationVisualizerEventNodeId(event = null) {
      const target = event?.target;
      if (!target || typeof target.closest !== 'function') return '';
      const nodeElement = target.closest('[data-node-id]');
      const svg = this.$refs.caseInvestigationVisualizerSvg;
      if (!nodeElement || (svg && !svg.contains(nodeElement))) return '';
      return normalizeCell(nodeElement.getAttribute('data-node-id'));
    },

    beginCaseInvestigationVisualizerPan(event, options = {}) {
      event?.preventDefault?.();
      const current = this.caseInvestigationVisualizerViewport || this.caseInvestigationVisualizerCurrentVisual?.viewBox;
      if (!current) return;
      this.clearCaseInvestigationVisualizerPanFrame();
      this.caseInvestigationVisualizerPendingViewport = null;
      const screenToSvgMatrix = this.getCaseInvestigationVisualizerScreenToSvgMatrix();
      this.caseInvestigationVisualizerPointer = {
        active: true,
        startX: event.clientX,
        startY: event.clientY,
        screenToSvgMatrix,
        viewBox: { ...current }
      };
      this.caseInvestigationVisualizerDraggingNodes = false;
      this.caseInvestigationVisualizerSelectingNodes = false;
      this.clearCaseInvestigationVisualizerSnapGuide();
      this.caseInvestigationVisualizerInteraction = {
        mode: 'pan',
        pointerId: event.pointerId,
        startClient: { x: event.clientX, y: event.clientY },
        lastClient: { x: event.clientX, y: event.clientY },
        clickedNodeId: options.clickedNodeId || '',
        shiftPan: !!options.shiftPan,
        moved: false
      };
      this.captureCaseInvestigationVisualizerPointer(event);
    },

    beginCaseInvestigationVisualizerSelection(event) {
      event?.preventDefault?.();
      const start = this.getCaseInvestigationVisualizerSvgPointFromEvent(event);
      this.caseInvestigationVisualizerPointer = { active: false, startX: 0, startY: 0, viewBox: null };
      this.caseInvestigationVisualizerSelectingNodes = true;
      this.caseInvestigationVisualizerDraggingNodes = false;
      this.clearCaseInvestigationVisualizerSnapGuide();
      this.caseInvestigationVisualizerSelectionBox = {
        active: true,
        startX: start.x,
        startY: start.y,
        endX: start.x,
        endY: start.y
      };
      this.caseInvestigationVisualizerInteraction = {
        mode: 'select',
        pointerId: event.pointerId,
        startClient: { x: event.clientX, y: event.clientY },
        initialSelectedNodeIds: [...(this.caseInvestigationVisualizerSelectedNodeIds || [])],
        moved: false
      };
      this.captureCaseInvestigationVisualizerPointer(event);
    },

    beginCaseInvestigationVisualizerNodeDrag(event, nodeId = '') {
      event?.preventDefault?.();
      const node = this.caseInvestigationVisualizerNodeMap?.get?.(nodeId) || null;
      if (!this.isCaseInvestigationVisualizerSelectableNode(node)) return;
      const selected = this.caseInvestigationVisualizerSelectedNodeIdSet || new Set();
      const dragIds = selected.has(nodeId) && this.caseInvestigationVisualizerSelectedNodeIds?.length
        ? this.caseInvestigationVisualizerSelectedNodeIds
        : [nodeId];
      this.setCaseInvestigationVisualizerSelection(dragIds);
      const startWorld = this.getCaseInvestigationVisualizerSvgPointFromEvent(event);
      const nodeStartPositions = {};
      (this.caseInvestigationVisualizerSelectedNodes || []).forEach((selectedNode) => {
        nodeStartPositions[selectedNode.id] = { x: Number(selectedNode.x) || 0, y: Number(selectedNode.y) || 0 };
      });
      this.caseInvestigationVisualizerPointer = { active: false, startX: 0, startY: 0, viewBox: null };
      this.caseInvestigationVisualizerSelectingNodes = false;
      this.caseInvestigationVisualizerDraggingNodes = true;
      this.caseInvestigationVisualizerInteraction = {
        mode: 'dragNodes',
        pointerId: event.pointerId,
        startClient: { x: event.clientX, y: event.clientY },
        startWorld,
        nodeStartPositions,
        draggedNodeId: nodeId,
        preDragSnapshot: this.captureCaseInvestigationVisualizerVisualSnapshot('Move selected node placement'),
        snapUsed: false,
        moved: false
      };
      this.captureCaseInvestigationVisualizerPointer(event);
    },

    onCaseInvestigationVisualizerPointerDown(event) {
      if (this.caseInvestigationVisualizerIsLocked?.()) return;
      if (!this.caseInvestigationVisualizerCurrentVisual) return;
      const isRightButton = event.button === 2;
      const isLeftButton = event.button === 0 || event.button === undefined;
      if (isRightButton) {
        this.beginCaseInvestigationVisualizerSelection(event);
        return;
      }
      if (!isLeftButton) return;
      const nodeId = this.findCaseInvestigationVisualizerEventNodeId(event);
      if (event.shiftKey || !nodeId || !this.isCaseInvestigationVisualizerSelectableNodeId(nodeId)) {
        this.beginCaseInvestigationVisualizerPan(event, { clickedNodeId: nodeId || '', shiftPan: !!event.shiftKey });
        return;
      }
      this.beginCaseInvestigationVisualizerNodeDrag(event, nodeId);
    },

    continueCaseInvestigationVisualizerPan(event, state = {}) {
      const pointer = this.caseInvestigationVisualizerPointer || {};
      if (!pointer.active) return;
      const currentViewBox = this.caseInvestigationVisualizerViewport || this.caseInvestigationVisualizerCurrentVisual?.viewBox || pointer.viewBox;
      if (!currentViewBox) return;

      const lastClient = state.lastClient || { x: pointer.startX, y: pointer.startY };
      const dxClient = Number(event.clientX) - Number(lastClient.x);
      const dyClient = Number(event.clientY) - Number(lastClient.y);
      state.lastClient = { x: event.clientX, y: event.clientY };
      if (!Number.isFinite(dxClient) || !Number.isFinite(dyClient) || (dxClient === 0 && dyClient === 0)) return;

      let dx = 0;
      let dy = 0;
      const matrix = pointer.screenToSvgMatrix || null;
      if (matrix && [matrix.a, matrix.b, matrix.c, matrix.d].every(value => Number.isFinite(value))) {
        dx = (matrix.a * dxClient) + (matrix.c * dyClient);
        dy = (matrix.b * dxClient) + (matrix.d * dyClient);
      } else {
        const svg = this.$refs.caseInvestigationVisualizerSvg;
        const rect = svg?.getBoundingClientRect?.();
        if (!rect?.width || !rect?.height) return;
        dx = (dxClient / rect.width) * currentViewBox.width;
        dy = (dyClient / rect.height) * currentViewBox.height;
      }

      state.moved = state.moved || this.pointerMovedBeyondCaseInvestigationVisualizerThreshold(state.startClient, event);
      if (!Number.isFinite(dx) || !Number.isFinite(dy)) return;

      this.caseInvestigationVisualizerViewport = {
        ...currentViewBox,
        x: (Number(currentViewBox.x) || 0) - dx,
        y: (Number(currentViewBox.y) || 0) - dy
      };
      this.caseInvestigationVisualizerPendingViewport = null;
    },


    continueCaseInvestigationVisualizerSelection(event, state = {}) {
      const current = this.getCaseInvestigationVisualizerSvgPointFromEvent(event);
      state.moved = state.moved || this.pointerMovedBeyondCaseInvestigationVisualizerThreshold(state.startClient, event, 5);
      this.caseInvestigationVisualizerSelectionBox = {
        ...this.caseInvestigationVisualizerSelectionBox,
        active: true,
        endX: current.x,
        endY: current.y
      };
      if (state.moved) {
        this.setCaseInvestigationVisualizerSelection(this.caseInvestigationVisualizerNodeIdsInsideSelectionRect(this.caseInvestigationVisualizerSelectionBoxRect));
      }
    },

    continueCaseInvestigationVisualizerNodeDrag(event, state = {}) {
      const current = this.getCaseInvestigationVisualizerSvgPointFromEvent(event);
      if (!current || !state.startWorld) return;
      let dx = current.x - state.startWorld.x;
      let dy = current.y - state.startWorld.y;
      state.moved = state.moved || this.pointerMovedBeyondCaseInvestigationVisualizerThreshold(state.startClient, event, 3);
      const draggedIds = Object.keys(state.nodeStartPositions || {});
      const canAxisSnap = (event.ctrlKey || event.metaKey) && draggedIds.length === 1;
      if (canAxisSnap) {
        const dragNodeId = draggedIds[0];
        const start = state.nodeStartPositions[dragNodeId];
        const snap = this.computeCaseInvestigationVisualizerAxisSnap(dragNodeId, {
          x: start.x + dx,
          y: start.y + dy
        }, new Set(draggedIds));
        if (snap?.active) {
          dx = snap.x - start.x;
          dy = snap.y - start.y;
          this.caseInvestigationVisualizerSnapGuide = snap.guide;
          state.snapUsed = true;
        } else {
          this.clearCaseInvestigationVisualizerSnapGuide();
        }
      } else {
        this.clearCaseInvestigationVisualizerSnapGuide();
      }
      Object.entries(state.nodeStartPositions || {}).forEach(([nodeId, start]) => {
        const node = this.caseInvestigationVisualizerNodeMap?.get?.(nodeId)
          || (this.caseInvestigationVisualizerCurrentVisual?.nodes || []).find(candidate => candidate.id === nodeId);
        if (!node) return;
        node.x = start.x + dx;
        node.y = start.y + dy;
      });
    },

    onCaseInvestigationVisualizerPointerMove(event) {
      const state = this.caseInvestigationVisualizerInteraction || {};
      if (!state.mode || state.pointerId !== event.pointerId) return;
      if (state.mode === 'pan') {
        this.continueCaseInvestigationVisualizerPan(event, state);
        return;
      }
      if (state.mode === 'select') {
        this.continueCaseInvestigationVisualizerSelection(event, state);
        return;
      }
      if (state.mode === 'dragNodes') {
        this.continueCaseInvestigationVisualizerNodeDrag(event, state);
      }
    },

    onCaseInvestigationVisualizerPointerUp(event) {
      const state = this.caseInvestigationVisualizerInteraction || {};
      if (!state.mode || (event && state.pointerId !== event.pointerId)) return;
      if (state.mode === 'pan') {
        const clickedEmptySpace = !state.moved && !state.clickedNodeId && !state.shiftPan;
        if (clickedEmptySpace) this.clearCaseInvestigationVisualizerSelection();
        if (state.moved && state.clickedNodeId) this.caseInvestigationVisualizerSuppressNextNodeClick = true;
        if (this.caseInvestigationVisualizerPendingViewport) {
          this.caseInvestigationVisualizerViewport = this.caseInvestigationVisualizerPendingViewport;
          this.caseInvestigationVisualizerPendingViewport = null;
        }
      } else if (state.mode === 'select') {
        if (state.moved) {
          this.setCaseInvestigationVisualizerSelection(this.caseInvestigationVisualizerNodeIdsInsideSelectionRect(this.caseInvestigationVisualizerSelectionBoxRect));
        } else {
          this.setCaseInvestigationVisualizerSelection(state.initialSelectedNodeIds || []);
        }
      } else if (state.mode === 'dragNodes') {
        if (state.moved) {
          this.commitCaseInvestigationVisualizerVisualSnapshot(state.preDragSnapshot, 'Move selected node placement');
          const movedCount = Object.keys(state.nodeStartPositions || {}).length;
          const snapText = state.snapUsed ? ' Ctrl/Cmd-assisted axis snapping was applied.' : '';
          this.caseInvestigationVisualizerStatus = `Moved ${movedCount} selected visual node${movedCount === 1 ? '' : 's'}.${snapText} Main Data was not changed; Undo can restore the placement.`;
          this.caseInvestigationVisualizerSuppressNextNodeClick = true;
        }
      }
      this.releaseCaseInvestigationVisualizerPointer(event);
      this.resetCaseInvestigationVisualizerInteraction({ clearSelection: false });
    },

    computeCaseInvestigationVisualizerAxisSnap(nodeId = '', proposed = null, ignoredIds = new Set()) {
      if (!nodeId || !proposed) return null;
      const tolerance = this.caseInvestigationVisualizerAlignmentSnapToleranceWorld();
      const candidates = (this.caseInvestigationVisualizerVisibleNodes || [])
        .filter(node => this.isCaseInvestigationVisualizerSelectableNode(node) && node.id !== nodeId && !ignoredIds.has(node.id));
      let best = null;
      candidates.forEach((candidate) => {
        const xDistance = Math.abs((Number(proposed.x) || 0) - (Number(candidate.x) || 0));
        const yDistance = Math.abs((Number(proposed.y) || 0) - (Number(candidate.y) || 0));
        if (xDistance <= tolerance) {
          const option = { axis: 'x', score: xDistance, target: candidate };
          if (!best || option.score < best.score) best = option;
        }
        if (yDistance <= tolerance) {
          const option = { axis: 'y', score: yDistance, target: candidate };
          if (!best || option.score < best.score) best = option;
        }
      });
      if (!best?.target) return null;
      const snapped = {
        x: best.axis === 'x' ? best.target.x : proposed.x,
        y: best.axis === 'y' ? best.target.y : proposed.y
      };
      return {
        active: true,
        axis: best.axis,
        x: snapped.x,
        y: snapped.y,
        targetNodeId: best.target.id,
        guide: this.makeCaseInvestigationVisualizerSnapGuide(best.axis, best.target, snapped)
      };
    },

    caseInvestigationVisualizerAlignmentSnapToleranceWorld() {
      const svg = this.$refs.caseInvestigationVisualizerSvg;
      const rect = svg?.getBoundingClientRect?.();
      const viewBox = this.caseInvestigationVisualizerViewport || this.caseInvestigationVisualizerCurrentVisual?.viewBox;
      if (!rect?.width || !rect?.height || !viewBox?.width || !viewBox?.height) return 44;
      const worldPerScreenPixel = Math.max(viewBox.width / rect.width, viewBox.height / rect.height);
      return Math.max(22, Math.min(90, worldPerScreenPixel * 28));
    },

    makeCaseInvestigationVisualizerSnapGuide(axis = '', targetNode = {}, snappedPoint = {}) {
      const targetX = Number(targetNode?.x) || 0;
      const targetY = Number(targetNode?.y) || 0;
      const snappedX = Number(snappedPoint?.x) || 0;
      const snappedY = Number(snappedPoint?.y) || 0;
      const padding = 180;
      if (axis === 'x') {
        return {
          active: true,
          axis,
          x1: targetX,
          y1: Math.min(targetY, snappedY) - padding,
          x2: targetX,
          y2: Math.max(targetY, snappedY) + padding,
          targetX,
          targetY,
          targetNodeId: targetNode.id
        };
      }
      return {
        active: true,
        axis,
        x1: Math.min(targetX, snappedX) - padding,
        y1: targetY,
        x2: Math.max(targetX, snappedX) + padding,
        y2: targetY,
        targetX,
        targetY,
        targetNodeId: targetNode.id
      };
    },

    caseInvestigationVisualizerNodeIdsInsideSelectionRect(rect = null) {
      if (!rect || rect.width <= 0 || rect.height <= 0) return [];
      return (this.caseInvestigationVisualizerVisibleNodes || [])
        .filter(node => this.isCaseInvestigationVisualizerSelectableNode(node) && this.caseInvestigationVisualizerNodeIntersectsRect(node, rect))
        .map(node => node.id);
    },

    caseInvestigationVisualizerNodeIntersectsRect(node = {}, rect = {}) {
      const extents = this.caseInvestigationVisualizerNodeExtents(node);
      const nodeRect = {
        minX: (Number(node.x) || 0) - extents.left,
        maxX: (Number(node.x) || 0) + extents.right,
        minY: (Number(node.y) || 0) - extents.top,
        maxY: (Number(node.y) || 0) + extents.bottom
      };
      const selectionRect = {
        minX: rect.x,
        maxX: rect.x + rect.width,
        minY: rect.y,
        maxY: rect.y + rect.height
      };
      return nodeRect.maxX >= selectionRect.minX &&
        nodeRect.minX <= selectionRect.maxX &&
        nodeRect.maxY >= selectionRect.minY &&
        nodeRect.minY <= selectionRect.maxY;
    },

    caseInvestigationVisualizerNodeExtents(node = {}) {
      if (node.type === 'address') {
        const radius = Number(this.caseFlowVisualAddressRadius) || 34;
        const labelLines = Array.isArray(node.labelLines) ? node.labelLines.length : 0;
        return { left: radius + 110, right: radius + 110, top: radius + 34 + (labelLines * 20), bottom: radius + 26 };
      }
      if (node.type === 'amount') {
        const size = this.caseFlowVisualAmountNodeSize || { width: 146, height: 54 };
        return { left: size.width / 2 + 8, right: size.width / 2 + 8, top: size.height / 2 + 8, bottom: size.height / 2 + 8 };
      }
      if (node.type === 'flowLabel') {
        return { left: (Number(node.boxWidth) || 420) / 2 + 16, right: (Number(node.boxWidth) || 420) / 2 + 16, top: (Number(node.boxHeight) || 56) / 2 + 16, bottom: (Number(node.boxHeight) || 56) / 2 + 16 };
      }
      const size = this.caseFlowVisualTxNodeSize || { width: 230, height: 126 };
      return { left: size.width / 2 + 30, right: size.width / 2 + 30, top: size.height / 2 + 34, bottom: size.height / 2 + 92 };
    },

    zoomCaseInvestigationVisualizer(factor = 1) {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      const current = this.caseInvestigationVisualizerViewport || this.caseInvestigationVisualizerCurrentVisual?.viewBox;
      if (!current) return;
      const safeFactor = Math.max(0.2, Math.min(5, Number(factor) || 1));
      const centerX = current.x + current.width / 2;
      const centerY = current.y + current.height / 2;
      const width = Math.max(320, current.width * safeFactor);
      const height = Math.max(240, current.height * safeFactor);
      this.caseInvestigationVisualizerViewport = {
        x: centerX - width / 2,
        y: centerY - height / 2,
        width,
        height
      };
    },

    onCaseInvestigationVisualizerWheel(event) {
      if (this.caseInvestigationVisualizerIsLocked?.()) return;
      const current = this.caseInvestigationVisualizerViewport || this.caseInvestigationVisualizerCurrentVisual?.viewBox;
      if (!current) return;
      const factor = event.deltaY < 0 ? 0.86 : 1.16;
      const svgPoint = this.getCaseInvestigationVisualizerSvgPointFromEvent(event);
      const svg = this.$refs.caseInvestigationVisualizerSvg;
      const rect = svg?.getBoundingClientRect?.();
      const fallbackAnchorX = rect?.width ? current.x + ((event.clientX - rect.left) / rect.width) * current.width : current.x + current.width / 2;
      const fallbackAnchorY = rect?.height ? current.y + ((event.clientY - rect.top) / rect.height) * current.height : current.y + current.height / 2;
      const anchorX = Number.isFinite(svgPoint?.x) ? svgPoint.x : fallbackAnchorX;
      const anchorY = Number.isFinite(svgPoint?.y) ? svgPoint.y : fallbackAnchorY;
      const width = Math.max(320, current.width * factor);
      const height = Math.max(240, current.height * factor);
      this.caseInvestigationVisualizerViewport = {
        x: anchorX - ((anchorX - current.x) / current.width) * width,
        y: anchorY - ((anchorY - current.y) / current.height) * height,
        width,
        height
      };
    },

    toggleCaseInvestigationVisualizerFullscreen() {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      this.caseInvestigationVisualizerFullscreen = !this.caseInvestigationVisualizerFullscreen;
      this.$nextTick?.(() => {
        const refit = () => this.resetCaseInvestigationVisualizerViewport();
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(refit);
        } else {
          window.setTimeout(refit, 0);
        }
      });
    },

    buildCurrentCaseInvestigationVisualizerSvgPayload() {
      const visual = this.caseInvestigationVisualizerCurrentVisual;
      if (!visual) return null;
      const hidden = new Set(this.caseInvestigationVisualizerHiddenNodeIds || []);
      const nodes = (Array.isArray(visual.nodes) ? visual.nodes : []).filter(node => !hidden.has(node.id));
      const nodeIds = new Set(nodes.map(node => node.id));
      const edges = (Array.isArray(visual.edges) ? visual.edges : []).filter(edge => nodeIds.has(edge.from) && nodeIds.has(edge.to));
      const lookup = nodes.reduce((map, node) => {
        if (node?.id) map[node.id] = node;
        return map;
      }, {});
      return { ...visual, nodes, edges, lookup };
    },

    serializedCurrentCaseInvestigationVisualizerSvg() {
      const visual = this.buildCurrentCaseInvestigationVisualizerSvgPayload();
      if (!visual) return '';
      return serializeCaseFlowVisualSvg(visual, {
        title: `Case Buddy Investigation Visual ${visual.mainLabel || ''}`,
        selectedNodeId: this.caseInvestigationVisualizerSelectedNodeId
      });
    },

    downloadCurrentCaseInvestigationVisualizerSvg() {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      if (this.caseInvestigationVisualizerDownloading) return false;
      const svgText = this.serializedCurrentCaseInvestigationVisualizerSvg();
      if (!svgText) {
        this.showCaseToast('No current investigation visual is available to download.');
        return false;
      }
      this.caseInvestigationVisualizerDownloading = true;
      try {
        const mode = this.caseInvestigationVisualizerViewMode || 'single';
        const flow = this.caseInvestigationVisualizerCurrentFlow || {};
        const label = mode === 'allTogether' ? 'all_together' : mode === 'stacked' ? 'stacked' : (flow.mainLabel || `flow_${this.caseInvestigationVisualizerActiveIndex + 1}`);
        const filename = `case-buddy-investigation-${label}.svg`.replace(/[^a-z0-9._-]+/gi, '_');
        downloadCaseFlowVisualBlob(svgText, filename, 'image/svg+xml;charset=utf-8');
        this.caseInvestigationVisualizerStatus = 'Investigation visual SVG downloaded. Final report visuals were not marked complete.';
        this.showCaseToast(this.caseInvestigationVisualizerStatus);
        return true;
      } finally {
        this.caseInvestigationVisualizerDownloading = false;
      }
    },

    openPathOperationFromInvestigationVisualizer(action = 'prune') {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      const normalizedAction = ['prune', 'graft', 'bridge'].includes(action) ? action : 'prune';
      const candidate = this.caseInvestigationVisualizerSelectedCandidate;
      if (!candidate) {
        this.showCaseToast('Select a transaction/output node that maps to a qualified Main Data path-operation row.');
        return false;
      }
      if (normalizedAction !== 'prune' && !candidate.isPathEnding) {
        this.showCaseToast('Graft and Bridge graft require a selected Path ends row.');
        return false;
      }
      this.caseInvestigationVisualizerFullscreen = false;
      this.showCaseInvestigationVisualizerModal = false;
      return this.openPathOperationModal({ candidateId: candidate.id, action: normalizedAction, source: 'case-investigation-visualizer' });
    },

    selectInvestigationVisualizerCandidateInMainData() {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      const candidate = this.caseInvestigationVisualizerSelectedCandidate;
      if (!candidate || !Number.isInteger(candidate.rowIndex)) return false;
      this.activateLogicalSheet('main');
      this.$nextTick(() => {
        const sheet = this.getInputSheet?.();
        sheet?.selectCell?.(candidate.rowIndex, 0);
        sheet?.hotInstance?.scrollViewportTo?.(candidate.rowIndex, 0, true, true);
      });
      return true;
    },

    resetCaseHopSolverPlaceholderState(options = {}) {
      if ((this.caseHopSolverPlaceholderRunning || this.caseInvestigationHopSolverPlaceholder?.busy) && !options.force) return false;
      if (options.close !== false) this.showCaseHopSolverPlaceholderModal = false;
      this.caseHopSolverPlaceholderCandidate = null;
      this.caseHopSolverPlaceholderStatus = '';
      this.caseHopSolverPlaceholderRunning = false;
      this.caseHopSolverPlaceholderStopRequested = false;
      this.caseInvestigationHopSolverPlaceholder = {
        open: false,
        busy: false,
        stopRequested: false,
        status: '',
        detail: '',
        candidateId: '',
        candidateSnapshot: null,
        startedAtUtc: '',
        stoppedAtUtc: '',
        completedAtUtc: ''
      };
      return true;
    },

    openCaseHopSolverPlaceholderFromInvestigationVisualizer() {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      const candidate = this.caseInvestigationVisualizerSelectedCandidate;
      if (!candidate) {
        this.showCaseToast('Select a Path ends transaction/output node before opening Solve hop.');
        return false;
      }
      if (!candidate.isPathEnding) {
        this.showCaseToast('Solve hop is reserved for selected Path ends rows. Use Prune for non-ending outputs.');
        return false;
      }
      const candidateSnapshot = {
        id: normalizeCell(candidate.id),
        rowIndex: Number.isInteger(candidate.rowIndex) ? candidate.rowIndex : null,
        spreadsheetRowNumber: candidate.spreadsheetRowNumber || (Number.isInteger(candidate.rowIndex) ? candidate.rowIndex + 1 : ''),
        pathLabel: normalizeCell(candidate.pathLabel),
        hash: normalizeCell(candidate.hash),
        shortHash: normalizeCell(candidate.shortHash),
        toAddress: normalizeCell(candidate.toAddress),
        utcDate: normalizeCell(candidate.utcDate),
        amount: normalizeCell(candidate.amount),
        token: normalizeCell(candidate.token),
        fundsFollowed: normalizeCell(candidate.fundsFollowed),
        leftBoundsDisplay: normalizeCell(candidate.leftBoundsDisplay),
        leftBoundsNumeric: normalizeCell(candidate.leftBoundsNumeric),
        continuesAs: normalizeCell(candidate.continuesAs),
        chainId: normalizeCell(candidate.chainId),
        chainName: normalizeCell(candidate.chainName),
        hopLogic: normalizeCell(candidate.hopLogic),
        isPathEnding: !!candidate.isPathEnding
      };
      const now = new Date().toISOString();
      this.caseHopSolverPlaceholderCandidate = candidateSnapshot;
      this.caseHopSolverPlaceholderRunning = false;
      this.caseHopSolverPlaceholderStopRequested = false;
      this.caseHopSolverPlaceholderStatus = 'The in-app hop solver is not connected yet. This placeholder preserves the future action surface and confirms which path ending will be used when the tool is integrated.';
      this.caseInvestigationHopSolverPlaceholder = {
        open: true,
        busy: false,
        stopRequested: false,
        status: 'Placeholder only',
        detail: this.caseHopSolverPlaceholderStatus,
        candidateId: candidateSnapshot.id,
        candidateSnapshot,
        startedAtUtc: '',
        stoppedAtUtc: '',
        completedAtUtc: now
      };
      this.showCaseHopSolverPlaceholderModal = true;
      return true;
    },

    startCaseHopSolverPlaceholderPreviewRun() {
      const candidate = this.caseHopSolverPlaceholderCandidate;
      if (!candidate?.isPathEnding) {
        this.caseHopSolverPlaceholderStatus = 'Select a valid Path ends row before previewing the solve-hop running state.';
        return false;
      }
      const startedAtUtc = new Date().toISOString();
      this.caseHopSolverPlaceholderRunning = true;
      this.caseHopSolverPlaceholderStopRequested = false;
      this.caseHopSolverPlaceholderStatus = 'Previewing the future running state. Visualizer navigation and visual edits are paused until Request stop is pressed. No data lookup is running and Main Data / Raw Data will not change.';
      this.caseInvestigationHopSolverPlaceholder = {
        ...(this.caseInvestigationHopSolverPlaceholder || {}),
        open: true,
        busy: true,
        stopRequested: false,
        status: 'Preview running lock',
        detail: this.caseHopSolverPlaceholderStatus,
        candidateId: candidate.id || this.caseInvestigationHopSolverPlaceholder?.candidateId || '',
        candidateSnapshot: candidate,
        startedAtUtc,
        stoppedAtUtc: '',
        completedAtUtc: ''
      };
      this.showCaseToast('Solve-hop placeholder lock is active. Press Request stop to release it.');
      return true;
    },

    requestCaseHopSolverPlaceholderStop() {
      if (!this.caseHopSolverPlaceholderRunning && !this.caseInvestigationHopSolverPlaceholder?.busy) {
        this.caseHopSolverPlaceholderStatus = 'No solve-hop worker is running yet. The stop control will become active when the real solver is connected.';
        return false;
      }
      const stoppedAtUtc = new Date().toISOString();
      this.caseHopSolverPlaceholderRunning = false;
      this.caseHopSolverPlaceholderStopRequested = true;
      this.caseInvestigationHopSolverPlaceholder = {
        ...(this.caseInvestigationHopSolverPlaceholder || {}),
        busy: false,
        stopRequested: true,
        status: 'Stop requested',
        stoppedAtUtc,
        completedAtUtc: stoppedAtUtc
      };
      this.caseHopSolverPlaceholderStatus = 'Stop requested. The preview lock has been released. When the real solver is connected, this control should halt safely and return any partial raw/review data without modifying Main Data.';
      this.caseInvestigationVisualizerStatus = 'Solve Hop placeholder stopped. Visualizer controls are available again.';
      this.showCaseToast('Solve Hop placeholder stopped.');
      return true;
    },

    cancelCaseHopSolverPlaceholder() {
      if (this.caseHopSolverPlaceholderRunning || this.caseInvestigationHopSolverPlaceholder?.busy) {
        this.showCaseToast('The solve-hop placeholder is marked active. Request stop before closing.');
        return false;
      }
      return this.resetCaseHopSolverPlaceholderState({ close: true });
    },

    getCaseFullReportPackagePayloadCacheKey(options = {}) {
      const includeDeliverableViews = options.includeDeliverableViews !== false;
      const workflowState = this.caseSupportState.workflowState || {};
      const deliverableStateById = this.caseSupportState.deliverableStateById || {};
      const pickReview = (record = {}) => ({
        revision: Number.isFinite(Number(record?.revision)) ? Number(record.revision) : null,
        reviewedAtUtc: normalizeCell(record?.reviewedAtUtc || record?.confirmedAtUtc || ''),
        basisHash: normalizeCell(record?.basisHash || record?.reviewedBasisHash || record?.confirmedReviewBasisHash || record?.builtBasisHash || ''),
        rendererVersion: normalizeCell(record?.rendererVersion || '')
      });
      const pickDeliverable = (id) => {
        const record = deliverableStateById?.[id] || {};
        return {
          builtRevision: Number.isFinite(Number(record.builtRevision)) ? Number(record.builtRevision) : null,
          builtAtUtc: normalizeCell(record.builtAtUtc),
          reviewedAtUtc: normalizeCell(record.reviewedAtUtc),
          basisHash: normalizeCell(record.basisHash),
          rendererVersion: normalizeCell(record.rendererVersion)
        };
      };
      const visualsSignal = workflowState.signalCompleteByCode?.[WORKFLOW_SIGNAL_CASE_FLOW_VISUALS_CREATED] || {};
      return JSON.stringify({
        renderer: CASE_FULL_REPORT_PACKAGE_RENDERER_VERSION,
        caseId: normalizeCell(this.caseDisplayId || this.currentProjectFileId),
        caseName: normalizeCell(this.caseDisplayName),
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        includeDeliverableViews,
        reportBody: pickReview(workflowState.reportBodyReview),
        addendum: pickReview(workflowState.addendumBuildReview),
        addendum55: pickReview(workflowState.addendumPathEndingReview),
        addendum48: pickReview(workflowState.addendumRecipientEntityReview),
        legend: pickReview(workflowState.addressLegendReview),
        visuals: {
          signalRevision: Number.isFinite(Number(visualsSignal.revision)) ? Number(visualsSignal.revision) : null,
          completedAtUtc: normalizeCell(visualsSignal.completedAtUtc),
          source: normalizeCell(visualsSignal.source)
        },
        fullPackageReview: pickReview(workflowState.fullReportPackageReview),
        deliverables: [
          'suspicious_address_table',
          'exchange_address_table',
          'dormant_address_table',
          'dormant_address_monitoring_view',
          'what_went_where',
          'what_went_where_valuation_audit',
          'conclusion',
          'report_body',
          'addendum',
          'case_flow_visuals',
          'one_click_report_package'
        ].reduce((accumulator, id) => {
          accumulator[id] = pickDeliverable(id);
          return accumulator;
        }, {})
      });
    },

    getReusableCaseFullReportPackagePayload(options = {}) {
      if (options.forceRebuild) return null;
      const payload = this.caseFullReportPackagePayload;
      if (!payload || typeof payload !== 'object') return null;
      const cacheKey = this.getCaseFullReportPackagePayloadCacheKey(options);
      if (this.caseFullReportPackagePayloadCacheKey !== cacheKey) return null;
      if (Number(payload?.state?.caseDataRevision) !== (Number(this.caseSupportState.caseDataRevision) || 0)) return null;
      if (payload.rendererVersion !== CASE_FULL_REPORT_PACKAGE_RENDERER_VERSION) return null;
      return payload;
    },

    setCaseFullReportPackagePayloadCache(payload = null, options = {}) {
      this.caseFullReportPackagePayload = payload;
      this.caseFullReportPackagePayloadCacheKey = payload ? this.getCaseFullReportPackagePayloadCacheKey(options) : '';
      return payload;
    },

    getCaseFullReportPackageDefinitionsForSummary() {
      return CASE_DELIVERABLE_GROUPS
        .flatMap(group => group.items)
        .filter(item => item.id !== 'one_click_report_package' && item.id !== 'case_completion_summary');
    },

    getCaseFullReportPackageInputs(options = {}) {
      const mainData = this.getMatrixForLogicalSheet('main');
      const rawData = this.getMatrixForLogicalSheet('raw');
      const addressLegendAnalysis = options.addressLegendAnalysis || buildCaseAddressLegendAnalysis(
        mainData,
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        { labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately }
      );
      const whatWentWhereState = options.whatWentWhereState || this.getWhatWentWhereState();
      const caseConclusionState = options.caseConclusionState || this.getCaseConclusionState({ whatWentWhereState });
      const reportBodyState = options.reportBodyState || this.getReportBodyState({ legendAnalysis: addressLegendAnalysis });
      const caseAddendumState = options.caseAddendumState || this.getCaseAddendumState({ addressLegendAnalysis });
      const suspiciousAddressTableState = options.suspiciousAddressTableState || this.getSuspiciousAddressTableState();
      const exchangeAddressTableState = options.exchangeAddressTableState || this.getExchangeAddressTableState();
      const dormantAddressState = options.dormantAddressState || this.getDormantAddressDeliverablesState();
      const visualsState = options.visualsState || this.getCaseFlowVisualsState();
      const deliverableViews = options.includeDeliverableViews === false
        ? []
        : this.getCaseFullReportPackageDefinitionsForSummary().map(definition => this.buildDeliverableView(definition));
      let addressLegendRows = [];
      try {
        const storedEntries = Array.isArray(this.caseSupportState.workflowState?.addressLegendReview?.entries)
          ? this.caseSupportState.workflowState.addressLegendReview.entries
          : [];
        addressLegendRows = addressLegendAnalysis.buildIsCurrent && storedEntries.length > 0
          ? buildCaseAddressLegendQuickAccessRows(addressLegendAnalysis, { entries: storedEntries })
          : buildCaseAddressLegendQuickAccessRows(addressLegendAnalysis);
      } catch {
        addressLegendRows = [];
      }
      return {
        caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
        caseDisplayId: this.caseDisplayId,
        caseDisplayName: this.caseDisplayName,
        caseName: this.caseDetails?.caseName || '',
        caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        caseSummary: this.caseSummary || {},
        mainData,
        rawData,
        mainRowCount: normalizeCaseMatrix(mainData).filter(rowHasMeaningfulData).length,
        rawRowCount: normalizeCaseMatrix(rawData).filter(rowHasMeaningfulData).length,
        workflowState: this.caseSupportState.workflowState || {},
        reportBodyState,
        addendumState: caseAddendumState,
        conclusionState: caseConclusionState,
        conclusionText: this.caseConclusionDraft || caseConclusionState.selectedText || caseConclusionState.generatedText || '',
        whatWentWhereState,
        visualsState,
        visualOptions: this.getCaseFlowVisualOptions?.() || {},
        embedFlowVisualsInReport: true,
        whatWentWhereRows: buildCaseWhatWentWhereDeliverableRows(whatWentWhereState),
        suspiciousAddressRows: buildCaseSuspiciousAddressTableDeliverableRows(suspiciousAddressTableState),
        exchangeAddressRows: buildCaseExchangeAddressTableDeliverableRows(exchangeAddressTableState),
        dormantAddressRows: buildCaseDormantAddressTableDeliverableRows(dormantAddressState),
        addressLegendRows,
        duplicateHashRows: buildDuplicateHashTableRows(buildCaseDuplicateHashLabelerAnalysis(
          mainData,
          this.caseSupportState.workflowState || {},
          { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
        )),
        deliverableViews
      };
    },

    getCaseFullReportPackageState(options = {}) {
      const reusable = this.getReusableCaseFullReportPackagePayload?.(options);
      if (reusable?.state && !options.forceRebuild) return reusable.state;
      const cacheKey = this.getCaseComputationCacheKey?.('full-report-package-state', {
        payloadBasis: this.getCaseFullReportPackagePayloadCacheKey?.(options) || '',
        includeDeliverableViews: options.includeDeliverableViews !== false
      }) || '';
      return this.getCachedCaseComputation?.('state', cacheKey, () => buildCaseFullReportPackageState(this.getCaseFullReportPackageInputs(options))) ||
        buildCaseFullReportPackageState(this.getCaseFullReportPackageInputs(options));
    },

    getCaseFullReportPackagePayload(options = {}) {
      const reusable = this.getReusableCaseFullReportPackagePayload(options);
      if (reusable) return reusable;
      const inputs = this.getCaseFullReportPackageInputs(options);
      const state = options.state || buildCaseFullReportPackageState(inputs);
      const payload = buildCaseFullReportPackagePayload({ ...inputs, state });
      return this.setCaseFullReportPackagePayloadCache(payload, options);
    },

    buildCaseCompletionSummaryRows(options = {}) {
      const inputs = this.getCaseFullReportPackageInputs({ includeDeliverableViews: true, ...(options || {}) });
      const state = buildCaseFullReportPackageState(inputs);
      const text = buildCaseCompletionSummaryText(inputs, state);
      return [
        ['Case Completion Summary', this.caseDisplayName, '', '', '', '', '', '', '', 'Preview/audit only. Built from current workflow and deliverable state.'],
        ['Renderer', CASE_FULL_REPORT_PACKAGE_RENDERER_VERSION, 'Basis', state.currentBasisHash || '', 'Ready', state.isReady ? 'Yes' : 'No', 'Review Current', state.reviewIsCurrent ? 'Yes' : 'No'],
        [],
        ...String(text || '').split('\n').map(line => [line])
      ];
    },

    stageCaseCompletionSummaryPreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging completion summary…',
          message: 'Building preview rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Completion summary staged.'
        }, () => this.stageCaseCompletionSummaryPreview({ ...options, __caseOperationWrapped: true }));
      }
      const rows = this.buildCaseCompletionSummaryRows();
      this.loadOutputRows(rows, {
        title: 'Case completion summary preview',
        detail: 'Final workflow and deliverable completion summary staged for review. Main Data is not modified.',
        sourceActionId: 'case_completion_summary',
        sourceDeliverableId: 'case_completion_summary',
        target: 'deliverable-preview'
      });
      const definition = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(item => item.id === 'case_completion_summary');
      if (definition) this.markDeliverableBuilt(definition);
      if (!options.silent) {
        this.scrollToSection('case-output');
        this.showCaseToast('Case completion summary staged in Output/Review.');
      }
      return true;
    },

    downloadCaseCompletionSummaryText(options = {}) {
      const text = this.buildCaseCompletionSummaryRows().map(row => Array.isArray(row) ? row.join('\t') : '').join('\n');
      downloadTextFile('case_completion_summary.txt', text, 'text/plain;charset=utf-8;');
      const definition = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(item => item.id === 'case_completion_summary');
      if (definition) this.markDeliverableBuilt(definition);
      if (!options.silent) this.showCaseToast('Case completion summary downloaded.');
      return true;
    },

    buildCaseFullReportPackageCheckResult(state = null) {
      const packageState = state || this.getCaseFullReportPackageState({ includeDeliverableViews: false });
      if (!packageState.isReady) {
        return this.buildCheckResult('full_report_package', 'fail', packageState.summary || 'Full report package prerequisites are not current.', packageState);
      }
      if (!packageState.reviewIsCurrent) {
        return this.buildCheckResult('full_report_package', 'warn', 'Full report package is ready, but it has not been copied, downloaded, or marked reviewed for the current case data.', packageState);
      }
      return this.buildCheckResult('full_report_package', 'pass', 'Full report package is current and reviewed/copied for this case revision.', packageState);
    },

    updateCaseFullReportPackageCheckResult(state = null) {
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        full_report_package: this.buildCaseFullReportPackageCheckResult(state)
      };
      this.persistCaseSupportState();
    },

    async openCaseFullReportPackageModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening full report package…',
        message: 'Assembling the one-click report package preview.',
        target: 'full-report-package-modal',
        finishMessage: 'Full report package preview ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.setCaseFullReportPackagePayloadCache(null);
        this.caseFullReportPackageStatus = 'Preparing the full report package preview…';
        this.caseFullReportPackageActiveView = options.view || 'rich';
        this.showCaseFullReportPackageModal = true;
        await this.yieldToCaseUi();
        this.refreshCaseFullReportPackageReview({ silent: true });
        return true;
      });
    },

    cancelCaseFullReportPackageModal() {
      if (this.caseFullReportPackageRefreshRunning) return;
      this.showCaseFullReportPackageModal = false;
    },

    refreshCaseFullReportPackageReview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Refreshing full report package…',
          message: 'Building the one-click package and embedding available flow visuals.',
          stepMessage: 'Preparing current report sections…',
          target: 'full-report-package-modal',
          finishMessage: 'Full report package refreshed.'
        }, () => this.refreshCaseFullReportPackageReview({ ...options, __caseOperationWrapped: true }));
      }
      this.caseFullReportPackageRefreshRunning = true;
      try {
        const payload = this.getCaseFullReportPackagePayload({ includeDeliverableViews: true, forceRebuild: !!options.forceRebuild });
        this.setCaseFullReportPackagePayloadCache(payload, { includeDeliverableViews: true });
        this.caseFullReportPackageStatus = payload.summary || 'Full report package refreshed.';
        this.updateCaseFullReportPackageCheckResult(payload.state);
        if (!options.silent) this.showCaseToast(this.caseFullReportPackageStatus);
        return payload;
      } finally {
        this.caseFullReportPackageRefreshRunning = false;
      }
    },

    markCaseFullReportPackageBuiltRecord(payload = null, options = {}) {
      const activePayload = payload || this.getCaseFullReportPackagePayload({ includeDeliverableViews: true });
      const reviewedAtUtc = options.reviewed ? (options.reviewedAtUtc || new Date().toISOString()) : '';
      const existing = this.caseSupportState.deliverableStateById?.one_click_report_package || {};
      const storedReviewedAtUtc = reviewedAtUtc || (options.keepReview && existing.basisHash === activePayload.basisHash ? existing.reviewedAtUtc || '' : '');
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        one_click_report_package: {
          ...existing,
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: options.builtAtUtc || new Date().toISOString(),
          basisHash: activePayload.basisHash || '',
          reviewedAtUtc: storedReviewedAtUtc
        }
      };

      let updatedState = activePayload.state || {};
      if (options.reviewed && activePayload.isReady) {
        this.caseSupportState.workflowState = {
          ...this.caseSupportState.workflowState,
          fullReportPackageReview: {
            ...(this.caseSupportState.workflowState?.fullReportPackageReview || {}),
            reviewedAtUtc,
            copiedAtUtc: options.copied ? reviewedAtUtc : (this.caseSupportState.workflowState?.fullReportPackageReview?.copiedAtUtc || ''),
            downloadedAtUtc: options.downloaded ? reviewedAtUtc : (this.caseSupportState.workflowState?.fullReportPackageReview?.downloadedAtUtc || ''),
            revision: Number(this.caseSupportState.caseDataRevision) || 0,
            basisHash: activePayload.basisHash || '',
            rendererVersion: activePayload.rendererVersion || CASE_FULL_REPORT_PACKAGE_RENDERER_VERSION,
            plainTextLength: activePayload.plainTextLength || 0,
            htmlLength: activePayload.htmlLength || 0,
            source: options.source || 'full_report_package'
          }
        };
        this.recordWorkflowSignal(WORKFLOW_SIGNAL_FULL_REPORT_PACKAGE_REVIEWED, options.source || 'full_report_package', { silent: true });
        updatedState = {
          ...updatedState,
          reviewedAtUtc,
          reviewIsCurrent: true,
          summary: 'Full report package is ready and has been marked reviewed/copied for the current case data.'
        };
      } else if (storedReviewedAtUtc && activePayload.isReady) {
        updatedState = {
          ...updatedState,
          reviewedAtUtc: storedReviewedAtUtc,
          reviewIsCurrent: !!updatedState.reviewIsCurrent
        };
      }

      const updatedPayload = {
        ...activePayload,
        reviewedAtUtc: updatedState.reviewedAtUtc || activePayload.reviewedAtUtc || '',
        reviewIsCurrent: !!updatedState.reviewIsCurrent,
        state: updatedState
      };
      this.setCaseFullReportPackagePayloadCache(updatedPayload, { includeDeliverableViews: true });
      this.updateCaseFullReportPackageCheckResult(updatedState);
      this.persistCaseSupportState();
      return updatedPayload;
    },

    stageCaseFullReportPackagePreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging full report package…',
          message: 'Building preview rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Full report package staged.'
        }, () => this.stageCaseFullReportPackagePreview({ ...options, __caseOperationWrapped: true }));
      }
      const payload = this.getCaseFullReportPackagePayload({ includeDeliverableViews: true });
      this.setCaseFullReportPackagePayloadCache(payload, { includeDeliverableViews: true });
      this.loadOutputRows(buildCaseFullReportPackagePreviewRows(payload, payload.state), {
        title: 'Full report package preview',
        detail: 'One-click report package preview staged in Output/Review. Main Data is not modified.',
        sourceActionId: 'one_click_report_package_preview',
        sourceDeliverableId: 'one_click_report_package',
        target: 'deliverable-preview'
      });
      this.markCaseFullReportPackageBuiltRecord(payload, { keepReview: true });
      this.caseFullReportPackageStatus = payload.isReady
        ? 'Full report package preview staged. It is ready to copy or download.'
        : `Draft package preview staged. ${payload.missingReason || 'Some prerequisites are missing.'}`;
      if (!options.silent) {
        this.scrollToSection('case-output');
        this.showCaseToast(this.caseFullReportPackageStatus);
      }
      return true;
    },

    downloadCaseFullReportPackageText(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Downloading full report package…',
          message: 'Building the one-click package before creating the TXT file.',
          target: 'full-report-package-download',
          finishMessage: 'Full report package TXT ready.'
        }, () => this.downloadCaseFullReportPackageText({ ...options, __caseOperationWrapped: true }));
      }
      const payload = this.getCaseFullReportPackagePayload({ includeDeliverableViews: true });
      downloadTextFile('case_full_report_package.txt', payload.plainText || '', 'text/plain;charset=utf-8;');
      this.setCaseFullReportPackagePayloadCache(payload, { includeDeliverableViews: true });
      this.markCaseFullReportPackageBuiltRecord(payload, { keepReview: true, reviewed: payload.isReady, downloaded: payload.isReady, source: 'download_full_report_package_text' });
      this.caseFullReportPackageStatus = payload.isReady ? 'Full report package TXT downloaded and marked reviewed.' : 'Draft full report package TXT downloaded; prerequisites are still missing.';
      if (!options.silent) this.showCaseToast(this.caseFullReportPackageStatus);
      return true;
    },

    async downloadCaseFullReportPackageHtml(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Downloading full report package…',
          message: 'Building the HTML package and converting inline flow visuals to document-friendly images.',
          target: 'full-report-package-download',
          finishMessage: 'Full report package HTML ready.'
        }, () => this.downloadCaseFullReportPackageHtml({ ...options, __caseOperationWrapped: true }));
      }
      const payload = this.getCaseFullReportPackagePayload({ includeDeliverableViews: true });
      const htmlForDownload = await buildCaseFullReportPackageClipboardHtml(payload, {
        scale: 2,
        maxRasterWidth: 1800,
        replaceFailedSvgWithPlaceholder: false
      });
      downloadTextFile('case_full_report_package.html', htmlForDownload.htmlDocument || payload.htmlDocument || payload.htmlFragment || '', 'text/html;charset=utf-8;');
      this.setCaseFullReportPackagePayloadCache(payload, { includeDeliverableViews: true });
      this.markCaseFullReportPackageBuiltRecord(payload, { keepReview: true, reviewed: payload.isReady, downloaded: payload.isReady, source: 'download_full_report_package_html' });
      const imageNote = htmlForDownload.convertedVisualCount
        ? ` with ${htmlForDownload.convertedVisualCount} embedded PNG figure${htmlForDownload.convertedVisualCount === 1 ? '' : 's'}`
        : '';
      this.caseFullReportPackageStatus = payload.isReady
        ? `Full report package HTML downloaded${imageNote} and marked reviewed.`
        : `Draft full report package HTML downloaded${imageNote}; prerequisites are still missing.`;
      if (!options.silent) this.showCaseToast(this.caseFullReportPackageStatus);
      return true;
    },

    async copyCaseFullReportPackageText(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Copying full report package…',
          message: 'Building the one-click package before copying text.',
          target: 'full-report-package-copy',
          finishMessage: 'Full report package text copied.'
        }, () => this.copyCaseFullReportPackageText({ ...options, __caseOperationWrapped: true }));
      }
      const payload = this.getCaseFullReportPackagePayload({ includeDeliverableViews: true });
      const copied = await this.copyTextToClipboard(payload.plainText || '', 'Full report package text copied.');
      this.setCaseFullReportPackagePayloadCache(payload, { includeDeliverableViews: true });
      if (copied) {
        this.markCaseFullReportPackageBuiltRecord(payload, { keepReview: true, reviewed: payload.isReady, copied: payload.isReady, source: 'copy_full_report_package_text' });
      }
      this.caseFullReportPackageStatus = copied
        ? payload.isReady ? 'Full report package text copied and marked reviewed.' : 'Draft full report package text copied; prerequisites are still missing.'
        : 'Could not copy the full report package text in this browser.';
      if (!options.silent) this.showCaseToast(this.caseFullReportPackageStatus);
      return copied;
    },

    async copyCaseFullReportPackageRich(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Copying rich full report package…',
          message: 'Building the rich HTML package and embedding available flow visuals.',
          target: 'full-report-package-copy',
          finishMessage: 'Full report package copied.'
        }, () => this.copyCaseFullReportPackageRich({ ...options, __caseOperationWrapped: true }));
      }
      const payload = this.getCaseFullReportPackagePayload({ includeDeliverableViews: true });
      this.setCaseFullReportPackagePayloadCache(payload, { includeDeliverableViews: true });
      try {
        if (typeof navigator !== 'undefined' && navigator?.clipboard?.write && typeof ClipboardItem !== 'undefined') {
          const clipboardHtml = await buildCaseFullReportPackageClipboardHtml(payload, {
            scale: 2,
            maxRasterWidth: 1800,
            replaceFailedSvgWithPlaceholder: true
          });
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([clipboardHtml.htmlDocument || payload.htmlDocument || payload.htmlFragment || ''], { type: 'text/html' }),
              'text/plain': new Blob([payload.plainText || ''], { type: 'text/plain' })
            })
          ]);
          this.markCaseFullReportPackageBuiltRecord(payload, { keepReview: true, reviewed: payload.isReady, copied: payload.isReady, source: 'copy_full_report_package_rich' });
          const imageNote = clipboardHtml.convertedVisualCount
            ? ` with ${clipboardHtml.convertedVisualCount} document-friendly PNG figure${clipboardHtml.convertedVisualCount === 1 ? '' : 's'}`
            : clipboardHtml.sourceVisualCount
              ? ' with figure placeholders because this browser could not rasterize the SVG previews'
              : '';
          this.caseFullReportPackageStatus = payload.isReady
            ? `Full report package copied as rich HTML${imageNote}, with plain text fallback, and marked reviewed.`
            : `Draft full report package copied as rich HTML${imageNote}; prerequisites are still missing.`;
          if (!options.silent) this.showCaseToast(this.caseFullReportPackageStatus);
          return true;
        }
      } catch {
        // Fall back to text copy below.
      }
      const copied = await this.copyTextToClipboard(payload.plainText || '', 'Full report package text copied because rich clipboard was unavailable.');
      if (copied) {
        this.markCaseFullReportPackageBuiltRecord(payload, { keepReview: true, reviewed: payload.isReady, copied: payload.isReady, source: 'copy_full_report_package_text_fallback' });
      }
      this.caseFullReportPackageStatus = copied
        ? payload.isReady ? 'Rich clipboard unavailable; copied text-only package and marked reviewed.' : 'Rich clipboard unavailable; copied draft text-only package.'
        : 'Could not copy the full report package in this browser.';
      if (!options.silent) this.showCaseToast(this.caseFullReportPackageStatus);
      return copied;
    },

    markCaseFullReportPackageReviewComplete(options = {}) {
      const payload = this.getCaseFullReportPackagePayload({ includeDeliverableViews: true });
      this.setCaseFullReportPackagePayloadCache(payload, { includeDeliverableViews: true });
      if (!payload.isReady) {
        this.caseFullReportPackageStatus = payload.summary || 'Resolve package prerequisites before marking it reviewed.';
        this.showCaseToast(this.caseFullReportPackageStatus);
        return false;
      }
      this.markCaseFullReportPackageBuiltRecord(payload, { reviewed: true, source: options.source || 'mark_full_report_package_reviewed' });
      this.loadOutputRows(buildCaseFullReportPackageAuditRows(this.caseFullReportPackagePayload, 'Full report package marked reviewed'), {
        title: 'Full report package review audit',
        detail: 'Audit-only confirmation that the full report package was reviewed for the current case data.',
        sourceActionId: 'review_full_report_package',
        sourceDeliverableId: 'one_click_report_package',
        target: 'audit-only'
      });
      this.caseFullReportPackageStatus = 'Full report package marked reviewed for the current case data.';
      this.showCaseToast(this.caseFullReportPackageStatus);
      return true;
    },

    toggleCaseFullReportPackageReviewed() {
      const existing = this.caseSupportState.deliverableStateById?.one_click_report_package || {};
      if (existing.reviewedAtUtc) {
        this.caseSupportState.deliverableStateById = {
          ...(this.caseSupportState.deliverableStateById || {}),
          one_click_report_package: { ...existing, reviewedAtUtc: '' }
        };
        this.caseSupportState.workflowState = {
          ...(this.caseSupportState.workflowState || {}),
          fullReportPackageReview: null,
          signalCompleteByCode: { ...(this.caseSupportState.workflowState?.signalCompleteByCode || {}) }
        };
        delete this.caseSupportState.workflowState.signalCompleteByCode[WORKFLOW_SIGNAL_FULL_REPORT_PACKAGE_REVIEWED];
        this.updateCaseFullReportPackageCheckResult();
        this.showCaseToast('Full report package review mark cleared.');
        return true;
      }
      return this.markCaseFullReportPackageReviewComplete({ source: 'deliverables-panel' });
    },

    buildOneClickPackagePreview() {
      this.openCaseFullReportPackageModal();
    }
};
